import { getAuthHeaders } from "./utils/authHeaders"
import React from 'react'
import { Alert, Button, Checkbox, Empty, Input, Modal, Popover, Select, Space, Spin, Tag, Typography, message, Tooltip } from 'antd'
import {
  ThunderboltOutlined,
  TeamOutlined,
  WarningOutlined,
  BranchesOutlined,
  NodeIndexOutlined,
  InfoCircleOutlined,
  EditOutlined,
  FilterOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import axios from 'axios'
import './CharactersTab.css'
import type { StepsProps, TourProps } from 'antd'
import { useTourFlags } from './tours/useTourFlags'
import AppTour, { renderAppTourPanel } from './tours/AppTour'
import LoadingCard from './components/LoadingCard'
import { useTutorialSandbox } from './tutorials/TutorialSandboxProvider'
import { isOptionalFormalityReason, relationshipReviewSummary } from './utils/relationshipReviewSummary'

const { Text } = Typography

type TourStep = NonNullable<TourProps['steps']>[number]
interface TourStepConfig extends TourStep {
  id: string
  targetIds: string[]
  waitForTargetIds?: string[]
  modalKey?: 'analysis' | 'alias' | 'matrix' | 'relationship'
  onEnter?: () => void
  onExit?: () => void
}

type StepStatus = NonNullable<StepsProps['items']>[number]['status']
type CharacterReviewStatusTone = 'ready' | 'needed' | 'attention' | 'blocked'

interface CharacterWorkflowActionItem {
  title: React.ReactNode
  description?: React.ReactNode
  chipLabel: string
  chipStatusLabel: string
  status?: StepStatus
  statusTone: CharacterReviewStatusTone
  onAction?: () => void
  disabled?: boolean
}

type GenderValue = 'male' | 'female' | 'unknown'
type GenderFilterValue = 'all' | GenderValue
type RelationshipFormalityValue = 'Formal' | 'Informal'
type RelationshipFormalityFilter =
  | 'all'
  | RelationshipFormalityValue
  | 'asymmetric'
  | 'mixed'
  | 'unknown'
type CharacterSortValue = 'mostChapters' | 'alpha'
type RelationshipSortValue = 'mostChapters' | 'alpha'
type RelationshipStatusFilter = 'all' | 'needsReview' | 'suggested' | 'ok'
type RelationshipChapterFilter = 'all' | '1-2' | '3-5' | '6-10' | '11+'
type RelationshipFormalitySummary = 'Formal' | 'Informal' | 'Mixed' | 'Unknown'
type RelationshipChapterGroupKey = 'needsReview' | 'suggested' | 'overrides' | 'matchesDefault'

interface ChapterRef {
  chapterGuid?: string
  chapterNumber?: number
  chapterName?: string
  chapterUpdatedAt?: string
}

interface CharacterEntry {
  id: string
  canonicalName: string
  aliases?: string[]
  aliasCounts?: Record<string, number>
  gender?: string
  genderCounts?: Record<string, number>
  hasGenderConflict?: boolean
  occurrenceCount?: number
  chapterCount?: number
  firstSeen?: ChapterRef | null
  lastSeen?: ChapterRef | null
  descriptionCounts?: Record<string, Record<string, number>>
  descriptionOverrides?: Record<string, string[]>
  descriptionRefresh?: CharacterDescriptionRefreshState | null
}

interface CharacterDescriptionRefreshState {
  status?: 'idle' | 'queued' | 'running' | 'completed' | 'failed' | 'exhausted' | string
  usedCount?: number
  remainingCount?: number
  requestedAt?: string | null
  startedAt?: string | null
  completedAt?: string | null
  lastError?: string | null
  chapterCount?: number
  manifestId?: string | null
}

interface NormalizedCharacterDescriptionRefreshState {
  status: string
  usedCount: number
  remainingCount: number
  requestedAt: string | null
  startedAt: string | null
  completedAt: string | null
  lastError: string | null
  chapterCount: number
  manifestId: string | null
}

interface RelationshipEntry {
  id: string
  pairKey?: string
  characterAId: string
  characterBId: string
  characterAName: string
  characterBName: string
  formalityCounts: Record<string, number>
  formalityCountsAtoB?: Record<string, number>
  formalityCountsBtoA?: Record<string, number>
  chapterSamples?: Array<
    ChapterRef & { formality?: string; formalityAtoB?: string; formalityBtoA?: string }
  >
  chapterCount?: number
  missingChapterCount?: number
  lastUpdatedAt?: string | null
  reviewStatus?: string
  provisionalChapterCount?: number
  reviewSummaryVersion?: number
  reviewAttentionChapterCount?: number
  suggestedDefaultChapterCount?: number
  deferredDefaultChapterCount?: number
  supportedFormalityVariation?: boolean
  approvedDecisionAuditIncomplete?: boolean
  approvedDecision?: { status?: string; formalityAtoB?: string; formalityBtoA?: string; chapterOverrides?: Array<{ chapterGuid: string; context: string; formalityAtoB: string; formalityBtoA: string }> }
  approvedDecisionConflicts?: Array<{ chapterGuid: string; direction: string; approved: string; observed: string }>
  reviewedAt?: string | null
  reviewedBy?: string | null
}

type RelationshipChapterSample = ChapterRef & {
  formality?: string
  formalityAtoB?: string
  formalityBtoA?: string
}

interface FormalityEvidence {
  value: string
  needsReview: boolean
  basis?: string
  reason?: string
  originChapterId?: string
  addressRelevance?: 'observed' | 'contextual' | 'not_observed'
  evidence?: unknown[]
}

interface RelationshipDetailRow {
  aToBEvidence?: FormalityEvidence
  bToAEvidence?: FormalityEvidence
  characterAName: string
  characterAGender?: string
  characterBName: string
  characterBGender?: string
  formality?: string
  formalityAtoB?: string
  formalityBtoA?: string
}

interface RelationshipDetailChapter {
  chapterGuid?: string
  chapterDocumentId?: string
  chapterNumber?: number
  chapterName?: string
  formality?: string
  formalityAtoB?: string
  formalityBtoA?: string
  rows?: RelationshipDetailRow[]
}

interface RelationshipDetailPayload {
  approvedDecision?: RelationshipEntry['approvedDecision']
  approvedDecisionConflicts?: RelationshipEntry['approvedDecisionConflicts']
  relationshipId?: string
  pairKey?: string
  registryRevision?: string
  characterAId?: string
  characterBId?: string
  characterAName: string
  characterBName: string
  chapters: RelationshipDetailChapter[]
  totalChapters?: number
  totalRows?: number
}

interface RelationshipFormalityOverride {
  chapterGuid: string
  formality?: RelationshipFormalityValue
  formalityAtoB?: RelationshipFormalityValue
  formalityBtoA?: RelationshipFormalityValue
}

interface CharacterRegistryPayload {
  version: number
  sourceVersion?: number
  registryRevision?: string
  registryRefreshRequired?: boolean
  invalidRelationshipCount?: number
  bookId: string
  updatedAt: string
  registryRefreshStatus?: string
  registryRefreshStartedAt?: string
  registryRefreshUpdatedAt?: string
  registryRefreshCompletedAt?: string
  registryRefreshLastError?: string
  characters: CharacterEntry[]
  relationships: RelationshipEntry[]
  stats?: Record<string, number>
}

interface CharacterAliasMapping {
  alias: string
  target: string
}

type CharacterSplitReanalyzeMode = 'none' | 'targeted' | 'auto'

interface CharacterSplitRewriteSummary {
  updatedChapters?: number
  updatedRows?: number
  updatedSides?: number
  ambiguousRows?: number
  ambiguousChapterGuids?: string[]
}

interface CharacterSplitSummary {
  status?: string
  operationId?: string
  sourceName?: string
  splitName?: string
  targetName?: string
  aliasMappingsRemoved?: number
  sourceRecordUpdated?: boolean
  targetRecordCreatedOrUpdated?: boolean
  rewrite?: CharacterSplitRewriteSummary
}

interface CharacterSplitResponse {
  status?: string
  split?: CharacterSplitSummary
  ambiguousChapters?: string[]
  reanalyzeMode?: CharacterSplitReanalyzeMode | string
  reanalyzeChapters?: string[]
  reanalysis?: Record<string, any>
}

interface CharacterSplitJobPayload {
  status?: string
  operationId?: string
  sourceName?: string
  splitName?: string
  targetName?: string
  startedAt?: string
  completedAt?: string
  error?: string
  resultSummary?: CharacterSplitSummary
}

interface AliasConflict {
  alias: string
  canonicalName: string
}

interface RelationshipAnalysisSummary {
  missingChapters: number
  wordCount?: number
  creditCost?: number
  freeRunAvailable?: boolean
}

interface CharactersTabProps {
  bookId: string
  apiBase: string
  userId: string
  onOpenMatrixEditor: (chapterGuid?: string) => void
  onOpenAnalysis: () => void
  onCloseMatrixEditor?: () => void
  onCloseAnalysis?: () => void
  startTourSignal?: number
  relationshipSummaryCard?: React.ReactNode
  analysisLoading?: boolean
  analysisStatus?: string | null
  registryRefreshSignal?: number | null
  registryClearSignal?: number | null
  showTranslationStep?: boolean
}

const genderColorMap: Record<string, string> = {
  male: 'geekblue',
  female: 'magenta',
  unknown: 'default',
}

const GENDER_LABELS: Record<GenderValue, string> = {
  male: 'Masculine',
  female: 'Feminine',
  unknown: 'Not specified',
}

const GENDER_OPTIONS: Array<{ label: string; value: GenderValue }> = [
  { label: GENDER_LABELS.male, value: 'male' },
  { label: GENDER_LABELS.female, value: 'female' },
]

const GENDER_FILTER_OPTIONS: Array<{ label: string; value: GenderFilterValue }> = [
  { label: 'All forms', value: 'all' },
  { label: GENDER_LABELS.male, value: 'male' },
  { label: GENDER_LABELS.female, value: 'female' },
  { label: GENDER_LABELS.unknown, value: 'unknown' },
]

const RELATIONSHIP_FORMALITY_FILTER_OPTIONS: Array<{
  label: string
  value: RelationshipFormalityFilter
}> = [
  { label: 'All Formal / Informal Speech', value: 'all' },
  { label: 'Formal', value: 'Formal' },
  { label: 'Informal', value: 'Informal' },
  { label: 'Asymmetric', value: 'asymmetric' },
  { label: 'Mixed', value: 'mixed' },
  { label: 'Unknown', value: 'unknown' },
]

const RELATIONSHIP_STATUS_FILTER_OPTIONS: Array<{
  label: string
  value: RelationshipStatusFilter
}> = [
  { label: 'All statuses', value: 'all' },
  { label: 'Needs review', value: 'needsReview' },
  { label: 'Suggested defaults', value: 'suggested' },
  { label: 'No issues', value: 'ok' },
]

const RELATIONSHIP_CHAPTER_FILTER_OPTIONS: Array<{
  label: string
  value: RelationshipChapterFilter
}> = [
  { label: 'All chapters', value: 'all' },
  { label: '1-2 chapters', value: '1-2' },
  { label: '3-5 chapters', value: '3-5' },
  { label: '6-10 chapters', value: '6-10' },
  { label: '11+ chapters', value: '11+' },
]

const RELATIONSHIP_SORT_OPTIONS: Array<{ label: string; value: RelationshipSortValue }> = [
  { label: 'Most chapters', value: 'mostChapters' },
  { label: 'A-Z', value: 'alpha' },
]

const RELATIONSHIP_FORMALITY_SELECT_OPTIONS: Array<{
  label: React.ReactNode
  value: RelationshipFormalityValue
}> = [
  {
    label: (
      <span className="relationship-formality-option">
        <span className="relationship-formality-dot relationship-formality-dot-formal" />
        <span>Formal</span>
      </span>
    ),
    value: 'Formal',
  },
  {
    label: (
      <span className="relationship-formality-option">
        <span className="relationship-formality-dot relationship-formality-dot-informal" />
        <span>Informal</span>
      </span>
    ),
    value: 'Informal',
  },
]

const CHARACTER_SORT_OPTIONS: Array<{ label: string; value: CharacterSortValue }> = [
  { label: 'Most chapters', value: 'mostChapters' },
  { label: 'A-Z', value: 'alpha' },
]

const CHARACTER_SORT_STORAGE_KEY = 'characters-character-sort'
const RELATIONSHIP_SORT_STORAGE_KEY = 'characters-relationship-sort'
const CHARACTERS_PANEL_KEY = 'characters'
const RELATIONSHIPS_PANEL_KEY = 'relationships'

const EDITOR_LABEL_STYLE = { fontSize: 14 }
const DESCRIPTION_LABEL_STYLE = { fontSize: 16, fontWeight: 600 }
const DESCRIPTION_TOOLTIP =
  'Use stable physical traits (hair/eye color, skin tone, build, permanent features). Avoid temporary details like clothing or injuries.'

const DESCRIPTION_FIELDS: Array<{ key: string; label: string; max: number }> = [
  { key: 'hairColor', label: 'Hair color', max: 1 },
  { key: 'hairLength', label: 'Hair length', max: 1 },
  { key: 'eyeColor', label: 'Eye color', max: 1 },
  { key: 'skinTone', label: 'Skin tone', max: 1 },
  { key: 'race', label: 'Race', max: 1 },
  { key: 'bodyType', label: 'Body type', max: 1 },
  { key: 'distinguishingFeatures', label: 'Features', max: 3 },
]
const DESCRIPTION_INPUT_FIELDS: Array<{ key: string; label: string; placeholder: string }> = [
  { key: 'hairColor', label: 'Hair color', placeholder: 'e.g., brown' },
  { key: 'hairLength', label: 'Hair length', placeholder: 'e.g., long' },
  { key: 'eyeColor', label: 'Eye color', placeholder: 'e.g., green' },
  { key: 'skinTone', label: 'Skin tone', placeholder: 'e.g., tan' },
  { key: 'race', label: 'Race', placeholder: 'e.g., Latino' },
  { key: 'bodyType', label: 'Body type', placeholder: 'e.g., athletic' },
  { key: 'distinguishingFeatures', label: 'Features', placeholder: 'e.g., scar on cheek, freckles' },
]

const formatChapterRef = (ref?: ChapterRef | null) => {
  if (!ref) return '—'
  if (ref.chapterName) return ref.chapterName
  if (typeof ref.chapterNumber === 'number') return `Chapter ${ref.chapterNumber}`
  return ref.chapterGuid || 'Unknown chapter'
}

const sumFormalityCounts = (counts?: Record<string, number>) =>
  Object.values(counts || {}).reduce((acc, value) => acc + value, 0)

const formatGenderLabel = (value?: string) => {
  if (value === 'male' || value === 'female' || value === 'unknown') {
    return GENDER_LABELS[value]
  }
  return GENDER_LABELS.unknown
}

const normalizeGenderValue = (value?: string): GenderValue => {
  if (value === 'male' || value === 'female' || value === 'unknown') {
    return value
  }
  return 'unknown'
}

const formatPluralCount = (count: number, singular: string, plural = `${singular}s`) =>
  `${count.toLocaleString()} ${count === 1 ? singular : plural}`

const normalizeSplitStatus = (value?: string | null): string => {
  return (value || '').toString().trim().toLowerCase()
}

const isSplitStatusActive = (value?: string | null): boolean => {
  const normalized = normalizeSplitStatus(value)
  return normalized === 'queued' || normalized === 'pending' || normalized === 'running' || normalized === 'retrying'
}

const getFormalityToneColor = (value?: string): string => {
  const normalized = normalizeRelationshipFormalityValue(value)
  if (normalized === 'Formal') {
    return 'blue'
  }
  if (normalized === 'Informal') {
    return 'green'
  }
  const lower = (value || '').toString().trim().toLowerCase()
  if (lower === 'mixed') {
    return 'orange'
  }
  return 'default'
}

const CHAR_PLACEHOLDER_PREFIX = 'char_'

const normalizePlaceholderCandidate = (value?: string): string => {
  return (value || '')
    .trim()
    .replace(/^[\s"'`“”‘’([{<]+/, '')
    .replace(/[\s"'`“”‘’)\]}>.,:;!?]+$/, '')
    .trim()
    .toLowerCase()
}

const getPlaceholderKey = (primary?: string, fallback?: string): string => {
  const primaryValue = (primary || '').trim()
  return primaryValue || (fallback || '').trim()
}

const isCharPlaceholder = (value?: string): boolean => {
  return normalizePlaceholderCandidate(value).startsWith(CHAR_PLACEHOLDER_PREFIX)
}

const getSafeCharacterName = (value?: string, fallback = 'Unnamed'): string => {
  const trimmed = (value || '').trim()
  if (!trimmed || isCharPlaceholder(trimmed)) {
    return fallback
  }
  return trimmed
}

const getSafeRelationshipName = (value?: string, fallback = 'Character'): string =>
  getSafeCharacterName(value, fallback)

const getRelationshipPairKeyFromIds = (
  characterAId?: string,
  characterBId?: string
): string | null => {
  if (typeof characterAId !== 'string' || typeof characterBId !== 'string') {
    return null
  }
  const characterA = characterAId.trim()
  const characterB = characterBId.trim()
  if (!characterA || !characterB || characterA === characterB) {
    return null
  }
  return [characterA, characterB].sort().join('__')
}

const getRelationshipPairKey = (relationship?: {
  pairKey?: string
  characterAId?: string
  characterBId?: string
} | null): string | null => {
  if (!relationship) {
    return null
  }
  const derived = getRelationshipPairKeyFromIds(
    relationship.characterAId,
    relationship.characterBId
  )
  if (!derived) {
    return null
  }
  const declared =
    typeof relationship.pairKey === 'string' ? relationship.pairKey.trim() : ''
  if (relationship.pairKey != null && typeof relationship.pairKey !== 'string') {
    return null
  }
  if (declared && declared !== derived) {
    return null
  }
  return derived
}

const normalizeContractString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

const getRegistryRevision = (registry?: {
  registryRevision?: string
  updatedAt?: string
} | null): string =>
  normalizeContractString(registry?.registryRevision) ||
  normalizeContractString(registry?.updatedAt)

const isPlaceholderCharacter = (character: CharacterEntry): boolean => {
  const key = getPlaceholderKey(character.canonicalName, character.id)
  return isCharPlaceholder(key)
}

const isPlaceholderRelationship = (relationship: RelationshipEntry): boolean => {
  const aKey = getPlaceholderKey(relationship.characterAName, relationship.characterAId)
  const bKey = getPlaceholderKey(relationship.characterBName, relationship.characterBId)
  return isCharPlaceholder(aKey) || isCharPlaceholder(bKey)
}

const isValidAliasValue = (value?: string): boolean => {
  const trimmed = (value || '').trim()
  return Boolean(trimmed) && !isCharPlaceholder(trimmed)
}

const getCharacterStatus = (
  character: CharacterEntry,
  hasAliasCollision = false
): { label: 'Needs review' | 'No issues'; color: string } => {
  const needsReview = Boolean(character.hasGenderConflict) || hasAliasCollision
  return needsReview ? { label: 'Needs review', color: 'gold' } : { label: 'No issues', color: 'green' }
}

const formatCharacterCoverage = (character: CharacterEntry): string => {
  return typeof character.chapterCount === 'number'
    ? `${character.chapterCount} ch`
    : '— ch'
}

const getCharacterChapterCount = (character: CharacterEntry): number => {
  if (typeof character.chapterCount === 'number') {
    return character.chapterCount
  }
  if (typeof character.occurrenceCount === 'number') {
    return character.occurrenceCount
  }
  return 0
}

const getRelationshipCountsForDirection = (
  relationship: RelationshipEntry,
  direction: 'AtoB' | 'BtoA'
): Record<string, number> => {
  if (direction === 'AtoB') {
    return relationship.formalityCountsAtoB || relationship.formalityCounts || {}
  }
  return relationship.formalityCountsBtoA || relationship.formalityCounts || {}
}

const getRelationshipDirectionSummary = (
  relationship: RelationshipEntry,
  direction: 'AtoB' | 'BtoA'
): RelationshipFormalitySummary => {
  const counts = getRelationshipCountsForDirection(relationship, direction)
  const formal = counts.Formal ?? 0
  const informal = counts.Informal ?? 0
  if (formal > 0 && informal > 0) {
    return 'Mixed'
  }
  if (formal > 0) {
    return 'Formal'
  }
  if (informal > 0) {
    return 'Informal'
  }
  return 'Unknown'
}

const getRelationshipDefaultFormality = (
  relationship: RelationshipEntry,
  direction: 'AtoB' | 'BtoA'
): RelationshipFormalityValue => {
  const summary = getRelationshipDirectionSummary(relationship, direction)
  if (summary === 'Formal' || summary === 'Informal') {
    return summary
  }
  const fallback = getRelationshipDirectionSummary(
    relationship,
    direction === 'AtoB' ? 'BtoA' : 'AtoB'
  )
  if (fallback === 'Formal' || fallback === 'Informal') {
    return fallback
  }
  return 'Formal'
}

const isRelationshipAsymmetric = (relationship: RelationshipEntry): boolean => {
  const summaryA = getRelationshipDirectionSummary(relationship, 'AtoB')
  const summaryB = getRelationshipDirectionSummary(relationship, 'BtoA')
  if (summaryA === 'Unknown' || summaryB === 'Unknown') {
    return false
  }
  if (summaryA === 'Mixed' || summaryB === 'Mixed') {
    return false
  }
  return summaryA !== summaryB
}

const getRelationshipSymmetricFormality = (
  relationship: RelationshipEntry
): RelationshipFormalityValue | null => {
  const summaryA = getRelationshipDirectionSummary(relationship, 'AtoB')
  const summaryB = getRelationshipDirectionSummary(relationship, 'BtoA')
  if (summaryA === summaryB && (summaryA === 'Formal' || summaryA === 'Informal')) {
    return summaryA
  }
  return null
}

const hasMixedFormality = (relationship: RelationshipEntry): boolean => {
  return (
    getRelationshipDirectionSummary(relationship, 'AtoB') === 'Mixed' ||
    getRelationshipDirectionSummary(relationship, 'BtoA') === 'Mixed'
  )
}

const hasUnknownFormality = (relationship: RelationshipEntry): boolean => {
  return (
    getRelationshipDirectionSummary(relationship, 'AtoB') === 'Unknown' ||
    getRelationshipDirectionSummary(relationship, 'BtoA') === 'Unknown'
  )
}

const isRelationshipReviewed = (relationship: RelationshipEntry): boolean => {
  return (relationship.reviewStatus || '').toString().trim().toLowerCase() === 'reviewed' &&
    ['Formal', 'Informal'].includes(relationship.approvedDecision?.formalityAtoB || '') &&
    ['Formal', 'Informal'].includes(relationship.approvedDecision?.formalityBtoA || '')
}

const hasRelationshipAutoNeedsReview = (relationship: RelationshipEntry): boolean => {
  if (relationship.reviewSummaryVersion === 1) {
    return hasUnknownFormality(relationship) ||
      (hasMixedFormality(relationship) && !relationship.supportedFormalityVariation) ||
      (relationship.reviewAttentionChapterCount || 0) > 0
  }
  return hasMixedFormality(relationship) || hasUnknownFormality(relationship) ||
    (relationship.provisionalChapterCount || 0) > 0
}

const hasProvisionalFormality = (chapter: RelationshipDetailChapter): boolean =>
  !!chapter.rows?.some(row => row.aToBEvidence?.needsReview || row.bToAEvidence?.needsReview)

const hasOnlyOptionalDefaults = (chapter: RelationshipDetailChapter): boolean =>
  hasProvisionalFormality(chapter) && !(chapter.rows || []).some(row =>
    [row.aToBEvidence, row.bToAEvidence].some(e => e?.needsReview && !isOptionalFormalityReason(e.reason)))

const getRelationshipChapterCount = (relationship: RelationshipEntry): number => {
  if (typeof relationship.chapterCount === 'number') {
    return relationship.chapterCount
  }
  return sumFormalityCounts(relationship.formalityCountsAtoB || relationship.formalityCounts)
}

const getRelationshipStatus = (
  relationship: RelationshipEntry
): { label: string; color: string } => {
  if (hasRelationshipNeedsReview(relationship)) {
    return { label: 'Needs review', color: 'gold' }
  }
  if (!isRelationshipReviewed(relationship) && (relationship.provisionalChapterCount || 0) > 0) {
    return { label: 'Suggested default', color: 'blue' }
  }
  if (hasMixedFormality(relationship) && relationship.supportedFormalityVariation) {
    return { label: 'Varies by chapter', color: 'blue' }
  }
  return { label: 'No issues', color: 'green' }
}

const getRelationshipStatusKey = (relationship: RelationshipEntry): RelationshipStatusFilter => {
  if (hasRelationshipNeedsReview(relationship)) {
    return 'needsReview'
  }
  if (!isRelationshipReviewed(relationship) && (relationship.provisionalChapterCount || 0) > 0) {
    return 'suggested'
  }
  return 'ok'
}

const formatRelationshipCoverage = (relationship: RelationshipEntry): string => {
  const chapterCount = getRelationshipChapterCount(relationship)
  const rowCount = sumFormalityCounts(relationship.formalityCountsAtoB || relationship.formalityCounts)
  return `${chapterCount} ch \u2022 ${formatPluralCount(rowCount, 'row')}`
}

const formatRelationshipDistribution = (relationship: RelationshipEntry): string => {
  const summaryA = getRelationshipDirectionSummary(relationship, 'AtoB')
  const summaryB = getRelationshipDirectionSummary(relationship, 'BtoA')
  if (summaryA === 'Mixed' || summaryB === 'Mixed') {
    return 'Mixed'
  }
  if (summaryA === 'Unknown' || summaryB === 'Unknown') {
    return 'Unknown'
  }
  if (summaryA === summaryB) {
    return summaryA
  }
  return 'Asymmetric'
}

const formatRelationshipDetailDistribution = (relationship: RelationshipEntry): string => {
  const nameA = getSafeRelationshipName(relationship.characterAName, 'Character A')
  const nameB = getSafeRelationshipName(relationship.characterBName, 'Character B')
  const summaryA = getRelationshipDirectionSummary(relationship, 'AtoB')
  const summaryB = getRelationshipDirectionSummary(relationship, 'BtoA')
  if (summaryA === summaryB) {
    return summaryA
  }
  return `${nameA} -> ${nameB} ${summaryA} / ${nameB} -> ${nameA} ${summaryB}`
}

const hasRelationshipNeedsReview = (relationship: RelationshipEntry): boolean => {
  if (isRelationshipReviewed(relationship)) {
    return !!relationship.approvedDecisionConflicts?.length || !!relationship.approvedDecisionAuditIncomplete
  }
  return hasRelationshipAutoNeedsReview(relationship)
}

const normalizeRelationshipFormalityValue = (
  value?: string | null
): RelationshipFormalityValue | null => {
  if (!value) {
    return null
  }
  const normalized = value.toString().trim().toLowerCase()
  if (normalized === 'formal') {
    return 'Formal'
  }
  if (normalized === 'informal') {
    return 'Informal'
  }
  return null
}

const buildRelationshipCountsFromDetail = (
  detail: RelationshipDetailPayload | null
): Pick<
  RelationshipEntry,
  | 'formalityCounts'
  | 'formalityCountsAtoB'
  | 'formalityCountsBtoA'
  | 'chapterCount'
  | 'provisionalChapterCount'
  | 'reviewSummaryVersion'
  | 'reviewAttentionChapterCount'
  | 'suggestedDefaultChapterCount'
  | 'deferredDefaultChapterCount'
  | 'supportedFormalityVariation'
  | 'chapterSamples'
  | 'lastUpdatedAt'
> | null => {
  if (!detail) {
    return null
  }
  const countsA: Record<string, number> = {}
  const countsB: Record<string, number> = {}
  const samples: RelationshipChapterSample[] = []
  const sampleKeys = new Set<string>()
  detail.chapters.forEach((chapter) => {
    ;(chapter.rows || []).forEach((row) => {
      const formalityA = normalizeRelationshipFormalityValue(
        row.formalityAtoB ?? row.formality
      )
      if (formalityA) {
        countsA[formalityA] = (countsA[formalityA] ?? 0) + 1
      }
      const formalityB = normalizeRelationshipFormalityValue(
        row.formalityBtoA ?? row.formality
      )
      if (formalityB) {
        countsB[formalityB] = (countsB[formalityB] ?? 0) + 1
      }
    })
    const sampleKey =
      chapter.chapterGuid || `${chapter.chapterNumber ?? ''}-${chapter.chapterName ?? ''}`
    if (sampleKey && !sampleKeys.has(sampleKey)) {
      sampleKeys.add(sampleKey)
      samples.push({
        chapterGuid: chapter.chapterGuid,
        chapterNumber: chapter.chapterNumber,
        chapterName: chapter.chapterName,
        formality: chapter.formality,
        formalityAtoB: chapter.formalityAtoB,
        formalityBtoA: chapter.formalityBtoA,
      })
    }
  })
  return {
    formalityCounts: countsA,
    formalityCountsAtoB: countsA,
    formalityCountsBtoA: countsB,
    chapterCount: detail.totalChapters ?? detail.chapters.length,
    provisionalChapterCount: detail.chapters.filter(hasProvisionalFormality).length,
    ...relationshipReviewSummary(detail.chapters),
    chapterSamples: samples,
    lastUpdatedAt: new Date().toISOString(),
  }
}

const getChapterDirectionSummary = (
  chapter: RelationshipDetailChapter,
  direction: 'AtoB' | 'BtoA'
): RelationshipFormalitySummary => {
  const raw =
    (direction === 'AtoB' ? chapter.formalityAtoB : chapter.formalityBtoA) ?? chapter.formality
  const normalized = normalizeRelationshipFormalityValue(raw)
  if (normalized) {
    return normalized
  }
  const rawLower = (raw || '').toString().trim().toLowerCase()
  if (rawLower === 'mixed') {
    return 'Mixed'
  }
  return 'Unknown'
}

const getTopDescriptionValues = (counts: Record<string, number> | undefined, max: number) => {
  if (!counts) return []
  return Object.entries(counts)
    .filter(([, count]) => Number(count) > 0)
    .sort((a, b) => {
      const diff = Number(b[1]) - Number(a[1])
      if (diff !== 0) return diff
      return a[0].localeCompare(b[0])
    })
    .slice(0, max)
    .map(([value]) => value)
}

const normalizeDescriptionInputValue = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) return ''
  const lower = trimmed.toLowerCase()
  if (['unknown', 'n/a', 'none', 'unspecified'].includes(lower)) {
    return ''
  }
  return lower
}

const parseDescriptionInputValues = (key: string, rawValue: string) => {
  const parts = key === 'distinguishingFeatures' ? rawValue.split(',') : [rawValue]
  return parts
    .map((value) => normalizeDescriptionInputValue(String(value)))
    .filter(Boolean)
}

const buildDescriptionOverridesFromCharacterOverrides = (
  character?: CharacterEntry | null
) => {
  const overrides: Record<string, string[]> = {}
  DESCRIPTION_FIELDS.forEach(({ key, max }) => {
    const rawValues = character?.descriptionOverrides?.[key]
    const values = Array.isArray(rawValues)
      ? rawValues
          .map((value) => normalizeDescriptionInputValue(String(value)))
          .filter(Boolean)
      : []
    if (values.length > 0) {
      overrides[key] = values.slice(0, max)
    }
  })
  return overrides
}

const buildDescriptionOverridePayload = (
  inputs: Record<string, string>,
  touchedFields: Set<string>,
  initialOverrides: Record<string, string[]>
) => {
  const payload: Record<string, string[] | null> = {}
  DESCRIPTION_FIELDS.forEach(({ key, max }) => {
    if (!touchedFields.has(key)) {
      return
    }
    const raw = inputs[key] || ''
    const values = parseDescriptionInputValues(key, raw).slice(0, max)
    const initial = initialOverrides[key] || []
    const valuesMatch =
      values.length === initial.length &&
      values.every((value, index) => value === initial[index])
    if (valuesMatch) {
      return
    }
    payload[key] = values.length > 0 ? values : null
  })
  return payload
}

const buildDescriptionInputsFromCharacter = (character?: CharacterEntry | null) => {
  const inputs: Record<string, string> = {}
  DESCRIPTION_FIELDS.forEach(({ key }) => {
    const overrideValues = character?.descriptionOverrides?.[key]
    if (Array.isArray(overrideValues) && overrideValues.length > 0) {
      inputs[key] = overrideValues.join(', ')
      return
    }
    const countValues = getTopDescriptionValues(
      character?.descriptionCounts?.[key],
      key === 'distinguishingFeatures' ? 3 : 1
    )
    if (countValues.length > 0) {
      inputs[key] = countValues.join(', ')
    } else {
      inputs[key] = ''
    }
  })
  return inputs
}

const formatDescriptionSummary = (
  descriptionOverrides?: Record<string, string[]>,
  descriptionCounts?: Record<string, Record<string, number>>
) => {
  const parts = DESCRIPTION_FIELDS.flatMap(({ key, label, max }) => {
    const overrides = descriptionOverrides?.[key]
    const manualValues = Array.isArray(overrides)
      ? overrides.map((value) => String(value || '').trim()).filter(Boolean).slice(0, max)
      : []
    const values = manualValues.length > 0
      ? manualValues
      : getTopDescriptionValues(descriptionCounts?.[key], max)
    if (values.length === 0) {
      return []
    }
    return `${label}: ${values.join(', ')}`
  })
  return parts.join(' | ')
}

const normalizeDescriptionRefreshState = (
  refresh?: CharacterDescriptionRefreshState | null
): NormalizedCharacterDescriptionRefreshState => {
  const status = String(refresh?.status || 'idle').trim().toLowerCase() || 'idle'
  const usedCount = Math.max(0, Number(refresh?.usedCount || 0))
  const remainingCount =
    refresh?.remainingCount == null ? (usedCount >= 1 ? 0 : 1) : Math.max(0, Number(refresh.remainingCount || 0))
  return {
    status,
    usedCount,
    remainingCount,
    requestedAt: refresh?.requestedAt || null,
    startedAt: refresh?.startedAt || null,
    completedAt: refresh?.completedAt || null,
    lastError: refresh?.lastError || null,
    chapterCount: Number(refresh?.chapterCount || 0),
    manifestId: refresh?.manifestId || null,
  }
}

const isDescriptionRefreshActive = (refresh?: CharacterDescriptionRefreshState | null) => {
  const status = String(refresh?.status || '').trim().toLowerCase()
  return status === 'queued' || status === 'running'
}

const getDescriptionRefreshStatusLabel = (refresh?: CharacterDescriptionRefreshState | null) => {
  const normalized = normalizeDescriptionRefreshState(refresh)
  if (normalized.status === 'queued') return 'Waiting for an analysis worker...'
  if (normalized.status === 'running') {
    return normalized.chapterCount
      ? `Analyzing ${normalized.chapterCount} chapter${normalized.chapterCount === 1 ? '' : 's'}...`
      : 'Analyzing character description...'
  }
  if (normalized.status === 'failed') return 'Last refresh failed'
  if (normalized.usedCount >= 1 || normalized.remainingCount <= 0) return '1 free refresh used'
  return ''
}

const CharactersTab: React.FC<CharactersTabProps> = ({
  bookId,
  apiBase,
  userId,
  onOpenMatrixEditor,
  onOpenAnalysis,
  onCloseMatrixEditor,
  onCloseAnalysis,
  startTourSignal,
  relationshipSummaryCard,
  analysisLoading = false,
  analysisStatus,
  registryRefreshSignal,
  registryClearSignal,
  showTranslationStep = false,
}) => {
  const [registry, setRegistry] = React.useState<CharacterRegistryPayload | null>(null)
  const [characterRecords, setCharacterRecords] = React.useState<CharacterEntry[]>([])
  const [loading, setLoading] = React.useState(false)
  const [charactersLoading, setCharactersLoading] = React.useState(false)
  const [rebuilding, setRebuilding] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [showConflictsOnly, setShowConflictsOnly] = React.useState(false)
  const [isAliasModalVisible, setIsAliasModalVisible] = React.useState(false)
  const [aliasMappings, setAliasMappings] = React.useState<CharacterAliasMapping[]>([])
  const [aliasTarget, setAliasTarget] = React.useState<string | null>(null)
  const [aliasSources, setAliasSources] = React.useState<string[]>([])
  const [aliasMappingSearch, setAliasMappingSearch] = React.useState('')
  const [aliasLoading, setAliasLoading] = React.useState(false)
  const [aliasSaving, setAliasSaving] = React.useState(false)
  const [aliasRemovalChoice, setAliasRemovalChoice] = React.useState<{
    alias: string
    targetCanonicalName: string
  } | null>(null)
  const [isSplitModalVisible, setIsSplitModalVisible] = React.useState(false)
  const [splitSourceName, setSplitSourceName] = React.useState('')
  const [splitName, setSplitName] = React.useState('')
  const [splitTargetName, setSplitTargetName] = React.useState('')
  const [splitSubmitting, setSplitSubmitting] = React.useState(false)
  const [splitJob, setSplitJob] = React.useState<CharacterSplitJobPayload | null>(null)
  const [splitJobError, setSplitJobError] = React.useState<string | null>(null)
  const [splitWarnings, setSplitWarnings] = React.useState<string[]>([])
  const [splitResultSummary, setSplitResultSummary] = React.useState<CharacterSplitSummary | null>(null)
  const splitJobPollTimerRef = React.useRef<number | null>(null)
  const [isEditorModalVisible, setIsEditorModalVisible] = React.useState(false)
  const [editorTarget, setEditorTarget] = React.useState<CharacterEntry | null>(null)
  const [editorName, setEditorName] = React.useState('')
  const [editorGender, setEditorGender] = React.useState<GenderValue>('unknown')
  const [editorDescriptions, setEditorDescriptions] = React.useState<Record<string, string>>({})
  const [editorAliases, setEditorAliases] = React.useState<string[]>([])
  const editorAliasTouchedRef = React.useRef(false)
  const [editorSaving, setEditorSaving] = React.useState(false)
  const [conflictResolvingGender, setConflictResolvingGender] = React.useState<'male' | 'female' | null>(null)
  const [characterSearch, setCharacterSearch] = React.useState('')
  const [genderFilter, setGenderFilter] = React.useState<GenderFilterValue>('all')
  const [characterSort, setCharacterSort] = React.useState<CharacterSortValue>(() => {
    if (typeof window === 'undefined') {
      return 'mostChapters'
    }
    const stored = window.sessionStorage.getItem(CHARACTER_SORT_STORAGE_KEY)
    if (stored === 'mostChapters' || stored === 'alpha') {
      return stored
    }
    return 'mostChapters'
  })
  const [characterDetailId, setCharacterDetailId] = React.useState<string | null>(null)
  const [relationshipSearch, setRelationshipSearch] = React.useState('')
  const [relationshipStatusFilter, setRelationshipStatusFilter] =
    React.useState<RelationshipStatusFilter>('all')
  const [relationshipFormalityFilter, setRelationshipFormalityFilter] =
    React.useState<RelationshipFormalityFilter>('all')
  const [relationshipChapterFilter, setRelationshipChapterFilter] =
    React.useState<RelationshipChapterFilter>('all')
  const [relationshipSort, setRelationshipSort] = React.useState<RelationshipSortValue>(() => {
    if (typeof window === 'undefined') {
      return 'mostChapters'
    }
    const stored = window.sessionStorage.getItem(RELATIONSHIP_SORT_STORAGE_KEY)
    if (stored === 'needsReview') {
      return 'mostChapters'
    }
    if (stored === 'impact') {
      return 'mostChapters'
    }
    if (stored === 'recentlyEdited') {
      return 'mostChapters'
    }
    if (stored === 'mostChapters' || stored === 'alpha') {
      return stored
    }
    return 'mostChapters'
  })
  const [relationshipBulkFormalityAtoB, setRelationshipBulkFormalityAtoB] =
    React.useState<RelationshipFormalityValue>('Formal')
  const [relationshipBulkFormalityBtoA, setRelationshipBulkFormalityBtoA] =
    React.useState<RelationshipFormalityValue>('Formal')
  const [relationshipBulkLinkDirections, setRelationshipBulkLinkDirections] =
    React.useState(true)
  const [relationshipSelectedChapters, setRelationshipSelectedChapters] = React.useState<string[]>([])
  const [relationshipSectionExpanded, setRelationshipSectionExpanded] = React.useState<Record<RelationshipChapterGroupKey, boolean>>({
    needsReview: true,
    suggested: true,
    overrides: true,
    matchesDefault: false,
  })
  const [relationshipUndoStack, setRelationshipUndoStack] = React.useState<
    RelationshipFormalityOverride[][]
  >([])
  const [relationshipDetail, setRelationshipDetail] = React.useState<RelationshipDetailPayload | null>(null)
  const [selectedRelationshipPairKey, setSelectedRelationshipPairKey] =
    React.useState<string | null>(null)
  const [relationshipDetailLoading, setRelationshipDetailLoading] = React.useState(false)
  const [relationshipDetailError, setRelationshipDetailError] = React.useState<string | null>(null)
  const [relationshipBulkUpdating, setRelationshipBulkUpdating] = React.useState(false)
  const [relationshipReviewUpdating, setRelationshipReviewUpdating] = React.useState(false)
  const [descriptionRefreshSubmittingId, setDescriptionRefreshSubmittingId] = React.useState<string | null>(null)
  const relationshipDetailRequestRef = React.useRef(0)
  const registryRequestRef = React.useRef(0)
  const selectedRelationshipPairKeyRef = React.useRef<string | null>(null)
  const relationshipAutoSelectAllRef = React.useRef(false)
  const [analysisSummary, setAnalysisSummary] = React.useState<RelationshipAnalysisSummary | null>(null)
  const [analysisSummaryLoading, setAnalysisSummaryLoading] = React.useState(false)
  const registryLoadingTitle = 'Loading Character Data'
  const registryLoadingTip = rebuilding
    ? 'This can take a moment.'
    : loading
      ? 'Fetching the latest character list for this book.'
      : 'Pulling detailed character data.'

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    window.sessionStorage.setItem(CHARACTER_SORT_STORAGE_KEY, characterSort)
  }, [characterSort])

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    window.sessionStorage.setItem(RELATIONSHIP_SORT_STORAGE_KEY, relationshipSort)
  }, [relationshipSort])

  React.useEffect(() => {
    selectedRelationshipPairKeyRef.current = selectedRelationshipPairKey
  }, [selectedRelationshipPairKey])

  const registryBookRef = React.useRef(bookId)
  const hasAccess = Boolean(userId)
  const normalizedAnalysisStatus = (analysisStatus || '').toString().trim().toLowerCase()
  const analysisReady = normalizedAnalysisStatus === 'completed'
  const analysisFailed = normalizedAnalysisStatus === 'failed'
  const analysisRunning = normalizedAnalysisStatus === 'queued' || normalizedAnalysisStatus === 'running'
  const splitStatusKey = normalizeSplitStatus(splitJob?.status)
  const splitOperationRunning = splitSubmitting || isSplitStatusActive(splitStatusKey)
  const showRegistryLoading =
    analysisReady && !analysisLoading && (loading || rebuilding || charactersLoading)
  const registryRefreshStatus = (registry?.registryRefreshStatus || '').toString().trim().toLowerCase()
  const isRegistryRefreshing = registryRefreshStatus === 'running'
  const castError = error || (registryRefreshStatus === 'failed'
    ? registry?.registryRefreshLastError || 'Character list refresh failed. Please try again.' : null)
  const registryStale = Boolean(castError)
  const registryContractRevision = getRegistryRevision(registry)
  const {
    flags: tourFlags,
    initialized: tourFlagsInitialized,
    markSeen: markTourSeen,
  } = useTourFlags(hasAccess ? userId : null, apiBase)
  const controlsTourSeen = Boolean(tourFlags.charactersControlsTourSeen)
  const analysisTourSeen = Boolean(tourFlags.charactersAnalysisTourSeen)
  const [controlsTourOpen, setControlsTourOpen] = React.useState(false)
  const [analysisTourOpen, setAnalysisTourOpen] = React.useState(false)
  const [controlsTourStep, setControlsTourStep] = React.useState(0)
  const [analysisTourStep, setAnalysisTourStep] = React.useState(0)
  const controlsTourTriggeredRef = React.useRef(false)
  const analysisTourTriggeredRef = React.useRef(false)
  const tourFallbackRef = React.useRef<HTMLElement | null>(null)
  const controlsTourWaitTokenRef = React.useRef(0)
  const analysisTourWaitTokenRef = React.useRef(0)
  const pendingControlsTourStepRef = React.useRef<number | null>(null)
  const pendingAnalysisTourStepRef = React.useRef<number | null>(null)
  const manualTourQueueRef = React.useRef<'analysis' | null>(null)
  const lastExternalTourSignalRef = React.useRef<number | null>(startTourSignal ?? null)
  const charactersPanelRef = React.useRef<HTMLDivElement | null>(null)
  const relationshipsPanelRef = React.useRef<HTMLDivElement | null>(null)
  const charactersPanelHeaderRef = React.useRef<HTMLDivElement | null>(null)
  const relationshipsPanelHeaderRef = React.useRef<HTMLDivElement | null>(null)
  const tutorialSandbox = useTutorialSandbox()
  const tutorialFixturePayload =
    tutorialSandbox.enabled && tutorialSandbox.mode === 'fixture' && tutorialSandbox.fixturePayload
      ? tutorialSandbox.fixturePayload
      : null
  const tutorialCharacterRegistry = tutorialFixturePayload?.characterRegistry as
    | CharacterRegistryPayload
    | undefined
  const tutorialCharacterRecordsPayload = tutorialFixturePayload?.characterRecords
  const tutorialCharacterRecords = Array.isArray(tutorialCharacterRecordsPayload)
    ? (tutorialCharacterRecordsPayload as CharacterEntry[])
    : null
  const tutorialRelationshipAnalysisSummary = tutorialFixturePayload?.relationshipAnalysisSummary as
    | RelationshipAnalysisSummary
    | undefined
  const tutorialRelationshipDetails = tutorialFixturePayload?.relationshipDetails as
    | Record<string, RelationshipDetailPayload>
    | undefined

  const fetchRegistry = React.useCallback(
    async (options?: { silent?: boolean }) => {
      if (analysisLoading || !analysisReady) {
        return
      }
      if (!bookId || !hasAccess) {
        registryRequestRef.current += 1
        setRegistry(null)
        setCharacterRecords([])
        return
      }
      if (registryBookRef.current !== bookId) {
        registryBookRef.current = bookId
        setRegistry(null)
        setCharacterRecords([])
        lastRegistryContractRefreshRef.current = null
      }
      const requestId = ++registryRequestRef.current
      setRebuilding(false)
      setCharactersLoading(false)
      if (tutorialCharacterRegistry) {
        setRegistry(tutorialCharacterRegistry)
        setCharacterRecords(tutorialCharacterRecords ?? tutorialCharacterRegistry.characters ?? [])
        setError(null)
        setLoading(false)
        setCharactersLoading(false)
        return
      }
      if (!options?.silent) {
        setLoading(true)
      }
      setError(null)
      try {
        const { data } = await axios.get<CharacterRegistryPayload>(
          `${apiBase}/uploadedbooks/book/${bookId}/characters/registry`,
          { params: { userId }, headers: await getAuthHeaders() }
        )
        const characterResponse = await axios
          .get<{ characters?: CharacterEntry[] }>(
            `${apiBase}/uploadedbooks/book/${bookId}/characters`,
            { params: { userId } }
          )
          .catch(() => null)
        if (registryRequestRef.current !== requestId) {
          return
        }
        setRegistry(data)
        setCharacterRecords(characterResponse?.data?.characters || data?.characters || [])
      } catch (err: any) {
        if (registryRequestRef.current !== requestId) {
          return
        }
        const detail = err?.response?.data?.detail || err?.message || 'Unable to load characters'
        const detailText =
          typeof detail === 'string'
            ? detail
            : detail && typeof detail === 'object'
              ? String((detail as any).message || (detail as any).error || '')
              : ''
        const normalizedDetailText = detailText.toLowerCase()
        const analysisTransitioning =
          analysisLoading ||
          analysisRunning ||
          splitOperationRunning ||
          splitSubmitting
        const isAnalysisPendingDetail =
          normalizedDetailText.includes('analysis not completed') ||
          normalizedDetailText.includes('analysis is running') ||
          normalizedDetailText.includes('not completed')
        if (analysisTransitioning && isAnalysisPendingDetail) {
          setError(null)
          return
        }
        setError(typeof detail === 'string' ? detail : 'Unable to refresh the character list.')
      } finally {
        if (registryRequestRef.current === requestId) {
          setLoading(false)
        }
      }
    },
    [
      analysisLoading,
      analysisReady,
      analysisRunning,
      apiBase,
      bookId,
      hasAccess,
      splitOperationRunning,
      splitSubmitting,
      tutorialCharacterRecords,
      tutorialCharacterRegistry,
      userId,
    ]
  )

  const fetchAnalysisSummary = React.useCallback(async () => {
    if (!bookId || !hasAccess) {
      setAnalysisSummary(null)
      return
    }
    if (tutorialRelationshipAnalysisSummary) {
      setAnalysisSummary(tutorialRelationshipAnalysisSummary)
      return
    }
    setAnalysisSummaryLoading(true)
    try {
      const { data } = await axios.get<RelationshipAnalysisSummary>(
        `${apiBase}/uploadedbooks/book/${bookId}/relationships/analysis-summary`,
        { params: { userId } }
      )
      setAnalysisSummary(data || null)
    } catch (_err) {
      setAnalysisSummary(null)
    } finally {
      setAnalysisSummaryLoading(false)
    }
  }, [apiBase, bookId, hasAccess, tutorialRelationshipAnalysisSummary, userId])

  const rebuildRegistry = React.useCallback(
    async (options?: { silent?: boolean }) => {
      if (analysisLoading || !analysisReady) return
      if (!bookId || !hasAccess) return
      const requestId = ++registryRequestRef.current
      setLoading(false)
      setCharactersLoading(false)
      setRebuilding(true)
      setError(null)
      try {
        const { data } = await axios.post<CharacterRegistryPayload>(
          `${apiBase}/uploadedbooks/book/${bookId}/characters/registry/rebuild`,
          { userId }
        )
        if (registryRequestRef.current !== requestId) {
          return
        }
        setRegistry(data)
        setCharacterRecords(data?.characters || [])
        if (!options?.silent) {
          message.success('Character List rebuilt.')
        }
      } catch (err: any) {
        if (registryRequestRef.current !== requestId) {
          return
        }
        const detail =
          err?.response?.data?.detail || err?.message || 'Unable to rebuild character list.'
        setError(detail)
        message.error(detail)
      } finally {
        if (registryRequestRef.current === requestId) {
          setRebuilding(false)
        }
      }
    },
    [analysisLoading, analysisReady, apiBase, bookId, hasAccess, userId]
  )

  React.useEffect(() => {
    fetchRegistry()
  }, [fetchRegistry])

  React.useEffect(() => {
    fetchAnalysisSummary()
  }, [analysisStatus, fetchAnalysisSummary, registryClearSignal, registryRefreshSignal])

  React.useEffect(() => {
    if (!analysisReady || !isRegistryRefreshing) {
      return
    }
    let cancelled = false
    let timer: number | null = null
    const poll = async () => {
      await fetchRegistry({ silent: true })
      if (!cancelled) {
        timer = window.setTimeout(poll, 3000)
      }
    }
    timer = window.setTimeout(poll, 3000)
    return () => {
      cancelled = true
      if (timer != null) {
        window.clearTimeout(timer)
      }
    }
  }, [analysisReady, fetchRegistry, isRegistryRefreshing])

  const hasActiveDescriptionRefresh = React.useMemo(
    () =>
      characterRecords.some((character) =>
        isDescriptionRefreshActive(character.descriptionRefresh)
      ),
    [characterRecords]
  )

  React.useEffect(() => {
    if (!analysisReady || !hasActiveDescriptionRefresh) {
      return
    }
    const timer = window.setTimeout(() => {
      fetchRegistry({ silent: true })
    }, 3000)
    return () => window.clearTimeout(timer)
  }, [analysisReady, fetchRegistry, hasActiveDescriptionRefresh])

  const lastClearSignalRef = React.useRef<number | null>(null)

  const clearRegistryState = React.useCallback(() => {
    registryRequestRef.current += 1
    relationshipDetailRequestRef.current += 1
    setRegistry(null)
    setCharacterRecords([])
    setError(null)
    setLoading(false)
    setCharactersLoading(false)
    setRebuilding(false)
    setRelationshipDetail(null)
    selectedRelationshipPairKeyRef.current = null
    setSelectedRelationshipPairKey(null)
    setRelationshipDetailError(null)
    setRelationshipDetailLoading(false)
  }, [])

  React.useEffect(() => {
    if (registryClearSignal == null) {
      return
    }
    if (lastClearSignalRef.current === registryClearSignal) {
      return
    }
    lastClearSignalRef.current = registryClearSignal
    clearRegistryState()
  }, [registryClearSignal, clearRegistryState])

  React.useEffect(() => {
    if (analysisLoading) {
      clearRegistryState()
    }
  }, [analysisLoading, clearRegistryState])

  React.useEffect(() => {
    if (!analysisReady && !analysisRunning) {
      clearRegistryState()
    }
  }, [analysisReady, analysisRunning, clearRegistryState])

  const lastRefreshSignalRef = React.useRef<number | null>(null)
  const lastRegistryContractRefreshRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (registryRefreshSignal == null) {
      return
    }
    if (!analysisReady) {
      return
    }
    if (lastRefreshSignalRef.current === registryRefreshSignal) {
      return
    }
    lastRefreshSignalRef.current = registryRefreshSignal
    rebuildRegistry({ silent: true })
  }, [analysisReady, registryRefreshSignal, rebuildRegistry])

  React.useEffect(() => {
    if (
      !analysisReady ||
      !registry?.registryRefreshRequired ||
      rebuilding ||
      isRegistryRefreshing
    ) {
      return
    }
    const revision = registryContractRevision || 'unknown'
    if (lastRegistryContractRefreshRef.current === revision) {
      return
    }
    lastRegistryContractRefreshRef.current = revision
    void rebuildRegistry({ silent: true })
  }, [
    analysisReady,
    rebuildRegistry,
    rebuilding,
    isRegistryRefreshing,
    registry,
    registryContractRevision,
  ])

  const updatedAtLabel = React.useMemo(() => {
    if (!registry?.updatedAt) {
      return '—'
    }
    try {
      const date = new Date(registry.updatedAt)
      return date.toLocaleString()
    } catch {
      return registry.updatedAt
    }
  }, [registry?.updatedAt])

  const stats = React.useMemo(() => {
    if (registry?.stats) {
      return registry.stats
    }
    if (registry) {
      return {
        characterCount: registry.characters?.length || 0,
        relationshipCount: registry.relationships?.length || 0,
      }
    }
    return null
  }, [registry])

  const isTabBusy = analysisLoading || loading || rebuilding || charactersLoading
  const disableSection = analysisLoading
  const disableActions = isTabBusy || analysisRunning || splitOperationRunning
  const disableRunAnalysis = disableActions || analysisRunning
  const actionDisabledTooltip = analysisRunning
    ? 'Analysis in progress'
    : splitOperationRunning
      ? 'Character split in progress'
    : !analysisReady
      ? 'Run analysis first'
      : null
  const splitStatusLabel = splitStatusKey || null
  const splitStatusTone: 'info' | 'success' | 'warning' | 'error' = splitStatusKey === 'completed'
    ? 'success'
    : splitStatusKey === 'failed'
      ? 'error'
      : splitStatusKey === 'blocked'
        ? 'warning'
        : 'info'
  const wrapDisabledAction = React.useCallback(
    (button: React.ReactElement) => {
      if (!actionDisabledTooltip || !button.props.disabled) {
        return button
      }
      return (
        <Tooltip title={actionDisabledTooltip}>
          <span className="characters-disabled-tooltip">{button}</span>
        </Tooltip>
      )
    },
    [actionDisabledTooltip]
  )

  const characterList = React.useMemo(() => {
    if (!analysisReady) {
      return []
    }
    const source = characterRecords.length > 0 ? characterRecords : registry?.characters || []
    return source.filter((character) => !isPlaceholderCharacter(character))
  }, [analysisReady, characterRecords, registry?.characters])

  const relationshipList = React.useMemo(() => {
    if (!analysisReady) {
      return []
    }
    const relationships = registry?.relationships || []
    const seen = new Set<string>()
    return relationships.filter((relationship) => {
      const pairKey = getRelationshipPairKey(relationship)
      if (isPlaceholderRelationship(relationship) || !pairKey || seen.has(pairKey)) {
        return false
      }
      seen.add(pairKey)
      return true
    })
  }, [analysisReady, registry?.relationships])

  const invalidRelationshipCount = React.useMemo(() => {
    const relationships = registry?.relationships || []
    const seen = new Set<string>()
    const clientInvalidCount = relationships.filter((relationship) => {
      const pairKey = getRelationshipPairKey(relationship)
      if (isPlaceholderRelationship(relationship) || !pairKey || seen.has(pairKey)) {
        return true
      }
      seen.add(pairKey)
      return false
    }).length
    return Math.max(clientInvalidCount, registry?.invalidRelationshipCount || 0)
  }, [registry?.invalidRelationshipCount, registry?.relationships])

  const relationshipByPairKey = React.useMemo(() => {
    const entries = new Map<string, RelationshipEntry>()
    relationshipList.forEach((relationship) => {
      const pairKey = getRelationshipPairKey(relationship)
      if (pairKey && !entries.has(pairKey)) {
        entries.set(pairKey, relationship)
      }
    })
    return entries
  }, [relationshipList])

  const relationshipDetailTarget = React.useMemo(() => {
    if (!selectedRelationshipPairKey) {
      return null
    }
    return relationshipByPairKey.get(selectedRelationshipPairKey) || null
  }, [relationshipByPairKey, selectedRelationshipPairKey])

  const hasAnalysisData = React.useMemo(() => {
    if (!analysisReady) {
      return false
    }
    return relationshipList.length > 0
  }, [analysisReady, relationshipList])

  const getTourTarget = React.useCallback((ids: string[]): HTMLElement | null => {
    if (typeof document === 'undefined') return null
    for (const id of ids) {
      const el = document.querySelector(`[data-tour-id="${id}"]`)
      if (el instanceof HTMLElement) {
        return el
      }
    }
    return null
  }, [])

  const resolveTourTarget = React.useCallback(
    (primaryIds: string[], fallbackIds?: string[]) => {
      const found = getTourTarget(primaryIds) ?? (fallbackIds ? getTourTarget(fallbackIds) : null)
      if (found) return found
      if (typeof document !== 'undefined' && document.body) {
        return document.body
      }
      if (!tourFallbackRef.current) {
        tourFallbackRef.current =
          typeof document !== 'undefined' ? document.createElement('div') : ({} as HTMLElement)
      }
      return tourFallbackRef.current
    },
    [getTourTarget]
  )

  const renderTourPanel = React.useCallback(
    (content: React.ReactNode) => renderAppTourPanel(content),
    []
  )

  const isTourElementReady = React.useCallback((element: HTMLElement | null) => {
    if (!element) return false
    const readyFlag = element.getAttribute('data-tour-ready')
    if (readyFlag == null) return true
    return readyFlag === 'true'
  }, [])

  const waitForTourTarget = React.useCallback(
    async (ids: string[], waitTokenRef: React.MutableRefObject<number>) => {
      const token = ++waitTokenRef.current
      const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

      while (waitTokenRef.current === token) {
        const element = getTourTarget(ids)
        if (element && isTourElementReady(element)) {
          return true
        }
        await sleep(50)
      }
      return false
    },
    [getTourTarget, isTourElementReady]
  )

  const isTourReady = hasAccess && analysisReady && tourFlagsInitialized && !isTabBusy && registry !== null

  React.useEffect(() => {
    if (!isTourReady) return
    if (controlsTourSeen) return
    if (controlsTourOpen || analysisTourOpen) return
    if (controlsTourTriggeredRef.current) return
    controlsTourTriggeredRef.current = true
    const timer = window.setTimeout(() => {
      pendingControlsTourStepRef.current = null
      controlsTourWaitTokenRef.current += 1
      setControlsTourStep(0)
      setControlsTourOpen(true)
    }, 200)
    return () => window.clearTimeout(timer)
  }, [analysisTourOpen, controlsTourOpen, controlsTourSeen, isTourReady])

  React.useEffect(() => {
    if (!isTourReady) return
    if (analysisTourSeen) return
    if (!hasAnalysisData) return
    if (!controlsTourSeen) return
    if (controlsTourOpen || analysisTourOpen) return
    if (analysisTourTriggeredRef.current) return
    analysisTourTriggeredRef.current = true
    const timer = window.setTimeout(() => {
      pendingAnalysisTourStepRef.current = null
      analysisTourWaitTokenRef.current += 1
      setAnalysisTourStep(0)
      setAnalysisTourOpen(true)
    }, 200)
    return () => window.clearTimeout(timer)
  }, [
    analysisTourOpen,
    analysisTourSeen,
    controlsTourOpen,
    controlsTourSeen,
    hasAnalysisData,
    isTourReady,
  ])

  const scrollToPanelHeader = React.useCallback(
    (key: string, ref: React.RefObject<HTMLDivElement>) => {
      window.setTimeout(() => {
        const target = ref.current || document.querySelector(`[data-panel-key="${key}"]`)
        if (target && 'scrollIntoView' in target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 0)
    },
    []
  )

  const scrollToCharactersPanel = React.useCallback(() => {
    scrollToPanelHeader(CHARACTERS_PANEL_KEY, charactersPanelHeaderRef)
  }, [scrollToPanelHeader])

  const scrollToRelationshipsPanel = React.useCallback(() => {
    scrollToPanelHeader(RELATIONSHIPS_PANEL_KEY, relationshipsPanelHeaderRef)
  }, [scrollToPanelHeader])

  const characterOptions = React.useMemo(() => {
    const seen = new Set<string>()
    const options: Array<{ label: string; value: string }> = []
    characterList.forEach((character) => {
      const name = (character.canonicalName || '').trim()
      if (!name) {
        return
      }
      const key = name.toLowerCase()
      if (seen.has(key)) {
        return
      }
      seen.add(key)
      options.push({
        label: name,
        value: name,
      })
    })
    return options
  }, [characterList])

  const aliasSourceOptions = React.useMemo(() => {
    if (!aliasTarget) {
      return characterOptions
    }
    const targetKey = aliasTarget.toLowerCase()
    return characterOptions.filter((option) => option.value.toLowerCase() !== targetKey)
  }, [aliasTarget, characterOptions])

  const initialDescriptionOverridesRef = React.useRef<Record<string, string[]>>({})
  const descriptionTouchedRef = React.useRef<Set<string>>(new Set())

  const editorDescriptionSummary = React.useMemo(() => {
    if (!editorTarget) {
      return ''
    }
    return formatDescriptionSummary(editorTarget.descriptionOverrides, editorTarget.descriptionCounts)
  }, [editorTarget])

  const fetchAliasMappings = React.useCallback(async () => {
    if (!bookId || !hasAccess) {
      return
    }
    setAliasLoading(true)
    try {
      const { data } = await axios.get<{ mappings?: CharacterAliasMapping[] }>(
        `${apiBase}/uploadedbooks/book/${bookId}/characters/aliases`,
        { params: { userId } }
      )
      setAliasMappings(data?.mappings || [])
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Unable to load alias mappings.'
      message.error(detail)
    } finally {
      setAliasLoading(false)
    }
  }, [apiBase, bookId, hasAccess, userId])

  React.useEffect(() => {
    if (!isEditorModalVisible) {
      return
    }
    fetchAliasMappings()
  }, [fetchAliasMappings, isEditorModalVisible])

  const fetchRelationshipDetail = React.useCallback(
    async (relationship: RelationshipEntry) => {
      if (!bookId || !hasAccess) {
        return null
      }
      const requestedPairKey = getRelationshipPairKey(relationship)
      if (!requestedPairKey) {
        setRelationshipDetail(null)
        setRelationshipDetailError('This relationship has invalid character identifiers. Refresh the Character List.')
        return null
      }
      const requestedRegistryRevision = registryContractRevision
      const requestId = ++relationshipDetailRequestRef.current
      setRelationshipDetailLoading(true)
      setRelationshipDetailError(null)
      const fixtureDetail =
        tutorialRelationshipDetails?.[relationship.id] ||
        tutorialRelationshipDetails?.[`${relationship.characterAId}:${relationship.characterBId}`]
      if (fixtureDetail) {
        setRelationshipDetail(fixtureDetail)
        setRelationshipDetailLoading(false)
        return fixtureDetail
      }
      try {
        const { data } = await axios.get<RelationshipDetailPayload>(
          `${apiBase}/uploadedbooks/book/${bookId}/relationships/detail`,
          {
            headers: await getAuthHeaders(),
            params: {
              userId,
              characterAId: relationship.characterAId,
              characterBId: relationship.characterBId,
              characterAName: relationship.characterAName,
              characterBName: relationship.characterBName,
              pairKey: requestedPairKey,
              registryRevision: requestedRegistryRevision || undefined,
            },
          }
        )
        if (relationshipDetailRequestRef.current !== requestId) {
          return null
        }
        const responsePairKey = getRelationshipPairKey(data)
        const declaredResponsePairKey = (data?.pairKey || '').trim()
        const responseRegistryRevision = normalizeContractString(data?.registryRevision)
        if (
          !responsePairKey ||
          responsePairKey !== requestedPairKey ||
          (declaredResponsePairKey && declaredResponsePairKey !== requestedPairKey) ||
          (requestedRegistryRevision && responseRegistryRevision !== requestedRegistryRevision)
        ) {
          setRelationshipDetail(null)
          setRelationshipDetailError(
            'Relationship data changed while it was loading. Refresh and select the relationship again.'
          )
          return null
        }
        const normalizedData: RelationshipDetailPayload = {
          ...data,
          pairKey: requestedPairKey,
          registryRevision: responseRegistryRevision || requestedRegistryRevision,
        }
        setRelationshipDetail(normalizedData)
        return normalizedData
      } catch (err: any) {
        if (relationshipDetailRequestRef.current !== requestId) {
          return null
        }
        const responseDetail = err?.response?.data?.detail
        const detail =
          (typeof responseDetail === 'string'
            ? responseDetail
            : responseDetail?.message || responseDetail?.error) ||
          err?.message ||
          'Unable to load relationship chapter details.'
        setRelationshipDetail(null)
        setRelationshipDetailError(detail)
        if (err?.response?.status === 409) {
          selectedRelationshipPairKeyRef.current = null
          setSelectedRelationshipPairKey(null)
          setRelationshipSelectedChapters([])
          relationshipAutoSelectAllRef.current = false
          setRegistry(null)
          setCharacterRecords([])
          await fetchRegistry({ silent: true })
        }
        message.error(detail)
        return null
      } finally {
        if (relationshipDetailRequestRef.current === requestId) {
          setRelationshipDetailLoading(false)
        }
      }
    },
    [
      apiBase,
      bookId,
      fetchRegistry,
      hasAccess,
      registryContractRevision,
      tutorialRelationshipDetails,
      userId,
    ]
  )

  const relationshipSamplesByCharacterId = React.useMemo(() => {
    const map = new Map<string, RelationshipChapterSample[]>()
    if (relationshipList.length === 0) {
      return map
    }
    relationshipList.forEach((relationship) => {
      const samples = relationship.chapterSamples || []
      if (samples.length === 0) {
        return
      }
      const ids = [relationship.characterAId, relationship.characterBId].filter(Boolean)
      ids.forEach((id) => {
        const existing = map.get(id) || []
        samples.forEach((sample) => {
          if (!sample) {
            return
          }
          const key = sample.chapterGuid || `${sample.chapterNumber ?? ''}-${sample.chapterName ?? ''}`
          if (!key) {
            return
          }
          const alreadyTracked = existing.some((entry) => {
            if (entry.chapterGuid && sample.chapterGuid) {
              return entry.chapterGuid === sample.chapterGuid
            }
            return (
              entry.chapterNumber === sample.chapterNumber &&
              entry.chapterName === sample.chapterName
            )
          })
          if (!alreadyTracked) {
            existing.push(sample)
          }
        })
        map.set(id, existing)
      })
    })
    return map
  }, [relationshipList])

  const clearSplitJobPollTimer = React.useCallback(() => {
    if (splitJobPollTimerRef.current != null) {
      window.clearTimeout(splitJobPollTimerRef.current)
      splitJobPollTimerRef.current = null
    }
  }, [])

  React.useEffect(() => {
    return () => {
      clearSplitJobPollTimer()
    }
  }, [clearSplitJobPollTimer])

  React.useEffect(() => {
    clearSplitJobPollTimer()
    setIsSplitModalVisible(false)
    setSplitSourceName('')
    setSplitName('')
    setSplitTargetName('')
    setSplitSubmitting(false)
    setSplitJob(null)
    setSplitJobError(null)
    setSplitWarnings([])
    setSplitResultSummary(null)
  }, [bookId, clearSplitJobPollTimer])

  const openSplitModal = React.useCallback(
    (sourceNameValue: string, splitNameValue: string, targetNameValue?: string) => {
      const source = (sourceNameValue || '').trim()
      const splitAlias = (splitNameValue || '').trim()
      if (!source || !splitAlias) {
        message.error('Choose a source character and alias to split.')
        return
      }
      clearSplitJobPollTimer()
      const target = (targetNameValue || splitAlias).trim() || splitAlias
      setSplitSourceName(source)
      setSplitName(splitAlias)
      setSplitTargetName(target)
      setSplitSubmitting(false)
      setSplitJob(null)
      setSplitJobError(null)
      setSplitWarnings([])
      setSplitResultSummary(null)
      setIsAliasModalVisible(false)
      setIsSplitModalVisible(true)
    },
    [clearSplitJobPollTimer]
  )

  const renderAliasSplitAction = React.useCallback(
    (
      sourceCanonicalName: string,
      alias: string,
      disabled: boolean = false
    ) => (
      <Tooltip
        title={
          disabled
            ? null
            : `Separate ${alias} into its own character`
        }
      >
        <Button
          size="small"
          type="text"
          className="characters-alias-split-trigger"
          icon={<BranchesOutlined />}
          aria-label={`Separate alias ${alias} from ${sourceCanonicalName}`}
          disabled={disabled}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            openSplitModal(sourceCanonicalName, alias)
          }}
        />
      </Tooltip>
    ),
    [openSplitModal]
  )

  const renderAliasSplitChip = React.useCallback(
    (sourceCanonicalName: string, alias: string, disabled = false) => (
      <span className="characters-alias-split-chip">
        <span className="characters-alias-split-label">{alias}</span>
        {renderAliasSplitAction(sourceCanonicalName, alias, disabled)}
      </span>
    ),
    [renderAliasSplitAction]
  )

  const closeSplitModal = React.useCallback(() => {
    setIsSplitModalVisible(false)
  }, [])

  const pollSplitJob = React.useCallback(
    async (operationId: string, attempt = 0) => {
      const operationKey = (operationId || '').trim()
      if (!operationKey || !bookId || !hasAccess) {
        return
      }
      try {
        const { data } = await axios.get<CharacterSplitJobPayload>(
          `${apiBase}/uploadedbooks/book/${bookId}/characters/split-jobs/${operationKey}`,
          { params: { userId } }
        )
        setSplitJob(data || null)
        const status = normalizeSplitStatus(data?.status)
        if (status === 'completed') {
          setSplitResultSummary((data?.resultSummary as CharacterSplitSummary) || null)
          clearSplitJobPollTimer()
          return
        }
        if (status === 'failed' || status === 'blocked') {
          const detail = (data?.error || 'Split did not complete.').trim()
          setSplitJobError(detail)
          clearSplitJobPollTimer()
          return
        }
        if (attempt >= 45) {
          setSplitWarnings((prev) => {
            if (prev.includes('Split is taking longer than expected. Status will continue updating in the background.')) {
              return prev
            }
            return [...prev, 'Split is taking longer than expected. Status will continue updating in the background.']
          })
          clearSplitJobPollTimer()
          return
        }
        clearSplitJobPollTimer()
        splitJobPollTimerRef.current = window.setTimeout(() => {
          void pollSplitJob(operationKey, attempt + 1)
        }, 2000)
      } catch (err: any) {
        if (attempt >= 6) {
          const detail =
            err?.response?.data?.detail ||
            err?.message ||
            'Unable to fetch split operation status.'
          setSplitJobError(typeof detail === 'string' ? detail : 'Unable to fetch split operation status.')
          clearSplitJobPollTimer()
          return
        }
        clearSplitJobPollTimer()
        splitJobPollTimerRef.current = window.setTimeout(() => {
          void pollSplitJob(operationKey, attempt + 1)
        }, 2000)
      }
    },
    [apiBase, bookId, clearSplitJobPollTimer, hasAccess, userId]
  )

  const executeSplitCharacter = React.useCallback(async () => {
    const source = splitSourceName.trim()
    const splitAlias = splitName.trim()
    const target = splitTargetName.trim() || splitAlias
    if (!source || !splitAlias) {
      message.error('Provide both the source character and the name to split.')
      return
    }
    setSplitSubmitting(true)
    setSplitJobError(null)
    setSplitWarnings([])
    setSplitResultSummary(null)
    setSplitJob({
      status: 'running',
      sourceName: source,
      splitName: splitAlias,
      targetName: target,
    })
    try {
      const { data } = await axios.post<CharacterSplitResponse>(
        `${apiBase}/uploadedbooks/book/${bookId}/characters/split`,
        {
          userId,
          sourceName: source,
          splitName: splitAlias,
          targetName: target,
          reanalyzeMode: 'auto' as CharacterSplitReanalyzeMode,
        }
      )
      const summary = data?.split || null
      const operationId = (summary?.operationId || '').trim() || null
      const warnings: string[] = []
      const ambiguousCount = summary?.rewrite?.ambiguousRows ?? 0
      if (ambiguousCount > 0) {
        warnings.push(
          `${formatPluralCount(ambiguousCount, 'relationship line')} still need review.`
        )
      }
      const reanalysisStatus = normalizeSplitStatus(String(data?.reanalysis?.status || ''))
      if (reanalysisStatus === 'queued') {
        warnings.push('Automatic chapter check was queued for impacted chapters.')
      }
      setSplitResultSummary(summary)
      setSplitWarnings(warnings)
      if (operationId) {
        void pollSplitJob(operationId)
      } else {
        setSplitJob({
          status: data?.status || summary?.status || 'completed',
          sourceName: source,
          splitName: splitAlias,
          targetName: target,
          resultSummary: summary || undefined,
        })
      }
      await fetchAliasMappings()
      await fetchRegistry({ silent: true })
      closeSplitModal()
      message.success('Character split completed.')
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      let errorMessage = 'Unable to split character.'
      const warnings: string[] = []
      if (typeof detail === 'string') {
        errorMessage = detail
      } else if (detail && typeof detail === 'object') {
        const detailMessage = (detail.message || detail.error || '').toString().trim()
        if (detailMessage) {
          errorMessage = detailMessage
        }
        const activeLocks = Array.isArray(detail.activeLocks) ? detail.activeLocks : []
        if (activeLocks.length > 0) {
          const lockSummary = activeLocks
            .slice(0, 5)
            .map((entry: any) => {
              const type = (entry?.type || 'Active job').toString().trim()
              const chapter = (entry?.chapterGuid || '').toString().trim()
              return chapter ? `${type} (${chapter})` : type
            })
            .join(', ')
          warnings.push(`Active jobs: ${lockSummary}`)
        }
      } else if (err?.message) {
        errorMessage = err.message
      }
      const normalizedErrorMessage = errorMessage.toLowerCase()
      const isAnalysisBusyError =
        normalizedErrorMessage.includes('analysis is running') ||
        normalizedErrorMessage.includes('analysis not completed') ||
        normalizedErrorMessage.includes('retry after it completes')
      setSplitJob({
        status: isAnalysisBusyError ? 'blocked' : 'failed',
        sourceName: source,
        splitName: splitAlias,
        targetName: target,
      })
      setSplitJobError(errorMessage)
      setSplitWarnings(warnings)
      if (isAnalysisBusyError) {
        message.info(errorMessage)
      } else {
        message.error(errorMessage)
      }
    } finally {
      setSplitSubmitting(false)
    }
  }, [
    apiBase,
    bookId,
    fetchAliasMappings,
    fetchRegistry,
    closeSplitModal,
    pollSplitJob,
    splitName,
    splitSourceName,
    splitTargetName,
    userId,
  ])

  const submitSplitCharacter = React.useCallback(() => {
    const source = splitSourceName.trim()
    const splitAlias = splitName.trim()
    const target = splitTargetName.trim() || splitAlias
    if (!source || !splitAlias) {
      message.error('Provide both the source character and the name to split.')
      return
    }
    Modal.confirm({
      title: 'Separate merged character?',
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div>
            This will move <strong>{splitAlias}</strong> out of <strong>{source}</strong> and save it
            as <strong>{target}</strong>.
          </div>
          <div>
            Impacted chapters will use the current automatic reanalysis behavior after the split.
          </div>
        </div>
      ),
      okText: 'Separate character',
      cancelText: 'Cancel',
      onOk: executeSplitCharacter,
    })
  }, [executeSplitCharacter, splitName, splitSourceName, splitTargetName])

  const normalizedSearch = React.useMemo(() => characterSearch.trim().toLowerCase(), [characterSearch])
  const normalizedRelationshipSearch = React.useMemo(
    () => relationshipSearch.trim().toLowerCase(),
    [relationshipSearch]
  )

  const filterMenuPopupContainer = React.useCallback(
    (triggerNode: HTMLElement) => triggerNode.parentElement ?? document.body,
    []
  )

  const visibleCharacters = React.useMemo(() => {
    const filtered = characterList.filter((character) => {
      if (showConflictsOnly && !character.hasGenderConflict) {
        return false
      }
      if (genderFilter !== 'all') {
        const normalizedGender = normalizeGenderValue((character.gender || '').toLowerCase())
        if (normalizedGender !== genderFilter) {
          return false
        }
      }
      if (!normalizedSearch) {
        return true
      }
      const candidates = [
        character.canonicalName,
        ...(character.aliases || []),
        ...Object.keys(character.aliasCounts || {}),
      ].filter((value) => isValidAliasValue(value))
      return candidates.some((value) =>
        (value || '').toLowerCase().includes(normalizedSearch)
      )
    })

    const sorted = [...filtered]
    const getCharacterSortName = (character: CharacterEntry) =>
      (character.canonicalName || 'Unnamed').trim().toLowerCase()
    const compareByName = (a: CharacterEntry, b: CharacterEntry) =>
      getCharacterSortName(a).localeCompare(getCharacterSortName(b))
    sorted.sort((a, b) => {
      if (characterSort === 'mostChapters') {
        const diff = getCharacterChapterCount(b) - getCharacterChapterCount(a)
        if (diff !== 0) {
          return diff
        }
      } else if (characterSort === 'alpha') {
        return compareByName(a, b)
      }
      return compareByName(a, b)
    })
    return sorted
  }, [characterList, genderFilter, normalizedSearch, showConflictsOnly, characterSort])

  const characterDetailTarget = React.useMemo(() => {
    if (!characterDetailId) {
      return null
    }
    return characterList.find((character) => character.id === characterDetailId) || null
  }, [characterDetailId, characterList])

  const characterFilterChips = React.useMemo(() => {
    const chips: Array<{ key: string; label: string; onClear: () => void }> = []
    const searchValue = characterSearch.trim()
    if (searchValue) {
      chips.push({
        key: 'search',
        label: `Search: ${searchValue}`,
        onClear: () => setCharacterSearch(''),
      })
    }
    if (genderFilter !== 'all') {
      const label =
        GENDER_FILTER_OPTIONS.find((option) => option.value === genderFilter)?.label ||
        genderFilter
      chips.push({
        key: 'gender',
        label: `Gender: ${label}`,
        onClear: () => setGenderFilter('all'),
      })
    }
    if (showConflictsOnly) {
      chips.push({
        key: 'conflicts',
        label: 'Conflicts only',
        onClear: () => setShowConflictsOnly(false),
      })
    }
    return chips
  }, [characterSearch, genderFilter, showConflictsOnly])

  const clearCharacterFilters = React.useCallback(() => {
    setCharacterSearch('')
    setGenderFilter('all')
    setShowConflictsOnly(false)
  }, [])

  const visibleRelationships = React.useMemo(() => {
    const relationships = relationshipList
    const filtered = relationships.filter((relationship) => {
      if (relationshipStatusFilter !== 'all') {
        if (getRelationshipStatusKey(relationship) !== relationshipStatusFilter) {
          return false
        }
      }
      if (relationshipFormalityFilter !== 'all') {
        if (relationshipFormalityFilter === 'mixed') {
          if (!hasMixedFormality(relationship)) {
            return false
          }
        } else if (relationshipFormalityFilter === 'asymmetric') {
          if (!isRelationshipAsymmetric(relationship)) {
            return false
          }
        } else if (relationshipFormalityFilter === 'unknown') {
          if (!hasUnknownFormality(relationship)) {
            return false
          }
        } else {
          const symmetric = getRelationshipSymmetricFormality(relationship)
          if (symmetric !== relationshipFormalityFilter) {
            return false
          }
        }
      }
      if (relationshipChapterFilter !== 'all') {
        const chapterCount = getRelationshipChapterCount(relationship)
        if (relationshipChapterFilter === '1-2') {
          if (chapterCount < 1 || chapterCount > 2) {
            return false
          }
        } else if (relationshipChapterFilter === '3-5') {
          if (chapterCount < 3 || chapterCount > 5) {
            return false
          }
        } else if (relationshipChapterFilter === '6-10') {
          if (chapterCount < 6 || chapterCount > 10) {
            return false
          }
        } else if (relationshipChapterFilter === '11+') {
          if (chapterCount < 11) {
            return false
          }
        }
      }
      if (!normalizedRelationshipSearch) {
        return true
      }
      const candidates = [relationship.characterAName, relationship.characterBName]
      return candidates.some((value) =>
        (value || '').toLowerCase().includes(normalizedRelationshipSearch)
      )
    })

    const sorted = [...filtered]
    const compareByName = (a: RelationshipEntry, b: RelationshipEntry) => {
      const aName = `${a.characterAName} ${a.characterBName}`.toLowerCase()
      const bName = `${b.characterAName} ${b.characterBName}`.toLowerCase()
      return aName.localeCompare(bName)
    }
    sorted.sort((a, b) => {
      if (relationshipSort === 'mostChapters') {
        const diff = getRelationshipChapterCount(b) - getRelationshipChapterCount(a)
        if (diff !== 0) {
          return diff
        }
      } else if (relationshipSort === 'alpha') {
        return compareByName(a, b)
      }
      return compareByName(a, b)
    })
    return sorted
  }, [
    normalizedRelationshipSearch,
    relationshipList,
    relationshipStatusFilter,
    relationshipFormalityFilter,
    relationshipChapterFilter,
    relationshipSort,
  ])

  const relationshipSummaryMetrics = React.useMemo(() => {
    const relationships = relationshipList
    let needsReviewCount = 0
    relationships.forEach((relationship) => {
      if (getRelationshipStatusKey(relationship) === 'needsReview') {
        needsReviewCount += 1
      }
    })
    return {
      total: relationships.length,
      needsReview: needsReviewCount,
      suggested: relationships.filter(r => getRelationshipStatusKey(r) === 'suggested').length,
    }
  }, [relationshipList])

  const relationshipFilterChips = React.useMemo(() => {
    const chips: Array<{ key: string; label: string; onClear: () => void }> = []
    const searchValue = relationshipSearch.trim()
    if (searchValue) {
      chips.push({
        key: 'search',
        label: `Search: ${searchValue}`,
        onClear: () => setRelationshipSearch(''),
      })
    }
    if (relationshipStatusFilter !== 'all') {
      const label =
        RELATIONSHIP_STATUS_FILTER_OPTIONS.find(
          (option) => option.value === relationshipStatusFilter
        )?.label || relationshipStatusFilter
      chips.push({
        key: 'status',
        label: `Status: ${label}`,
        onClear: () => setRelationshipStatusFilter('all'),
      })
    }
    if (relationshipFormalityFilter !== 'all') {
      const label =
        RELATIONSHIP_FORMALITY_FILTER_OPTIONS.find(
          (option) => option.value === relationshipFormalityFilter
        )?.label || relationshipFormalityFilter
      chips.push({
        key: 'formality',
        label: `Formal / Informal Speech: ${label}`,
        onClear: () => setRelationshipFormalityFilter('all'),
      })
    }
    if (relationshipChapterFilter !== 'all') {
      const label =
        RELATIONSHIP_CHAPTER_FILTER_OPTIONS.find(
          (option) => option.value === relationshipChapterFilter
        )?.label || relationshipChapterFilter
      chips.push({
        key: 'chapters',
        label: `Chapter count: ${label}`,
        onClear: () => setRelationshipChapterFilter('all'),
      })
    }
    return chips
  }, [
    relationshipStatusFilter,
    relationshipFormalityFilter,
    relationshipChapterFilter,
    relationshipSearch,
  ])

  const clearRelationshipFilters = React.useCallback(() => {
    setRelationshipSearch('')
    setRelationshipStatusFilter('all')
    setRelationshipFormalityFilter('all')
    setRelationshipChapterFilter('all')
  }, [])

  const relationshipDetailStats = React.useMemo(() => {
    if (!relationshipDetail) {
      return null
    }
    let formalRowsAtoB = 0
    let informalRowsAtoB = 0
    let formalRowsBtoA = 0
    let informalRowsBtoA = 0
    const overrides: RelationshipFormalityOverride[] = []

    relationshipDetail.chapters.forEach((chapter) => {
      const rows = chapter.rows || []
      let chapterFormalA = 0
      let chapterInformalA = 0
      let chapterFormalB = 0
      let chapterInformalB = 0
      rows.forEach((row) => {
        const normalizedA = normalizeRelationshipFormalityValue(
          row.formalityAtoB ?? row.formality
        )
        const normalizedB = normalizeRelationshipFormalityValue(
          row.formalityBtoA ?? row.formality
        )
        if (normalizedA === 'Formal') {
          formalRowsAtoB += 1
          chapterFormalA += 1
        } else if (normalizedA === 'Informal') {
          informalRowsAtoB += 1
          chapterInformalA += 1
        }
        if (normalizedB === 'Formal') {
          formalRowsBtoA += 1
          chapterFormalB += 1
        } else if (normalizedB === 'Informal') {
          informalRowsBtoA += 1
          chapterInformalB += 1
        }
      })
      if (!chapter.chapterGuid) {
        return
      }
      const override: RelationshipFormalityOverride = { chapterGuid: chapter.chapterGuid }
      if (chapterFormalA > 0 && chapterInformalA === 0) {
        override.formalityAtoB = 'Formal'
      } else if (chapterInformalA > 0 && chapterFormalA === 0) {
        override.formalityAtoB = 'Informal'
      }
      if (chapterFormalB > 0 && chapterInformalB === 0) {
        override.formalityBtoA = 'Formal'
      } else if (chapterInformalB > 0 && chapterFormalB === 0) {
        override.formalityBtoA = 'Informal'
      }
      if (override.formalityAtoB || override.formalityBtoA) {
        overrides.push(override)
      }
    })

    const totalRows = relationshipDetail.totalRows ?? formalRowsAtoB + informalRowsAtoB
    const totalChapters = relationshipDetail.totalChapters ?? relationshipDetail.chapters.length
    const hasMixed =
      (formalRowsAtoB > 0 && informalRowsAtoB > 0) ||
      (formalRowsBtoA > 0 && informalRowsBtoA > 0)

    return {
      formalRowsAtoB,
      informalRowsAtoB,
      formalRowsBtoA,
      informalRowsBtoA,
      totalRows,
      totalChapters,
      hasMixed,
      overrides,
    }
  }, [relationshipDetail])

  const relationshipReviewReasons = React.useMemo(() => {
    if (!relationshipDetailTarget) {
      return []
    }
    if (isRelationshipReviewed(relationshipDetailTarget)) {
      const conflicts = relationshipDetail?.approvedDecisionConflicts || relationshipDetailTarget.approvedDecisionConflicts || []
      return conflicts.length ? [`${conflicts.length} chapter observations conflict with the approved directional decisions.`] : []
    }
    const reasons: string[] = []
    if (relationshipDetail) {
      const nameA = getSafeRelationshipName(relationshipDetailTarget.characterAName, 'Character A')
      const nameB = getSafeRelationshipName(relationshipDetailTarget.characterBName, 'Character B')
      const aMixed = relationshipDetail.chapters.filter(
        (chapter) => getChapterDirectionSummary(chapter, 'AtoB') === 'Mixed'
      ).length
      const bMixed = relationshipDetail.chapters.filter(
        (chapter) => getChapterDirectionSummary(chapter, 'BtoA') === 'Mixed'
      ).length
      const aUnknown = relationshipDetail.chapters.filter(
        (chapter) => getChapterDirectionSummary(chapter, 'AtoB') === 'Unknown'
      ).length
      const bUnknown = relationshipDetail.chapters.filter(
        (chapter) => getChapterDirectionSummary(chapter, 'BtoA') === 'Unknown'
      ).length
      if (aMixed > 0) {
        reasons.push(`${nameA} -> ${nameB} mixed in ${formatPluralCount(aMixed, 'chapter')}`)
      }
      if (bMixed > 0) {
        reasons.push(`${nameB} -> ${nameA} mixed in ${formatPluralCount(bMixed, 'chapter')}`)
      }
      if (aUnknown > 0) {
        reasons.push(`${nameA} -> ${nameB} unknown in ${formatPluralCount(aUnknown, 'chapter')}`)
      }
      if (bUnknown > 0) {
        reasons.push(`${nameB} -> ${nameA} unknown in ${formatPluralCount(bUnknown, 'chapter')}`)
      }
    }
    return reasons
  }, [relationshipDetail, relationshipDetailTarget])

  const groupedRelationshipChapters = React.useMemo(() => {
    const groups: Record<RelationshipChapterGroupKey, RelationshipDetailChapter[]> = {
      needsReview: [],
      suggested: [],
      overrides: [],
      matchesDefault: [],
    }
    if (!relationshipDetail) {
      return groups
    }
    relationshipDetail.chapters.forEach((chapter) => {
      const summaryA = getChapterDirectionSummary(chapter, 'AtoB')
      const summaryB = getChapterDirectionSummary(chapter, 'BtoA')
      if (summaryA === 'Mixed' || summaryB === 'Mixed' || summaryA === 'Unknown' || summaryB === 'Unknown' ||
          (hasProvisionalFormality(chapter) && !hasOnlyOptionalDefaults(chapter))) {
        groups.needsReview.push(chapter)
        return
      }
      if (hasOnlyOptionalDefaults(chapter)) {
        groups.suggested.push(chapter)
        return
      }
      if (
        summaryA === relationshipBulkFormalityAtoB &&
        summaryB === relationshipBulkFormalityBtoA
      ) {
        groups.matchesDefault.push(chapter)
        return
      }
      groups.overrides.push(chapter)
    })
    return groups
  }, [relationshipBulkFormalityAtoB, relationshipBulkFormalityBtoA, relationshipDetail])

  const relationshipSelectedChapterSet = React.useMemo(
    () => new Set(relationshipSelectedChapters),
    [relationshipSelectedChapters]
  )

  const relationshipSelectableChapters = React.useMemo(() => {
    if (!relationshipDetail) {
      return []
    }
    const chapterIds = relationshipDetail.chapters
      .map((chapter) => chapter.chapterGuid)
      .filter((chapterGuid): chapterGuid is string => Boolean(chapterGuid))
    return Array.from(new Set(chapterIds))
  }, [relationshipDetail])
  const relationshipSelectableChapterSet = React.useMemo(
    () => new Set(relationshipSelectableChapters),
    [relationshipSelectableChapters]
  )
  const selectedRelationshipChapterCount = React.useMemo(
    () => relationshipSelectedChapters.filter((chapter) => relationshipSelectableChapterSet.has(chapter)).length,
    [relationshipSelectedChapters, relationshipSelectableChapterSet]
  )
  const allRelationshipChaptersSelected =
    relationshipSelectableChapters.length > 0 &&
    selectedRelationshipChapterCount === relationshipSelectableChapters.length
  const someRelationshipChaptersSelected =
    selectedRelationshipChapterCount > 0 && !allRelationshipChaptersSelected
  const currentRegistryRevision = registryContractRevision
  const relationshipTargetPairKey = getRelationshipPairKey(relationshipDetailTarget)
  const relationshipDetailPairKey = getRelationshipPairKey(relationshipDetail)
  const relationshipDetailRevision = normalizeContractString(
    relationshipDetail?.registryRevision
  )
  const relationshipDetailIsCoherent = Boolean(
    relationshipDetailTarget &&
      relationshipDetail &&
      selectedRelationshipPairKey &&
      relationshipTargetPairKey === selectedRelationshipPairKey &&
      relationshipDetailPairKey === selectedRelationshipPairKey &&
      (!currentRegistryRevision || relationshipDetailRevision === currentRegistryRevision)
  )
  const relationshipBulkControlsDisabled =
    !relationshipDetailIsCoherent ||
    relationshipDetailLoading ||
    registryStale ||
    isRegistryRefreshing ||
    analysisRunning ||
    relationshipBulkUpdating
  const relationshipBulkApplyDisabled =
    relationshipBulkControlsDisabled ||
    relationshipSelectableChapters.length === 0 ||
    selectedRelationshipChapterCount === 0
  const relationshipIsReviewed = relationshipDetailTarget
    ? isRelationshipReviewed(relationshipDetailTarget)
    : false
  const relationshipHasAutoNeedsReview = relationshipDetailTarget
    ? hasRelationshipAutoNeedsReview(relationshipDetailTarget)
    : false
  const relationshipShowReviewToggle =
    Boolean(relationshipDetailTarget) &&
    (relationshipIsReviewed || relationshipHasAutoNeedsReview)
  const relationshipReviewControlsDisabled =
    registryStale ||
    !relationshipDetailIsCoherent ||
    isRegistryRefreshing ||
    analysisRunning ||
    relationshipReviewUpdating
  const relationshipNameA = getSafeRelationshipName(relationshipDetailTarget?.characterAName, 'Character A')
  const relationshipNameB = getSafeRelationshipName(relationshipDetailTarget?.characterBName, 'Character B')
  const bulkDirectionLabelAtoB = `How ${relationshipNameA} speaks to ${relationshipNameB}`
  const bulkDirectionLabelBtoA = `How ${relationshipNameB} speaks to ${relationshipNameA}`
  const chapterDirectionLabelAtoB = `${relationshipNameA} -> ${relationshipNameB}`
  const chapterDirectionLabelBtoA = `${relationshipNameB} -> ${relationshipNameA}`
  const applyRelationshipLabel =
    selectedRelationshipChapterCount > 0
      ? `Apply to selected chapters (${selectedRelationshipChapterCount})`
      : 'Apply to selected chapters'

  React.useEffect(() => {
    if (!relationshipDetail) {
      return
    }
    if (!relationshipAutoSelectAllRef.current) {
      return
    }
    relationshipAutoSelectAllRef.current = false
    setRelationshipSelectedChapters(relationshipSelectableChapters)
  }, [relationshipDetail, relationshipSelectableChapters])

  const aliasCollisionSummary = React.useMemo(() => {
    const emptySummary = { count: 0, characterIds: new Set<string>() }
    if (!analysisReady || characterList.length === 0) {
      return emptySummary
    }
    const canonicalByKey = new Map<string, string>()
    characterList.forEach((character) => {
      const canonical = (character.canonicalName || '').trim()
      if (!canonical) {
        return
      }
      canonicalByKey.set(canonical.toLowerCase(), character.id)
    })
    const collisions = new Set<string>()
    const characterIds = new Set<string>()
    characterList.forEach((character) => {
      const canonical = (character.canonicalName || '').trim().toLowerCase()
      if (!canonical) {
        return
      }
      const aliases = new Set<string>([
        ...(character.aliases || []),
        ...Object.keys(character.aliasCounts || {}),
      ])
      aliases.forEach((alias) => {
        const aliasKey = (alias || '').trim().toLowerCase()
        if (!aliasKey || aliasKey === canonical || isCharPlaceholder(aliasKey)) {
          return
        }
        const otherId = canonicalByKey.get(aliasKey)
        if (!otherId || otherId === character.id) {
          return
        }
        const pairKey = [character.id, otherId].sort().join('__')
        collisions.add(pairKey)
        characterIds.add(character.id)
        characterIds.add(otherId)
      })
    })
    return { count: collisions.size, characterIds }
  }, [analysisReady, characterList])
  const unresolvedAliasCount = aliasCollisionSummary.count

  const analysisMissingChapters = React.useMemo(() => {
    if (!analysisSummary || typeof analysisSummary.missingChapters !== 'number') {
      return null
    }
    return analysisSummary.missingChapters
  }, [analysisSummary])

  const characterConflictCount = characterList.filter(
    (character) => character.hasGenderConflict
  ).length
  const characterSummaryMetrics = React.useMemo(() => {
    const needsReviewIds = new Set<string>(aliasCollisionSummary.characterIds)
    characterList.forEach((character) => {
      if (character.hasGenderConflict) {
        needsReviewIds.add(character.id)
      }
    })
    return {
      total: characterList.length,
      needsReview: needsReviewIds.size,
    }
  }, [aliasCollisionSummary.characterIds, characterList, characterList.length])
  const aliasStepDescription =
    unresolvedAliasCount > 0
      ? formatPluralCount(unresolvedAliasCount, 'potential duplicate')
      : 'No duplicates detected'
  const registryStepDescription =
    characterConflictCount > 0
      ? formatPluralCount(characterConflictCount, 'Grammar Gender (for translation) conflict')
      : 'No Grammar Gender conflicts detected'
  const relationshipNeedsReviewCount = relationshipSummaryMetrics.needsReview
  const relationshipsReviewSummary =
    relationshipNeedsReviewCount > 0
      ? `${formatPluralCount(relationshipNeedsReviewCount, 'pair')} ${
          relationshipNeedsReviewCount === 1 ? 'needs' : 'need'
        } review`
      : null
  const relationshipsStepDescription =
    analysisMissingChapters == null
      ? analysisSummaryLoading
        ? 'Checking chapter coverage…'
        : 'Coverage pending'
      : analysisMissingChapters > 0
        ? `${formatPluralCount(analysisMissingChapters, 'chapter')} missing`
        : relationshipsReviewSummary || (relationshipSummaryMetrics.suggested > 0
          ? `${formatPluralCount(relationshipSummaryMetrics.suggested, 'suggested default')} — optional review`
          : 'No unresolved relationship issues')

  const hasActiveFilters =
    Boolean(normalizedSearch) || genderFilter !== 'all' || showConflictsOnly

  const hasRelationshipFilters =
    Boolean(normalizedRelationshipSearch) ||
    relationshipStatusFilter !== 'all' ||
    relationshipFormalityFilter !== 'all' ||
    relationshipChapterFilter !== 'all'

  const hasConflicts =
    (stats?.charactersWithGenderConflict ?? 0) > 0 ||
    characterList.some((character) => character.hasGenderConflict)

  const analysisStepDescription = analysisReady
    ? 'Complete'
    : analysisRunning
      ? 'In progress'
      : 'Not started'
  const analysisStepStatus: StepStatus = analysisRunning
    ? 'process'
    : analysisReady
      ? 'finish'
      : 'wait'
  const aliasStepStatus: StepStatus = !analysisReady
    ? 'wait'
    : unresolvedAliasCount === 0
      ? 'finish'
      : 'process'
  const registryStepStatus: StepStatus = !analysisReady
    ? 'wait'
    : characterConflictCount === 0
      ? 'finish'
      : 'process'
  const relationshipsStepStatus: StepStatus = !analysisReady
    ? 'wait'
    : analysisMissingChapters == null
      ? 'wait'
      : analysisMissingChapters === 0 && relationshipNeedsReviewCount === 0
        ? 'finish'
        : 'process'
  const translationReady =
    analysisReady &&
    unresolvedAliasCount === 0 &&
    characterConflictCount === 0 &&
    (analysisMissingChapters ?? 0) === 0 &&
    relationshipNeedsReviewCount === 0

  const workflowStepItems = React.useMemo<NonNullable<StepsProps['items']>>(() => {
    const items: NonNullable<StepsProps['items']> = [
      {
        title: 'Run analysis',
        status: analysisStepStatus,
        description: analysisStepDescription,
      },
      {
        title: 'Resolve duplicates',
        status: aliasStepStatus,
        description: aliasStepDescription,
      },
      {
        title: 'Review registry',
        status: registryStepStatus,
        description: registryStepDescription,
      },
      {
        title: 'Review relationships',
        status: relationshipsStepStatus,
        description: relationshipsStepDescription,
      },
    ]
    if (showTranslationStep) {
      items.push({
        title: 'Ready for translation',
        status: translationReady ? 'finish' : 'wait',
        description: translationReady ? 'Ready' : 'Complete steps 1-4',
      })
    }
    return items
  }, [
    analysisStepDescription,
    analysisStepStatus,
    aliasStepDescription,
    aliasStepStatus,
    registryStepDescription,
    registryStepStatus,
    relationshipsStepDescription,
    relationshipsStepStatus,
    relationshipNeedsReviewCount,
    showTranslationStep,
    translationReady,
  ])

  const openAliasModal = React.useCallback(() => {
    setIsAliasModalVisible(true)
    fetchAliasMappings()
  }, [fetchAliasMappings])

  const closeAliasModal = React.useCallback(() => {
    setIsAliasModalVisible(false)
    setAliasTarget(null)
    setAliasSources([])
  }, [])

  const normalizeAliasInputs = (values: string[]) => {
    const seen = new Set<string>()
    const normalized: string[] = []
    values.forEach((raw) => {
      const trimmed = String(raw || '').trim()
      if (!isValidAliasValue(trimmed)) return
      const key = trimmed.toLowerCase()
      if (seen.has(key)) return
      seen.add(key)
      normalized.push(trimmed)
    })
    return normalized
  }

  const aliasPreviewAliases = React.useMemo(
    () => normalizeAliasInputs(aliasSources),
    [aliasSources]
  )

  const cleanedAliasMappings = React.useMemo(() => {
    return aliasMappings.filter((mapping) => {
      const alias = (mapping.alias || '').trim()
      const target = (mapping.target || '').trim()
      if (!alias || !target) {
        return false
      }
      if (isCharPlaceholder(alias) || isCharPlaceholder(target)) {
        return false
      }
      return true
    })
  }, [aliasMappings])

  const aliasConflicts = React.useMemo<AliasConflict[]>(() => {
    if (!aliasTarget || aliasPreviewAliases.length === 0) {
      return []
    }
    const nameIndex = new Map<string, Set<string>>()
    const addName = (rawName: string | undefined, canonicalName: string) => {
      const name = (rawName || '').trim()
      if (!name || isCharPlaceholder(name)) {
        return
      }
      const key = name.toLowerCase()
      const bucket = nameIndex.get(key) || new Set<string>()
      bucket.add(canonicalName)
      nameIndex.set(key, bucket)
    }
    characterList.forEach((character) => {
      const name = (character.canonicalName || '').trim()
      if (!name) {
        return
      }
      addName(name, name)
      ;(character.aliases || []).forEach((alias) => addName(alias, name))
      Object.keys(character.aliasCounts || {}).forEach((alias) => addName(alias, name))
    })
    cleanedAliasMappings.forEach((mapping) => {
      const target = (mapping.target || '').trim()
      if (!target) {
        return
      }
      addName(mapping.alias, target)
      addName(target, target)
    })
    const targetKey = aliasTarget.toLowerCase()
    const conflicts: AliasConflict[] = []
    aliasPreviewAliases.forEach((alias) => {
      const aliasKey = alias.toLowerCase()
      if (!aliasKey || aliasKey === targetKey) {
        return
      }
      const matches = nameIndex.get(aliasKey)
      if (!matches) {
        return
      }
      const otherNames = Array.from(matches).filter(
        (canonical) => canonical.toLowerCase() !== targetKey
      )
      if (otherNames.length === 0) {
        return
      }
      conflicts.push({ alias, canonicalName: otherNames.join(', ') })
    })
    return conflicts
  }, [aliasPreviewAliases, aliasTarget, characterList, cleanedAliasMappings])

  const normalizedAliasSearch = React.useMemo(
    () => aliasMappingSearch.trim().toLowerCase(),
    [aliasMappingSearch]
  )

  const filteredAliasMappings = React.useMemo(() => {
    if (!normalizedAliasSearch) {
      return cleanedAliasMappings
    }
    return cleanedAliasMappings.filter((mapping) => {
      const alias = (mapping.alias || '').toLowerCase()
      const target = (mapping.target || '').toLowerCase()
      return alias.includes(normalizedAliasSearch) || target.includes(normalizedAliasSearch)
    })
  }, [cleanedAliasMappings, normalizedAliasSearch])

  const aliasMappingGroups = React.useMemo(() => {
    const groups = new Map<string, string[]>()
    filteredAliasMappings.forEach((mapping) => {
      const target = (mapping.target || '').trim()
      const alias = (mapping.alias || '').trim()
      if (!target || !alias) {
        return
      }
      const existing = groups.get(target) || []
      existing.push(alias)
      groups.set(target, existing)
    })
    return Array.from(groups.entries())
      .map(([target, aliases]) => ({
        target,
        aliases: normalizeAliasInputs(aliases).sort((a, b) => a.localeCompare(b)),
      }))
      .sort((a, b) => a.target.localeCompare(b.target))
  }, [filteredAliasMappings])

  const editorMappedAliases = React.useMemo(() => {
    if (!editorTarget) {
      return []
    }
    const targetKey = (editorTarget.canonicalName || '').toLowerCase()
    if (!targetKey) {
      return []
    }
    const mapped = cleanedAliasMappings
      .filter((mapping) => (mapping.target || '').toLowerCase() === targetKey)
      .map((mapping) => mapping.alias)
    return normalizeAliasInputs(mapped)
  }, [cleanedAliasMappings, editorTarget])

  const editorObservedAliases = React.useMemo(() => {
    if (!editorTarget) {
      return []
    }
    const canonicalKey = (editorTarget.canonicalName || '').toLowerCase()
    const observed = Object.keys(editorTarget.aliasCounts || {})
    return normalizeAliasInputs(observed).filter((alias) => alias.toLowerCase() !== canonicalKey)
  }, [editorTarget])

  const editorAliasOptions = React.useMemo(
    () => editorObservedAliases.map((alias) => ({ label: alias, value: alias })),
    [editorObservedAliases]
  )

  React.useEffect(() => {
    if (!isEditorModalVisible) {
      editorAliasTouchedRef.current = false
      return
    }
    if (!editorTarget) {
      return
    }
    if (!editorAliasTouchedRef.current) {
      setEditorAliases(editorMappedAliases)
    }
  }, [editorMappedAliases, editorTarget, isEditorModalVisible])

  const handleAliasTargetChange = (value?: string) => {
    const trimmed = String(value || '').trim()
    const nextTarget = trimmed || null
    setAliasTarget(nextTarget)
    setAliasSources((prev) => {
      const normalized = normalizeAliasInputs(prev)
      if (!nextTarget) {
        return normalized
      }
      const targetKey = nextTarget.toLowerCase()
      return normalized.filter((name) => name.toLowerCase() !== targetKey)
    })
  }

  const handleAliasSourcesChange = (values: string[]) => {
    const normalized = normalizeAliasInputs(values)
    if (!aliasTarget) {
      setAliasSources(normalized)
      return
    }
    const targetKey = aliasTarget.toLowerCase()
    setAliasSources(normalized.filter((name) => name.toLowerCase() !== targetKey))
  }

  const submitAliasMapping = async (normalizedAliases: string[]) => {
    if (!aliasTarget || normalizedAliases.length === 0) {
      message.error('Select a target and at least one alias.')
      return
    }
    setAliasSaving(true)
    try {
      const { data } = await axios.post<{ mappings?: CharacterAliasMapping[] }>(
        `${apiBase}/uploadedbooks/book/${bookId}/characters/aliases`,
        { userId, targetName: aliasTarget, aliasNames: normalizedAliases }
      )
      setAliasMappings(data?.mappings || [])
      setAliasSources([])
      await fetchRegistry({ silent: true })
      message.success('Alias mapping updated.')
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Unable to update aliases.'
      message.error(detail)
    } finally {
      setAliasSaving(false)
    }
  }

  const handleAddAliases = async () => {
    const normalizedAliases = normalizeAliasInputs(aliasSources)
    if (!aliasTarget || normalizedAliases.length === 0) {
      message.error('Select a target and at least one alias.')
      return
    }
    const conflictContent =
      aliasConflicts.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div>
            The selected names already have character records. Saving will combine them under
            {` ${aliasTarget}`}. Continue only if they are the same individual, not different
            people or pets sharing a name.
          </div>
          <div>
            <strong>Characters to combine:</strong>{' '}
            {aliasConflicts.map((conflict) => `${conflict.alias} → ${conflict.canonicalName}`).join(', ')}
          </div>
        </div>
      ) : (
        <div>
          Save {formatPluralCount(normalizedAliases.length, 'alias')} to <strong>{aliasTarget}</strong>?
        </div>
      )
    Modal.confirm({
      title: aliasConflicts.length > 0 ? 'Merge duplicate characters?' : 'Save alias mapping?',
      content: conflictContent,
      okText: aliasConflicts.length > 0 ? 'Merge characters' : 'Save mapping',
      cancelText: aliasConflicts.length > 0 ? 'Review aliases' : 'Cancel',
      onOk: () => submitAliasMapping(normalizedAliases),
    })
  }

  const handleRemoveAlias = async (alias: string) => {
    setAliasSaving(true)
    try {
      const { data } = await axios.delete<{ mappings?: CharacterAliasMapping[] }>(
        `${apiBase}/uploadedbooks/book/${bookId}/characters/aliases`,
        { data: { userId, aliasNames: [alias] } }
      )
      setAliasMappings(data?.mappings || [])
      await fetchRegistry({ silent: true })
      message.success('Alias mapping removed.')
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Unable to remove alias.'
      message.error(detail)
    } finally {
      setAliasSaving(false)
    }
  }

  const closeAliasRemovalChoice = React.useCallback(() => {
    setAliasRemovalChoice(null)
  }, [])

  const confirmRemoveAlias = React.useCallback((targetCanonicalName: string, alias: string) => {
    setAliasRemovalChoice({ targetCanonicalName, alias })
  }, [])

  const handleRemoveAliasOnly = React.useCallback(async () => {
    const alias = aliasRemovalChoice?.alias
    if (!alias) {
      return
    }
    await handleRemoveAlias(alias)
    setAliasRemovalChoice(null)
  }, [aliasRemovalChoice?.alias, handleRemoveAlias])

  const handleSeparateAliasFromRemoval = React.useCallback(() => {
    const choice = aliasRemovalChoice
    if (!choice) {
      return
    }
    setAliasRemovalChoice(null)
    openSplitModal(choice.targetCanonicalName, choice.alias, choice.alias)
  }, [aliasRemovalChoice, openSplitModal])

  const renderAliasMappingChip = React.useCallback(
    (targetCanonicalName: string, alias: string, disabled = false) => (
      <span className="characters-alias-split-chip characters-alias-manage-chip">
        <span className="characters-alias-split-label">{alias}</span>
        {renderAliasSplitAction(targetCanonicalName, alias, disabled)}
        <Tooltip
          title={
            disabled
              ? null
              : `Remove alias mapping for ${alias}`
          }
        >
          <Button
            size="small"
            type="text"
            className="characters-alias-remove-trigger"
            icon={<CloseOutlined />}
            aria-label={`Remove alias ${alias} from ${targetCanonicalName}`}
            disabled={disabled}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              confirmRemoveAlias(targetCanonicalName, alias)
            }}
          />
        </Tooltip>
      </span>
    ),
    [confirmRemoveAlias, renderAliasSplitAction]
  )

  const openCharacterDetail = React.useCallback(
    (character: CharacterEntry) => {
      if (characterDetailId === character.id) {
        return
      }
      setCharacterDetailId(character.id)
    },
    [characterDetailId]
  )

  const clearCharacterDetail = React.useCallback(() => {
    setCharacterDetailId(null)
  }, [])

  const openRelationshipDetail = React.useCallback(
    (relationship: RelationshipEntry) => {
      const pairKey = getRelationshipPairKey(relationship)
      if (!pairKey) {
        message.error('This relationship has invalid character identifiers. Refresh the Character List.')
        return
      }
      if (selectedRelationshipPairKey === pairKey && relationshipDetail && !relationshipDetailError) {
        return
      }
      selectedRelationshipPairKeyRef.current = pairKey
      setSelectedRelationshipPairKey(pairKey)
      setRelationshipDetail(null)
      setRelationshipDetailError(null)
      const nextAtoB = normalizeRelationshipFormalityValue(relationship.approvedDecision?.formalityAtoB) || getRelationshipDefaultFormality(relationship, 'AtoB')
      const nextBtoA = normalizeRelationshipFormalityValue(relationship.approvedDecision?.formalityBtoA) || getRelationshipDefaultFormality(relationship, 'BtoA')
      const isLinked =
        nextAtoB === nextBtoA &&
        !hasMixedFormality(relationship) &&
        !hasUnknownFormality(relationship)
      setRelationshipBulkFormalityAtoB(nextAtoB)
      setRelationshipBulkFormalityBtoA(nextBtoA)
      setRelationshipBulkLinkDirections(isLinked)
      setRelationshipSelectedChapters([])
      setRelationshipSectionExpanded({
        needsReview: true,
        suggested: true,
        overrides: true,
        matchesDefault: false,
      })
      setRelationshipUndoStack([])
      relationshipAutoSelectAllRef.current = true
      fetchRelationshipDetail(relationship)
    },
    [
      fetchRelationshipDetail,
      relationshipDetail,
      relationshipDetailError,
      selectedRelationshipPairKey,
    ]
  )

  const handleBulkAtoBChange = React.useCallback(
    (value: RelationshipFormalityValue) => {
      setRelationshipBulkFormalityAtoB(value)
      if (relationshipBulkLinkDirections) {
        setRelationshipBulkFormalityBtoA(value)
      }
    },
    [relationshipBulkLinkDirections]
  )

  const handleBulkBtoAChange = React.useCallback(
    (value: RelationshipFormalityValue) => {
      setRelationshipBulkFormalityBtoA(value)
      if (relationshipBulkLinkDirections) {
        setRelationshipBulkFormalityAtoB(value)
      }
    },
    [relationshipBulkLinkDirections]
  )

  const handleBulkLinkDirectionsChange = React.useCallback(
    (checked: boolean) => {
      if (!checked) {
        setRelationshipBulkLinkDirections(false)
        return
      }
      if (relationshipBulkFormalityAtoB !== relationshipBulkFormalityBtoA) {
        const nameA = relationshipDetailTarget?.characterAName || 'Character A'
        const nameB = relationshipDetailTarget?.characterBName || 'Character B'
        Modal.confirm({
          title: 'Keep directions in sync?',
          content: `${nameB} -> ${nameA} will be set to match ${nameA} -> ${nameB} (${relationshipBulkFormalityAtoB}).`,
          okText: 'Sync directions',
          cancelText: 'Keep separate',
          onOk: () => {
            setRelationshipBulkLinkDirections(true)
            setRelationshipBulkFormalityBtoA(relationshipBulkFormalityAtoB)
          },
        })
        return
      }
      setRelationshipBulkLinkDirections(true)
      setRelationshipBulkFormalityBtoA(relationshipBulkFormalityAtoB)
    },
    [
      relationshipBulkFormalityAtoB,
      relationshipBulkFormalityBtoA,
      relationshipDetailTarget?.characterAName,
      relationshipDetailTarget?.characterBName,
    ]
  )

  const clearRelationshipDetail = React.useCallback(() => {
    relationshipDetailRequestRef.current += 1
    relationshipAutoSelectAllRef.current = false
    selectedRelationshipPairKeyRef.current = null
    setSelectedRelationshipPairKey(null)
    setRelationshipDetail(null)
    setRelationshipDetailError(null)
    setRelationshipSelectedChapters([])
    setRelationshipSectionExpanded({
      needsReview: true,
      suggested: true,
      overrides: true,
      matchesDefault: false,
    })
    setRelationshipUndoStack([])
  }, [])

  const toggleRelationshipChapterSelection = React.useCallback((chapterGuid: string) => {
    setRelationshipSelectedChapters((prev) => {
      if (prev.includes(chapterGuid)) {
        return prev.filter((entry) => entry !== chapterGuid)
      }
      return [...prev, chapterGuid]
    })
  }, [])

  const toggleAllRelationshipChapters = React.useCallback(
    (checked: boolean) => {
      if (checked) {
        setRelationshipSelectedChapters(relationshipSelectableChapters)
      } else {
        setRelationshipSelectedChapters([])
      }
    },
    [relationshipSelectableChapters]
  )

  const toggleRelationshipSection = React.useCallback((section: RelationshipChapterGroupKey) => {
    setRelationshipSectionExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }, [])

  React.useEffect(() => {
    if (!analysisReady) {
      return
    }
    if (visibleRelationships.length === 0) {
      if (relationshipDetailTarget) {
        clearRelationshipDetail()
      }
      return
    }
    const hasSelected = selectedRelationshipPairKey
      ? visibleRelationships.some(
          (relationship) => getRelationshipPairKey(relationship) === selectedRelationshipPairKey
        )
      : false
    if (!hasSelected) {
      openRelationshipDetail(visibleRelationships[0])
    }
  }, [
    analysisReady,
    clearRelationshipDetail,
    openRelationshipDetail,
    relationshipDetailTarget,
    selectedRelationshipPairKey,
    visibleRelationships,
  ])

  React.useEffect(() => {
    if (!relationshipDetailTarget || !relationshipDetail) {
      return
    }
    const currentRevision = registryContractRevision
    const detailRevision = normalizeContractString(relationshipDetail.registryRevision)
    if (!currentRevision || detailRevision === currentRevision) {
      return
    }
    setRelationshipDetail(null)
    setRelationshipDetailError(null)
    setRelationshipSelectedChapters([])
    relationshipAutoSelectAllRef.current = true
    void fetchRelationshipDetail(relationshipDetailTarget)
  }, [
    fetchRelationshipDetail,
    registryContractRevision,
    relationshipDetail,
    relationshipDetailTarget,
  ])

  React.useEffect(() => {
    if (!analysisReady) {
      return
    }
    if (visibleCharacters.length === 0) {
      if (characterDetailId) {
        clearCharacterDetail()
      }
      return
    }
    const hasSelected = characterDetailId
      ? visibleCharacters.some((character) => character.id === characterDetailId)
      : false
    if (!hasSelected) {
      setCharacterDetailId(visibleCharacters[0].id)
    }
  }, [analysisReady, characterDetailId, clearCharacterDetail, visibleCharacters])

  const openAnalysisModal = React.useCallback(() => {
    onOpenAnalysis()
  }, [onOpenAnalysis])

  const closeAnalysisModal = React.useCallback(() => {
    onCloseAnalysis?.()
  }, [onCloseAnalysis])

  const openMatrixEditorModal = React.useCallback(
    (chapterGuid?: string) => {
      onOpenMatrixEditor(chapterGuid)
    },
    [onOpenMatrixEditor]
  )

  const closeMatrixEditorModal = React.useCallback(() => {
    onCloseMatrixEditor?.()
  }, [onCloseMatrixEditor])

  const scrollCastRegistryIntoView = React.useCallback(() => {
    window.requestAnimationFrame(() => {
      charactersPanelHeaderRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    })
  }, [])

  const focusDuplicateReview = React.useCallback(() => {
    const duplicateId = Array.from(aliasCollisionSummary.characterIds)[0]
    clearCharacterFilters()
    if (duplicateId) {
      setCharacterDetailId(duplicateId)
    }
    openAliasModal()
    scrollCastRegistryIntoView()
  }, [aliasCollisionSummary.characterIds, clearCharacterFilters, openAliasModal, scrollCastRegistryIntoView])

  const focusGenderReview = React.useCallback(() => {
    const firstConflict = characterList.find((character) => character.hasGenderConflict)
    setCharacterSearch('')
    setGenderFilter('all')
    setShowConflictsOnly(true)
    if (firstConflict) {
      setCharacterDetailId(firstConflict.id)
    }
    scrollCastRegistryIntoView()
  }, [characterList, scrollCastRegistryIntoView])

  const focusRelationshipReview = React.useCallback(() => {
    const firstNeedsReview = relationshipList.find(
      (relationship) => getRelationshipStatusKey(relationship) === 'needsReview'
    )
    setRelationshipSearch('')
    setRelationshipStatusFilter('all')
    setRelationshipFormalityFilter('all')
    setRelationshipChapterFilter('all')
    if (firstNeedsReview) {
      openRelationshipDetail(firstNeedsReview)
    }
    window.requestAnimationFrame(() => {
      relationshipsPanelHeaderRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    })
  }, [openRelationshipDetail, relationshipList])

  const getWorkflowStatusTone = React.useCallback((status?: StepStatus): CharacterReviewStatusTone => {
    if (status === 'finish') {
      return 'ready'
    }
    if (status === 'process') {
      return 'attention'
    }
    return 'needed'
  }, [])

  const workflowActionItems = React.useMemo<CharacterWorkflowActionItem[]>(
    () =>
      workflowStepItems.map((item) => {
        const title = String(item.title || '')
        let onAction: (() => void) | undefined
        let chipLabel = title
        if (title === 'Run analysis') {
          onAction = openAnalysisModal
          chipLabel = 'Analysis'
        } else if (title === 'Resolve duplicates') {
          onAction = focusDuplicateReview
          chipLabel = 'Aliases'
        } else if (title === 'Review registry') {
          onAction = focusGenderReview
          chipLabel = 'Registry'
        } else if (title === 'Review relationships') {
          onAction = focusRelationshipReview
          chipLabel = 'Relationships'
        } else if (title === 'Ready for translation') {
          chipLabel = 'Translation'
        }
        const statusTone = getWorkflowStatusTone(item.status)
        const chipStatusLabel =
          statusTone === 'ready'
            ? 'Ready'
            : statusTone === 'attention'
              ? 'Attention'
              : statusTone === 'blocked'
                ? 'Blocked'
                : 'Needed'
        return {
          title: item.title,
          description: item.description,
          chipLabel,
          chipStatusLabel,
          status: item.status,
          statusTone,
          onAction,
          disabled: title === 'Ready for translation' || !onAction,
        }
      }),
    [
      focusDuplicateReview,
      focusGenderReview,
      focusRelationshipReview,
      getWorkflowStatusTone,
      openAnalysisModal,
      workflowStepItems,
    ]
  )

  const handleBulkFormalityUpdate = async (
    options?: {
      overrides?: RelationshipFormalityOverride[]
      skipUndo?: boolean
      formalityAtoB?: RelationshipFormalityValue
      formalityBtoA?: RelationshipFormalityValue
      suppressRegistryReload?: boolean
    }
  ) => {
    const target = relationshipDetailTarget
    const targetPairKey = getRelationshipPairKey(target)
    if (
      !target ||
      !targetPairKey ||
      !bookId ||
      !hasAccess ||
      !relationshipDetailIsCoherent ||
      registryStale ||
      selectedRelationshipPairKeyRef.current !== targetPairKey
    ) {
      message.info('Relationship data changed. Select the relationship again before applying changes.')
      return
    }
    const targetRevision = currentRegistryRevision
    const formalityAtoB = options?.formalityAtoB ?? relationshipBulkFormalityAtoB
    const formalityBtoA = options?.formalityBtoA ?? relationshipBulkFormalityBtoA
    let overrides = options?.overrides

    const buildOverride = (chapterGuid: string): RelationshipFormalityOverride => {
      const override: RelationshipFormalityOverride = { chapterGuid }
      override.formalityAtoB = formalityAtoB
      override.formalityBtoA = formalityBtoA
      if (formalityAtoB === formalityBtoA && formalityAtoB) {
        override.formality = formalityAtoB
      }
      return override
    }

    if (!overrides) {
      const selectedOverrides = relationshipSelectedChapters
        .map((chapterGuid) => chapterGuid.trim())
        .filter((chapterGuid) => relationshipSelectableChapterSet.has(chapterGuid))
        .map((chapterGuid) => buildOverride(chapterGuid))
      if (selectedOverrides.length === 0) {
        message.info('Select at least one chapter.')
        return
      }
      overrides = selectedOverrides
    }

    if (!options?.skipUndo && relationshipDetailStats?.overrides?.length) {
      const selectionSet = new Set(overrides.map((override) => override.chapterGuid))
      const undoOverrides = relationshipDetailStats.overrides.filter((override) =>
        selectionSet.has(override.chapterGuid)
      )
      if (undoOverrides.length > 0) {
        setRelationshipUndoStack((prev) => [...prev, undoOverrides])
      }
    }
    setRelationshipBulkUpdating(true)
    try {
      const { data } = await axios.post<{
        updatedChapters?: number
        updatedRows?: number
        skippedChapters?: number
        pairKey?: string
        registryRevision?: string
      }>(`${apiBase}/uploadedbooks/book/${bookId}/relationships/formality`, {
        userId,
        characterAId: target.characterAId,
        characterBId: target.characterBId,
        characterAName: target.characterAName,
        characterBName: target.characterBName,
        pairKey: targetPairKey,
        registryRevision: targetRevision || undefined,
        formalityAtoB,
        formalityBtoA,
        direction: 'both',
        applyMode: 'override',
        overrides,
      }, { headers: await getAuthHeaders() })
      if (
        (data?.pairKey && data.pairKey !== targetPairKey) ||
        (targetRevision && data?.registryRevision !== targetRevision)
      ) {
        throw new Error('Relationship data changed while Formal / Informal Speech was updating.')
      }
      const updatedChapters = data?.updatedChapters ?? 0
      const updatedRows = data?.updatedRows ?? 0
      const skippedChapters = data?.skippedChapters ?? 0
      const baseMessage = `Updated ${updatedRows} row${updatedRows === 1 ? '' : 's'} across ${updatedChapters} chapter${
        updatedChapters === 1 ? '' : 's'
      }.`
      if (skippedChapters > 0) {
        message.warning(
          `${baseMessage} Skipped ${skippedChapters} chapter${skippedChapters === 1 ? '' : 's'} with invalid rows.`
        )
      } else {
        message.success(baseMessage)
      }
      const targetIsStillSelected = selectedRelationshipPairKeyRef.current === targetPairKey
      const updatedDetail = targetIsStillSelected
        ? await fetchRelationshipDetail(target)
        : null
      const updatedCounts = buildRelationshipCountsFromDetail(updatedDetail)
      if (updatedCounts) {
        const applyUpdate = (entry: RelationshipEntry): RelationshipEntry => ({
          ...entry,
          ...updatedCounts,
        })
        setRegistry((prev) => {
          if (!prev || !prev.relationships) {
            return prev
          }
          const relationships = prev.relationships.map((relationship) => {
            if (getRelationshipPairKey(relationship) === targetPairKey) {
              return applyUpdate(relationship)
            }
            return relationship
          })
          return { ...prev, relationships }
        })
      }
      if (!options?.suppressRegistryReload) {
        await fetchRegistry({ silent: true })
      }
    } catch (err: any) {
      const responseDetail = err?.response?.data?.detail
      const detail =
        (typeof responseDetail === 'string'
          ? responseDetail
          : responseDetail?.message || responseDetail?.error) ||
        err?.message ||
        'Unable to update Formal / Informal Speech.'
      if (err?.response?.status === 409) {
        clearRelationshipDetail()
        setRegistry(null)
        setCharacterRecords([])
        await fetchRegistry({ silent: true })
      }
      message.error(detail)
    } finally {
      setRelationshipBulkUpdating(false)
    }
  }

  const [approveChapterVariation, setApproveChapterVariation] = React.useState(false)
  const [relationshipDecisionContext, setRelationshipDecisionContext] = React.useState('')
  React.useEffect(() => {
    setApproveChapterVariation(false)
    setRelationshipDecisionContext('')
  }, [relationshipDetailTarget?.characterAId, relationshipDetailTarget?.characterBId])
  const handleRelationshipReviewStatus = React.useCallback(
    async (reviewed: boolean) => {
      const target = relationshipDetailTarget
      const targetPairKey = getRelationshipPairKey(target)
      if (
        !target ||
        !targetPairKey ||
        !bookId ||
        !hasAccess ||
        !relationshipDetailIsCoherent ||
        registryStale ||
        selectedRelationshipPairKeyRef.current !== targetPairKey
      ) {
        message.info('Relationship data changed. Select the relationship again before updating its review status.')
        return
      }
      const targetRevision = currentRegistryRevision
      setRelationshipReviewUpdating(true)
      try {
        const { data } = await axios.post<{
          reviewedAt?: string
          reviewedBy?: string
          approvedDecision?: RelationshipEntry['approvedDecision']
          pairKey?: string
          registryRevision?: string
        }>(`${apiBase}/uploadedbooks/book/${bookId}/relationships/review-status`, {
          userId,
          characterAId: target.characterAId,
          characterBId: target.characterBId,
          pairKey: targetPairKey,
          registryRevision: targetRevision || undefined,
          reviewed,
          formalityAtoB: reviewed ? relationshipBulkFormalityAtoB : undefined,
          formalityBtoA: reviewed ? relationshipBulkFormalityBtoA : undefined,
          chapterOverrides: reviewed && approveChapterVariation ? relationshipDetail?.chapters.map(chapter => ({
            chapterGuid: chapter.chapterGuid,
            formalityAtoB: getChapterDirectionSummary(chapter, 'AtoB'),
            formalityBtoA: getChapterDirectionSummary(chapter, 'BtoA'),
            context: relationshipDecisionContext,
          })) : [],
        }, { headers: await getAuthHeaders() })
        if (
          (data?.pairKey && data.pairKey !== targetPairKey) ||
          (targetRevision && data?.registryRevision !== targetRevision)
        ) {
          throw new Error('Relationship data changed while the review status was updating.')
        }
        const nextReviewedAt = reviewed
          ? data?.reviewedAt || new Date().toISOString()
          : undefined
        const nextReviewedBy = reviewed ? data?.reviewedBy || userId : undefined
        const applyReviewUpdate = (entry: RelationshipEntry): RelationshipEntry => ({
          ...entry,
          reviewStatus: reviewed ? 'reviewed' : undefined,
          approvedDecision: reviewed ? data?.approvedDecision : undefined,
          reviewedAt: reviewed ? nextReviewedAt : undefined,
          reviewedBy: reviewed ? nextReviewedBy : undefined,
        })
        setRegistry((prev) => {
          if (!prev || !prev.relationships) {
            return prev
          }
          const relationships = prev.relationships.map((relationship) => {
            if (getRelationshipPairKey(relationship) === targetPairKey) {
              return applyReviewUpdate(relationship)
            }
            return relationship
          })
          return { ...prev, relationships }
        })
        await fetchRegistry({ silent: true })
        message.success(reviewed ? 'Approved directional choices saved. Chapter observations remain available for comparison.' : 'Review status cleared.')
      } catch (err: any) {
        const responseDetail = err?.response?.data?.detail
        const detail =
          (typeof responseDetail === 'string'
            ? responseDetail
            : responseDetail?.message || responseDetail?.error) ||
          err?.message ||
          'Unable to update relationship review status.'
        if (err?.response?.status === 409) {
          clearRelationshipDetail()
          setRegistry(null)
          setCharacterRecords([])
          await fetchRegistry({ silent: true })
        }
        message.error(detail)
      } finally {
        setRelationshipReviewUpdating(false)
      }
    },
    [
      approveChapterVariation, relationshipDecisionContext, relationshipDetail,
      relationshipBulkFormalityAtoB, relationshipBulkFormalityBtoA,
      apiBase,
      bookId,
      currentRegistryRevision,
      clearRelationshipDetail,
      fetchRegistry,
      hasAccess,
      relationshipDetailIsCoherent,
      registryStale,
      relationshipDetailTarget,
      userId,
    ]
  )

  const confirmBulkFormalityUpdate = (
    options?: {
      overrides?: RelationshipFormalityOverride[]
      skipUndo?: boolean
      formalityAtoB?: RelationshipFormalityValue
      formalityBtoA?: RelationshipFormalityValue
      suppressRegistryReload?: boolean
    }
  ) => {
    if (!relationshipDetailTarget || !bookId || !hasAccess) {
      return
    }
    const formalityAtoB = options?.formalityAtoB ?? relationshipBulkFormalityAtoB
    const formalityBtoA = options?.formalityBtoA ?? relationshipBulkFormalityBtoA
    const chapterCount = options?.overrides?.length ?? selectedRelationshipChapterCount
    if (chapterCount <= 0) {
      message.info('Select at least one chapter.')
      return
    }
    Modal.confirm({
      title: 'Apply Formal / Informal Speech?',
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div>
            Update {formatPluralCount(chapterCount, 'chapter')} for{' '}
            <strong>{relationshipNameA} ↔ {relationshipNameB}</strong>.
          </div>
          <div>
            {relationshipNameA} → {relationshipNameB}: <strong>{formalityAtoB}</strong>
          </div>
          <div>
            {relationshipNameB} → {relationshipNameA}: <strong>{formalityBtoA}</strong>
          </div>
        </div>
      ),
      okText: 'Apply changes',
      cancelText: 'Cancel',
      onOk: () => handleBulkFormalityUpdate(options),
    })
  }

  const confirmRelationshipReviewStatus = (reviewed: boolean) => {
    if (reviewed) {
      void handleRelationshipReviewStatus(true)
      return
    }
    Modal.confirm({
      title: 'Clear relationship review?',
      content: 'This pair will return to the review queue if analysis still thinks it needs attention.',
      okText: 'Clear review',
      cancelText: 'Cancel',
      onOk: () => handleRelationshipReviewStatus(false),
    })
  }

  const handleUndoBulkFormality = async () => {
    if (relationshipUndoStack.length === 0) {
      return
    }
    const nextStack = [...relationshipUndoStack]
    const overrides = nextStack.pop()
    if (!overrides || overrides.length === 0) {
      setRelationshipUndoStack(nextStack)
      message.info('Nothing to undo.')
      return
    }
    setRelationshipUndoStack(nextStack)
    const fallbackFormalityAtoB =
      overrides[0]?.formalityAtoB || overrides[0]?.formality || relationshipBulkFormalityAtoB
    const fallbackFormalityBtoA =
      overrides[0]?.formalityBtoA || overrides[0]?.formality || relationshipBulkFormalityBtoA
    await handleBulkFormalityUpdate({
      overrides,
      skipUndo: true,
      formalityAtoB: fallbackFormalityAtoB,
      formalityBtoA: fallbackFormalityBtoA,
    })
  }

  const renderRelationshipChapterRow = (
    chapter: RelationshipDetailChapter,
    index: number,
    groupKey: RelationshipChapterGroupKey
  ) => {
    const chapterLabel = formatChapterRef(chapter)
    const summaryA = getChapterDirectionSummary(chapter, 'AtoB')
    const summaryB = getChapterDirectionSummary(chapter, 'BtoA')
    const isMixed = summaryA === 'Mixed' || summaryB === 'Mixed'
    const isUnknown = summaryA === 'Unknown' || summaryB === 'Unknown'
    const formalityValue = isMixed
      ? 'Mixed'
      : isUnknown
      ? 'Unknown'
      : summaryA === summaryB
      ? summaryA
      : 'Asymmetric'
    const tagColor = isMixed
      ? 'orange'
      : summaryA === summaryB && summaryA === 'Formal'
      ? 'blue'
      : summaryA === summaryB && summaryA === 'Informal'
      ? 'green'
      : undefined
    const chapterKey = chapter.chapterGuid || `${groupKey}-${chapterLabel}-${index}`
    const hasRows = Boolean(chapter.rows && chapter.rows.length > 0)
    const showSelection = Boolean(relationshipDetail)
    const chapterGuid = chapter.chapterGuid
    const isSelected = chapterGuid ? relationshipSelectedChapterSet.has(chapterGuid) : false
    const chapterFormalityA =
      summaryA === 'Formal' || summaryA === 'Informal' ? summaryA : undefined
    const chapterFormalityB =
      summaryB === 'Formal' || summaryB === 'Informal' ? summaryB : undefined
    const chapterFormalityPlaceholderA = chapterFormalityA ? undefined : summaryA
    const chapterFormalityPlaceholderB = chapterFormalityB ? undefined : summaryB
    const chapterFormalityDisabled =
      relationshipBulkControlsDisabled || !chapterGuid

    return (
      <div key={chapterKey} className="relationships-detail-chapter">
        <div className="relationships-detail-chapter-header">
          <div className="relationships-detail-chapter-info">
            <div className="relationships-detail-chapter-title">
              {showSelection && (
                <Checkbox
                  checked={isSelected}
                  disabled={
                    !chapterGuid ||
                    relationshipBulkUpdating ||
                    relationshipDetailLoading ||
                    analysisRunning
                  }
                  onChange={() => {
                    if (chapterGuid) {
                      toggleRelationshipChapterSelection(chapterGuid)
                    }
                  }}
                />
              )}
              <Text strong>{chapterLabel}</Text>
              <Tag color={tagColor}>{formalityValue}</Tag>
              {hasProvisionalFormality(chapter) && <Tag color={hasOnlyOptionalDefaults(chapter) ? 'blue' : 'gold'}>
                {hasOnlyOptionalDefaults(chapter) ? 'Provisional default — optional review' : 'Suggested — needs review'}
              </Tag>}
            </div>
            {hasRows && relationshipDetailTarget && (
              <Text
                type="secondary"
                className="relationships-detail-chapter-pair"
                translate="no"
              >
                {relationshipNameA} ↔ {relationshipNameB}
              </Text>
            )}
          </div>
          <div className="relationships-detail-chapter-actions">
            <div className="relationships-detail-chapter-formality">
              <div className="relationships-detail-chapter-control">
                <Text
                  type="secondary"
                  className="relationships-detail-chapter-label"
                  translate="no"
                >
                  {chapterDirectionLabelAtoB}
                </Text>
                <Select
                  size="small"
                  options={RELATIONSHIP_FORMALITY_SELECT_OPTIONS}
                  value={chapterFormalityA}
                  placeholder={chapterFormalityPlaceholderA}
                  onChange={(value: RelationshipFormalityValue) => {
                    if (!chapterGuid || chapterFormalityA === value) {
                      return
                    }
                    confirmBulkFormalityUpdate({
                      overrides: [{ chapterGuid, formalityAtoB: value }],
                      formalityAtoB: value,
                      suppressRegistryReload: true,
                    })
                  }}
                  className="relationships-detail-chapter-select"
                  disabled={chapterFormalityDisabled}
                />
              </div>
              <div className="relationships-detail-chapter-control">
                <Text
                  type="secondary"
                  className="relationships-detail-chapter-label"
                  translate="no"
                >
                  {chapterDirectionLabelBtoA}
                </Text>
                <Select
                  size="small"
                  options={RELATIONSHIP_FORMALITY_SELECT_OPTIONS}
                  value={chapterFormalityB}
                  placeholder={chapterFormalityPlaceholderB}
                  onChange={(value: RelationshipFormalityValue) => {
                    if (!chapterGuid || chapterFormalityB === value) {
                      return
                    }
                    confirmBulkFormalityUpdate({
                      overrides: [{ chapterGuid, formalityBtoA: value }],
                      formalityBtoA: value,
                      suppressRegistryReload: true,
                    })
                  }}
                  className="relationships-detail-chapter-select"
                  disabled={chapterFormalityDisabled}
                />
              </div>
            </div>
          </div>
        </div>
        {hasRows && (
          <div className="relationships-detail-chapter-rows">
            {(chapter.rows ?? []).map((row, rowIndex) => (
              <div
                key={`${chapter.chapterGuid || chapter.chapterName}-${rowIndex}`}
                className="relationships-detail-row"
              >
                {(() => {
                  const rowA = normalizeRelationshipFormalityValue(
                    row.formalityAtoB ?? row.formality
                  )
                  const rowB = normalizeRelationshipFormalityValue(
                    row.formalityBtoA ?? row.formality
                  )
                  const labelA = rowA || (row.formalityAtoB ?? row.formality ?? 'Unknown')
                  const labelB = rowB || (row.formalityBtoA ?? row.formality ?? 'Unknown')
                  const rowNameA = getSafeRelationshipName(row.characterAName, relationshipNameA)
                  const rowNameB = getSafeRelationshipName(row.characterBName, relationshipNameB)
                  return (
                    <>
                      <div className="relationships-detail-row-pair">
                        <span className="relationships-detail-row-name">{rowNameA}</span>{' '}
                        <span className="relationships-detail-row-gender">
                          ({formatGenderLabel(row.characterAGender)})
                        </span>
                        <span className="relationships-detail-row-pair-separator">↔</span>
                        <span className="relationships-detail-row-name">{rowNameB}</span>{' '}
                        <span className="relationships-detail-row-gender">
                          ({formatGenderLabel(row.characterBGender)})
                        </span>
                      </div>
                      <div className="relationships-detail-row-speech">
                        <span className="relationships-detail-row-speech-label">Speech:</span>
                        {labelA === labelB ? (
                          <span className="relationships-detail-row-direction">
                            <span>Both directions</span>
                            <Tag color={getFormalityToneColor(labelA)}>{labelA}</Tag>
                          </span>
                        ) : (
                          <>
                            <span className="relationships-detail-row-direction">
                              <span>{rowNameA} to {rowNameB}</span>
                              <Tag color={getFormalityToneColor(labelA)}>{labelA}</Tag>
                            </span>
                            <span className="relationships-detail-row-direction-separator">•</span>
                            <span className="relationships-detail-row-direction">
                              <span>{rowNameB} to {rowNameA}</span>
                              <Tag color={getFormalityToneColor(labelB)}>{labelB}</Tag>
                            </span>
                          </>
                        )}
                      </div>
                      {([['aToBEvidence', rowNameA, rowNameB], ['bToAEvidence', rowNameB, rowNameA]] as const).map(([key, from, to]) => {
                        const evidence = row[key]
                        return evidence?.needsReview ? (
                          <Text key={key} type="secondary">
                            {from} → {to}: suggested {evidence.value}
                            {evidence.reason === 'limited_context'
                              ? evidence.addressRelevance === 'not_observed'
                                ? ' — provisional; no current address observed'
                                : ' — provisional default; optional review'
                              : ' — needs review (conflicting or unverified context)'}
                          </Text>
                        ) : null
                      })}
                    </>
                  )
                })()}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderRelationshipGroup = (
    title: string,
    groupKey: RelationshipChapterGroupKey,
    chapters: RelationshipDetailChapter[]
  ) => {
    if (chapters.length === 0) {
      return null
    }
    const isExpanded = relationshipSectionExpanded[groupKey]
    return (
      <div key={groupKey} className="relationships-detail-group">
        <button
          type="button"
          className="relationships-detail-group-header"
          onClick={() => toggleRelationshipSection(groupKey)}
        >
          <span className="relationships-detail-group-title">{title}</span>
          <span className="relationships-detail-group-count">
            {chapters.length}
          </span>
          <span className="relationships-detail-group-toggle">
            {isExpanded ? 'Hide' : 'Show'}
          </span>
        </button>
        {isExpanded && (
          <div className="relationships-detail-group-body">
            {chapters.map((chapter, index) =>
              renderRelationshipChapterRow(chapter, index, groupKey)
            )}
          </div>
        )}
      </div>
    )
  }

  const characterDetailInfo = React.useMemo(() => {
    if (!characterDetailTarget) {
      return null
    }
    const descriptionSummary = formatDescriptionSummary(
      characterDetailTarget.descriptionOverrides,
      characterDetailTarget.descriptionCounts
    )
    const hasAliasCollision = aliasCollisionSummary.characterIds.has(characterDetailTarget.id)
    const status = getCharacterStatus(characterDetailTarget, hasAliasCollision)
    const canonical = (characterDetailTarget.canonicalName || '').toLowerCase()
    const observedAliasCounts = characterDetailTarget.aliasCounts || {}
    const observedAliasKeys = new Set(
      Object.keys(observedAliasCounts)
        .map((alias) => (alias || '').trim().toLowerCase())
        .filter((alias) => Boolean(alias))
    )
    const mappedAliasesFromMappings = cleanedAliasMappings
      .filter((mapping) => (mapping.target || '').toLowerCase() === canonical)
      .map((mapping) => mapping.alias)
    const heuristicMappedAliases = normalizeAliasInputs(characterDetailTarget.aliases || []).filter(
      (alias) => !observedAliasKeys.has(alias.toLowerCase())
    )
    const aliasList = (
      mappedAliasesFromMappings.length > 0 ? mappedAliasesFromMappings : heuristicMappedAliases
    ).filter(
      (alias) => alias.toLowerCase() !== canonical
    )
    const mappedAliasKeySet = new Set(aliasList.map((alias) => alias.toLowerCase()))
    const observedAliasList = normalizeAliasInputs(Object.keys(observedAliasCounts)).filter(
      (alias) => alias.toLowerCase() !== canonical && !mappedAliasKeySet.has(alias.toLowerCase())
    )
    const genderCounts = Object.entries(characterDetailTarget.genderCounts || {})
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
    const conflictSamples = characterDetailTarget.hasGenderConflict
      ? [...(relationshipSamplesByCharacterId.get(characterDetailTarget.id) || [])].sort(
          (a, b) => (a.chapterNumber ?? 0) - (b.chapterNumber ?? 0)
        )
      : []
    return {
      descriptionSummary,
      status,
      aliasList,
      observedAliasList,
      hasAliasCollision,
      genderCounts,
      conflictSamples,
      coverage: formatCharacterCoverage(characterDetailTarget),
      genderLabel: formatGenderLabel(characterDetailTarget.gender),
      descriptionRefresh: normalizeDescriptionRefreshState(characterDetailTarget.descriptionRefresh),
      hasDescriptionOverrides: Boolean(
        characterDetailTarget.descriptionOverrides &&
          Object.keys(characterDetailTarget.descriptionOverrides).length > 0
      ),
    }
  }, [aliasCollisionSummary.characterIds, characterDetailTarget, cleanedAliasMappings, relationshipSamplesByCharacterId])

  const openEditorModal = (character: CharacterEntry) => {
    setEditorTarget(character)
    setEditorName(character.canonicalName || '')
    setEditorGender(normalizeGenderValue((character.gender || '').toLowerCase()))
    const initialDescriptionInputs = buildDescriptionInputsFromCharacter(character)
    setEditorDescriptions(initialDescriptionInputs)
    initialDescriptionOverridesRef.current = buildDescriptionOverridesFromCharacterOverrides(character)
    descriptionTouchedRef.current = new Set()
    editorAliasTouchedRef.current = false
    setEditorAliases([])
    setIsEditorModalVisible(true)
  }

  const closeEditorModal = () => {
    setIsEditorModalVisible(false)
    setEditorTarget(null)
    setEditorName('')
    setEditorDescriptions({})
    setEditorAliases([])
    editorAliasTouchedRef.current = false
    descriptionTouchedRef.current = new Set()
  }

  const handleDescriptionInputChange = (key: string, value: string) => {
    setEditorDescriptions((prev) => ({ ...prev, [key]: value }))
    descriptionTouchedRef.current.add(key)
  }

  const normalizedEditorName = editorName.trim()
  const currentEditorGender = editorTarget
    ? normalizeGenderValue((editorTarget.gender || '').toLowerCase())
    : 'unknown'
  const nameChanged = Boolean(editorTarget) && normalizedEditorName !== (editorTarget?.canonicalName || '')
  const genderChanged = Boolean(editorTarget) && editorGender !== currentEditorGender
  const normalizedDescriptionOverrides = React.useMemo(
    () =>
      buildDescriptionOverridePayload(
        editorDescriptions,
        descriptionTouchedRef.current,
        initialDescriptionOverridesRef.current
      ),
    [editorDescriptions]
  )
  const descriptionChanged = React.useMemo(
    () => Object.keys(normalizedDescriptionOverrides).length > 0,
    [normalizedDescriptionOverrides]
  )
  const normalizedEditorAliases = React.useMemo(
    () => normalizeAliasInputs(editorAliases),
    [editorAliases]
  )
  const initialEditorAliases = React.useMemo(
    () => normalizeAliasInputs(editorMappedAliases),
    [editorMappedAliases]
  )
  const aliasesChanged = React.useMemo(() => {
    if (!editorTarget) {
      return false
    }
    if (normalizedEditorAliases.length !== initialEditorAliases.length) {
      return true
    }
    const initialKeys = new Set(initialEditorAliases.map((alias) => alias.toLowerCase()))
    return normalizedEditorAliases.some((alias) => !initialKeys.has(alias.toLowerCase()))
  }, [editorTarget, initialEditorAliases, normalizedEditorAliases])
  const handleEditorAliasesChange = (values: string[]) => {
    editorAliasTouchedRef.current = true
    const normalized = normalizeAliasInputs(values)
    const canonicalKey = (normalizedEditorName || editorTarget?.canonicalName || '').toLowerCase()
    const filtered = canonicalKey
      ? normalized.filter((alias) => alias.toLowerCase() !== canonicalKey)
      : normalized
    setEditorAliases(filtered)
  }
  const canSaveCharacter =
    Boolean(editorTarget && normalizedEditorName) &&
    (nameChanged || genderChanged || descriptionChanged || aliasesChanged) &&
    (editorGender !== 'unknown' || (!nameChanged && !genderChanged))

  const handleSaveCharacter = async () => {
    if (!editorTarget) return
    if (!normalizedEditorName) {
      message.error('Enter a character name.')
      return
    }
    if ((nameChanged || genderChanged) && editorGender === 'unknown') {
      message.error('Select a Grammar Gender (for translation) form before saving.')
      return
    }
    if (!nameChanged && !genderChanged && !descriptionChanged && !aliasesChanged) {
      message.info('No changes to save.')
      return
    }
    const initialAliasKeys = new Set(initialEditorAliases.map((alias) => alias.toLowerCase()))
    const desiredAliasKeys = new Set(normalizedEditorAliases.map((alias) => alias.toLowerCase()))
    const aliasesToAdd = normalizedEditorAliases.filter(
      (alias) => !initialAliasKeys.has(alias.toLowerCase())
    )
    const aliasesToRemove = initialEditorAliases.filter(
      (alias) => !desiredAliasKeys.has(alias.toLowerCase())
    )
    setEditorSaving(true)
    try {
      if (nameChanged || genderChanged) {
        const aliasNames = Array.from(new Set(editorTarget.aliases || [])).filter((alias) =>
          isValidAliasValue(alias)
        )
        const { data } = await axios.post<{
          updatedChapters?: number
          updatedRows?: number
          updatedOccurrences?: number
          updatedNames?: number
        }>(
          `${apiBase}/uploadedbooks/book/${bookId}/characters/gender`,
          {
            userId,
            targetName: editorTarget.canonicalName,
            newName: normalizedEditorName,
            gender: editorGender,
            aliasNames,
          }
        )
        const updatedRows = data?.updatedRows ?? 0
        const updatedChapters = data?.updatedChapters ?? 0
        const updatedNames = data?.updatedNames ?? 0
        const updatedOccurrences = data?.updatedOccurrences ?? 0
        if (updatedRows > 0) {
          const parts = []
          if (updatedNames > 0) {
            parts.push(`${updatedNames} name${updatedNames === 1 ? '' : 's'}`)
          }
          if (updatedOccurrences > 0) {
            parts.push(`${updatedOccurrences} gender${updatedOccurrences === 1 ? '' : 's'}`)
          }
          const detail = parts.length > 0 ? ` (${parts.join(', ')})` : ''
          message.success(`Updated ${updatedRows} rows across ${updatedChapters} chapters${detail}.`)
        } else {
          message.info('No relationship rows needed updates.')
        }
      }
      if (descriptionChanged) {
        await axios.post(
          `${apiBase}/uploadedbooks/book/${bookId}/characters/description`,
          {
            userId,
            targetName: normalizedEditorName || editorTarget.canonicalName,
            descriptionOverrides: normalizedDescriptionOverrides,
          }
        )
        message.success('Character description updated.')
      }
      if (aliasesToAdd.length > 0) {
        await axios.post(
          `${apiBase}/uploadedbooks/book/${bookId}/characters/aliases`,
          {
            userId,
            targetName: normalizedEditorName || editorTarget.canonicalName,
            aliasNames: aliasesToAdd,
          }
        )
      }
      if (aliasesToRemove.length > 0) {
        await axios.delete(
          `${apiBase}/uploadedbooks/book/${bookId}/characters/aliases`,
          { data: { userId, aliasNames: aliasesToRemove } }
        )
      }
      if (aliasesToAdd.length > 0 || aliasesToRemove.length > 0) {
        await fetchAliasMappings()
        message.success('Alias mappings updated.')
      }
      await fetchRegistry({ silent: true })
      closeEditorModal()
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Unable to update character.'
      message.error(detail)
    } finally {
      setEditorSaving(false)
    }
  }

  const resolveCharacterGenderConflict = React.useCallback(
    (character: CharacterEntry, gender: 'male' | 'female') => {
      const characterName = getSafeCharacterName(character.canonicalName, 'this character')
      const genderLabel = formatGenderLabel(gender)
      Modal.confirm({
        title: `Resolve as ${genderLabel}?`,
        content: `Apply ${genderLabel} to all ${characterName} relationship rows across chapters?`,
        okText: `Apply ${genderLabel}`,
        cancelText: 'Cancel',
        onOk: async () => {
          setConflictResolvingGender(gender)
          try {
            const aliasNames = Array.from(
              new Set([...(character.aliases || []), ...Object.keys(character.aliasCounts || {})])
            ).filter((alias) => isValidAliasValue(alias))
            const { data } = await axios.post<{
              updatedChapters?: number
              updatedRows?: number
              updatedOccurrences?: number
              updatedNames?: number
            }>(`${apiBase}/uploadedbooks/book/${bookId}/characters/gender`, {
              userId,
              targetName: character.canonicalName,
              gender,
              aliasNames,
            })
            await fetchRegistry({ silent: true })
            const updatedRows = data?.updatedRows ?? 0
            const updatedChapters = data?.updatedChapters ?? 0
            const updatedOccurrences = data?.updatedOccurrences ?? 0
            const updatedNames = data?.updatedNames ?? 0
            if (updatedRows > 0) {
              const details = []
              if (updatedOccurrences > 0) {
                details.push(`${updatedOccurrences} gender tag${updatedOccurrences === 1 ? '' : 's'}`)
              }
              if (updatedNames > 0) {
                details.push(`${updatedNames} name sync${updatedNames === 1 ? '' : 's'}`)
              }
              const detailSuffix = details.length > 0 ? ` (${details.join(', ')})` : ''
              message.success(
                `Applied ${genderLabel} to ${updatedRows} row${updatedRows === 1 ? '' : 's'} across ${updatedChapters} chapter${updatedChapters === 1 ? '' : 's'}${detailSuffix}.`
              )
            } else {
              message.info(`No relationship rows needed updates for ${characterName}.`)
            }
          } catch (err: any) {
            const detail =
              err?.response?.data?.detail || err?.message || 'Unable to resolve Grammar Gender conflict.'
            message.error(detail)
          } finally {
            setConflictResolvingGender(null)
          }
        },
      })
    },
    [apiBase, bookId, fetchRegistry, userId]
  )

  const queueDescriptionRefresh = React.useCallback(
    (character: CharacterEntry) => {
      const refreshState = normalizeDescriptionRefreshState(character.descriptionRefresh)
      const chapterCount = Math.max(
        0,
        Number(refreshState.chapterCount || character.chapterCount || 0)
      )
      const hasOverrides = Boolean(
        character.descriptionOverrides && Object.keys(character.descriptionOverrides).length > 0
      )
      const characterName = getSafeCharacterName(character.canonicalName, 'this character')
      Modal.confirm({
        title: `Refresh ${characterName}'s description?`,
        content: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div>
              This will analyze {chapterCount} chapter{chapterCount === 1 ? '' : 's'} where this
              character appears.
            </div>
            <div>This refresh is free, but it can only be completed successfully once per character.</div>
            <div>Manual description overrides will stay in effect and may hide the refreshed inferred summary.</div>
            {hasOverrides ? (
              <div>Manual overrides are currently active for this character.</div>
            ) : null}
          </div>
        ),
        okText: 'Refresh description',
        cancelText: 'Cancel',
        onOk: async () => {
          setDescriptionRefreshSubmittingId(character.id)
          try {
            const { data } = await axios.post(
              `${apiBase}/uploadedbooks/book/${bookId}/characters/${character.id}/description/refresh`,
              { userId }
            )
            await fetchRegistry({ silent: true })
            message.success(
              data?.message ||
                'Character description refresh queued. Manual overrides remain in effect until cleared.'
            )
          } catch (err: any) {
            const detail =
              err?.response?.data?.detail ||
              err?.message ||
              'Unable to queue character description refresh.'
            message.error(
              typeof detail === 'string'
                ? detail
                : detail?.message || 'Unable to queue character description refresh.'
            )
          } finally {
            setDescriptionRefreshSubmittingId(null)
          }
        },
      })
    },
    [apiBase, bookId, fetchRegistry, userId]
  )

  const getTourRelationshipTarget = React.useCallback(() => {
    return visibleRelationships[0] ?? relationshipList[0] ?? null
  }, [relationshipList, visibleRelationships])

  const openTourRelationshipDetail = React.useCallback(() => {
    const target = getTourRelationshipTarget()
    if (target) {
      openRelationshipDetail(target)
    }
  }, [getTourRelationshipTarget, openRelationshipDetail])

  const controlsTourStepConfigs = React.useMemo<TourStepConfig[]>(
    () => [
      {
        id: 'registry',
        title: 'Character list',
        description: renderTourPanel(
          <>
            The Character List tracks every character, their aliases, and relationship signals across
            the entire book. Translators use it to keep pronouns and Formal / Informal Speech
            consistent in every language.
          </>
        ),
        targetIds: ['characters-panel', 'characters-analysis-overview'],
        target: () => resolveTourTarget(['characters-panel'], ['characters-analysis-overview']),
        placement: 'bottom',
      },
      {
        id: 'run-analysis',
        title: 'Analysis readiness',
        description: renderTourPanel(
          <>
            This chip shows whether the book-wide Character List scan is ready, missing, or needs
            attention. Select it to open the analysis modal.
          </>
        ),
        targetIds: ['characters-run-analysis-readiness'],
        target: () => resolveTourTarget(['characters-run-analysis-readiness'], ['characters-workflow']),
        placement: 'bottom',
      },
      {
        id: 'analysis-modal',
        title: 'Analysis modal',
        description: renderTourPanel(
          <>
            Review chapter coverage and what will be analyzed before starting the run. This is where
            you decide whether to rerun already-analyzed chapters.
          </>
        ),
        targetIds: ['analysis-modal-about'],
        waitForTargetIds: ['analysis-modal-about'],
        modalKey: 'analysis',
        onEnter: openAnalysisModal,
        onExit: closeAnalysisModal,
        target: () => resolveTourTarget(['analysis-modal-about']),
        placement: 'right',
      },
      {
        id: 'analysis-free-run',
        title: 'Free run and processing',
        description: renderTourPanel(
          <>
            The first full-book analysis is free. Reruns only charge credits for the chapters that
            get reprocessed. After launch, processing returns you here; results appear in the
            Character List and Relationships panels when the run finishes.
          </>
        ),
        targetIds: ['analysis-modal-cost'],
        waitForTargetIds: ['analysis-modal-cost'],
        modalKey: 'analysis',
        onExit: closeAnalysisModal,
        target: () => resolveTourTarget(['analysis-modal-cost'], ['analysis-modal-about']),
        placement: 'right',
      },
    ],
    [closeAnalysisModal, openAnalysisModal, renderTourPanel, resolveTourTarget]
  )

  const analysisTourStepConfigs = React.useMemo<TourStepConfig[]>(
    () => {
      const hasTourRelationship = Boolean(getTourRelationshipTarget());
      const steps: TourStepConfig[] = [
        {
        id: 'characters-card',
        title: 'Character cards',
        description: renderTourPanel(
          <>
            Each card lists the canonical name, aliases, descriptions, and chapter history. Conflicts
            are flagged so you can fix mismatched Grammar Gender (for translation) quickly.
          </>
        ),
        targetIds: ['characters-panel'],
        target: () => resolveTourTarget(['characters-panel']),
        placement: 'right',
      },
        {
        id: 'relationships-card',
        title: 'Relationship cards',
        description: renderTourPanel(
          <>
            Relationship cards summarize Formal / Informal Speech usage across chapters so you can
            spot shifts at a glance.
          </>
        ),
        targetIds: ['relationships-panel'],
        target: () => resolveTourTarget(['relationships-panel']),
        placement: 'left',
      },
        {
        id: 'alias-button',
        title: 'Alias mapping',
        description: renderTourPanel(
          <>
            Merge spelling variants into a canonical name without rewriting chapter relationship rows.
          </>
        ),
        targetIds: ['characters-alias-button'],
        target: () => resolveTourTarget(['characters-alias-button']),
        placement: 'bottom',
      },
        {
        id: 'alias-modal',
        title: 'Alias mapping modal',
        description: renderTourPanel(
          <>
            Choose a target name and aliases to merge. This updates the Character List only and keeps
            chapter rows intact.
          </>
        ),
        targetIds: ['alias-mapping-add-panel'],
        waitForTargetIds: ['alias-mapping-add-panel'],
        modalKey: 'alias',
        onEnter: openAliasModal,
        onExit: closeAliasModal,
        target: () => resolveTourTarget(['alias-mapping-add-panel'], ['alias-mapping-modal']),
        placement: 'right',
      },
        {
        id: 'matrix-button',
        title: 'Advanced Relationship Rules',
        description: renderTourPanel(
          <>
            Open the advanced relationship rules to adjust relationships one chapter at a time when you need a
            targeted tweak.
          </>
        ),
        targetIds: ['characters-matrix-button'],
        target: () => resolveTourTarget(['characters-matrix-button']),
        placement: 'bottom',
      },
        {
        id: 'matrix-modal',
        title: 'Advanced Relationship Rules editor',
        description: renderTourPanel(
          <>
            Edit per-chapter relationship rows, mark non-story chapters, and save data that translators
            depend on for Formal / Informal Speech and pronoun accuracy.
          </>
        ),
        targetIds: ['relationship-matrix-chapter-section'],
        waitForTargetIds: ['relationship-matrix-chapter-section'],
        modalKey: 'matrix',
        onEnter: () => openMatrixEditorModal(),
        onExit: closeMatrixEditorModal,
        target: () => resolveTourTarget(['relationship-matrix-chapter-section'], ['relationship-matrix-modal']),
        placement: 'left',
      },
        {
        id: 'relationship-button',
        title: 'Edit relationship',
        description: renderTourPanel(
          <>
            Jump straight into a specific relationship’s chapter list to review usage quickly.
          </>
        ),
        targetIds: ['relationships-edit-button'],
        target: () => resolveTourTarget(['relationships-edit-button'], ['relationships-panel']),
        placement: 'left',
        },
        {
        id: 'relationship-modal',
        title: 'Relationship details',
        description: renderTourPanel(
          <>
            Review every chapter where the pair appears and bulk-set Formal / Informal Speech in one
            step.
          </>
        ),
        targetIds: ['relationships-detail-panel'],
        waitForTargetIds: ['relationships-detail-panel'],
        modalKey: 'relationship',
        onEnter: openTourRelationshipDetail,
        onExit: clearRelationshipDetail,
        target: () => resolveTourTarget(['relationships-detail-panel'], ['relationships-panel']),
        placement: 'right',
        },
      ];
      return steps.filter((step) => {
        if (!hasTourRelationship && (step.id === 'relationship-button' || step.id === 'relationship-modal')) {
          return false;
        }
        return true;
      });
    },
    [
      closeAliasModal,
      closeMatrixEditorModal,
      clearRelationshipDetail,
      getTourRelationshipTarget,
      openAliasModal,
      openMatrixEditorModal,
      openTourRelationshipDetail,
      renderTourPanel,
      resolveTourTarget,
    ]
  )

  const controlsTourSteps = React.useMemo<TourProps['steps']>(
    () =>
      controlsTourStepConfigs.map(
        ({ id, modalKey, onEnter, onExit, targetIds, waitForTargetIds, ...step }) => step
      ),
    [controlsTourStepConfigs]
  )

  const analysisTourSteps = React.useMemo<TourProps['steps']>(
    () =>
      analysisTourStepConfigs.map(
        ({ id, modalKey, onEnter, onExit, targetIds, waitForTargetIds, ...step }) => step
      ),
    [analysisTourStepConfigs]
  )

  const handleTourStepChange = React.useCallback(
    (
      nextStep: number,
      currentStep: number,
      stepConfigs: TourStepConfig[],
      setStep: React.Dispatch<React.SetStateAction<number>>,
      pendingStepRef: React.MutableRefObject<number | null>,
      waitTokenRef: React.MutableRefObject<number>
    ) => {
      if (nextStep === currentStep) return
      const currentConfig = stepConfigs[currentStep]
      const nextConfig = stepConfigs[nextStep]
      if (!nextConfig) return

      if (currentConfig?.modalKey && currentConfig.modalKey !== nextConfig?.modalKey) {
        currentConfig.onExit?.()
      }

      if (pendingStepRef.current === nextStep) {
        return
      }

      if (nextConfig.onEnter) {
        nextConfig.onEnter()
      }

      if (nextConfig.waitForTargetIds) {
        pendingStepRef.current = nextStep
        void waitForTourTarget(nextConfig.waitForTargetIds, waitTokenRef).then((ready) => {
          if (pendingStepRef.current !== nextStep) return
          pendingStepRef.current = null
          if (!ready) return
          setStep(nextStep)
        })
        return
      }

      pendingStepRef.current = null
      setStep(nextStep)
    },
    [waitForTourTarget]
  )

  const handleControlsTourChange = React.useCallback(
    (nextStep: number) => {
      handleTourStepChange(
        nextStep,
        controlsTourStep,
        controlsTourStepConfigs,
        setControlsTourStep,
        pendingControlsTourStepRef,
        controlsTourWaitTokenRef
      )
    },
    [controlsTourStep, controlsTourStepConfigs, handleTourStepChange]
  )

  const handleAnalysisTourChange = React.useCallback(
    (nextStep: number) => {
      handleTourStepChange(
        nextStep,
        analysisTourStep,
        analysisTourStepConfigs,
        setAnalysisTourStep,
        pendingAnalysisTourStepRef,
        analysisTourWaitTokenRef
      )
    },
    [analysisTourStep, analysisTourStepConfigs, handleTourStepChange]
  )

  const closeControlsTour = React.useCallback(
    (options?: { finished?: boolean }) => {
      if (process.env.NODE_ENV !== 'production') {
        console.info('[CharactersTab] close controls tour', {
          finished: options?.finished ?? false,
          queuedNext: manualTourQueueRef.current === 'analysis',
          hasAnalysisData,
        })
      }
      pendingControlsTourStepRef.current = null
      controlsTourWaitTokenRef.current += 1
      setControlsTourOpen(false)
      setControlsTourStep(0)
      closeAnalysisModal()
      void markTourSeen('charactersControlsTourSeen')

      if (manualTourQueueRef.current === 'analysis' && hasAnalysisData) {
        manualTourQueueRef.current = null
        window.setTimeout(() => {
          pendingAnalysisTourStepRef.current = null
          analysisTourWaitTokenRef.current += 1
          setAnalysisTourStep(0)
          setAnalysisTourOpen(true)
        }, 200)
        return
      }

      manualTourQueueRef.current = null
    },
    [closeAnalysisModal, hasAnalysisData, markTourSeen]
  )

  const closeAnalysisTour = React.useCallback(() => {
    pendingAnalysisTourStepRef.current = null
    analysisTourWaitTokenRef.current += 1
    setAnalysisTourOpen(false)
    setAnalysisTourStep(0)
    manualTourQueueRef.current = null
    closeAliasModal()
    closeMatrixEditorModal()
    clearRelationshipDetail()
    void markTourSeen('charactersAnalysisTourSeen')
  }, [
    closeAliasModal,
    closeMatrixEditorModal,
    clearRelationshipDetail,
    markTourSeen,
  ])

  const handleControlsTourClose = React.useCallback(() => {
    closeControlsTour()
  }, [closeControlsTour])

  const handleControlsTourFinish = React.useCallback(() => {
    closeControlsTour({ finished: true })
  }, [closeControlsTour])

  const handleAnalysisTourClose = React.useCallback(() => {
    closeAnalysisTour()
  }, [closeAnalysisTour])

  const handleAnalysisTourFinish = React.useCallback(() => {
    closeAnalysisTour()
  }, [closeAnalysisTour])

  const isManualTourReady = hasAccess && !isTabBusy
  const canStartTour = isManualTourReady && !controlsTourOpen && !analysisTourOpen

  const handleTakeTour = React.useCallback(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.info('[CharactersTab] take tour', {
        canStartTour,
        hasAccess,
        tourFlagsInitialized,
        isTabBusy,
        hasAnalysisData,
        registryLoaded: registry !== null,
      })
    }
    if (!canStartTour) return false
    manualTourQueueRef.current = 'analysis'
    pendingControlsTourStepRef.current = null
    controlsTourWaitTokenRef.current += 1
    setControlsTourStep(0)
    setControlsTourOpen(true)
    return true
  }, [
    canStartTour,
    hasAccess,
    hasAnalysisData,
    isTabBusy,
    registry,
    tourFlagsInitialized,
  ])

  React.useEffect(() => {
    if (startTourSignal == null) return
    if (lastExternalTourSignalRef.current === startTourSignal) return
    if (controlsTourOpen || analysisTourOpen) return
    if (handleTakeTour()) {
      lastExternalTourSignalRef.current = startTourSignal
    }
  }, [analysisTourOpen, controlsTourOpen, handleTakeTour, startTourSignal])

  const analysisGateState = React.useMemo(() => {
    if (analysisReady) {
      return null
    }
    if (analysisFailed) {
      return {
        tone: 'error' as const,
        title: 'Analysis failed',
        description: 'Run analysis again to rebuild the name registry.',
        showActions: true,
        statusLabel: 'Action needed',
        statusColor: 'red',
      }
    }
    if (analysisRunning || analysisLoading) {
      return {
        tone: 'info' as const,
        title: 'Analysis in progress',
        description: 'Names and relationships will appear once the run completes.',
        showActions: false,
        statusLabel: 'Running',
        statusColor: 'blue',
      }
    }
    return {
      tone: 'info' as const,
      title: 'Analysis required',
      description: 'Run analysis to build the name registry.',
      showActions: true,
      statusLabel: 'Required',
      statusColor: 'gold',
    }
  }, [analysisFailed, analysisLoading, analysisReady, analysisRunning])

  const analysisInfoCopy =
    'We scan each chapter to map names, aliases, and Formal / Informal Speech for translation.'
  const analysisChapterCountLabel =
    analysisSummary &&
    typeof analysisSummary.missingChapters === 'number' &&
    analysisSummary.missingChapters > 0
      ? `${formatPluralCount(analysisSummary.missingChapters, 'chapter')} need analysis.`
      : null
  const analysisCostLabel = React.useMemo(() => {
    if (!analysisSummary) {
      return null
    }
    if (analysisSummary.freeRunAvailable) {
      return 'First full-book run is free.'
    }
    if (typeof analysisSummary.creditCost === 'number' && analysisSummary.creditCost > 0) {
      return `Estimated rerun cost: ${analysisSummary.creditCost.toLocaleString()} credits.`
    }
    return null
  }, [analysisSummary])

  const openAnalysisInfo = React.useCallback(() => {
    Modal.info({
      className: 'dark-aware-info-modal',
      title: 'Name and relationship analysis',
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>{analysisInfoCopy}</div>
          <div>Use it to keep pronouns and Formal / Informal Speech consistent across translations.</div>
          {analysisCostLabel && <div>{analysisCostLabel}</div>}
        </div>
      ),
    })
  }, [analysisCostLabel, analysisInfoCopy])

  if (!hasAccess) {
    return (
      <Alert
        type="info"
        showIcon
        message="Sign in to manage character relationships."
        description="We need your user ID to load and edit this book’s character data."
      />
    )
  }

  const emptyDescription = analysisReady
    ? 'No character data yet'
    : analysisFailed
      ? 'Analysis failed'
      : analysisRunning || analysisLoading
        ? 'Analysis in progress'
        : 'Run analysis to view names'

  const mainContent = analysisReady && registry ? (
    <div className="characters-main-content" data-testid="tutorial:characters:main-content">
      <div className="characters-sections">
        <section className="characters-task-section book-detail-secondary-surface" data-testid="tutorial:characters:cast-registry">
          <div
            ref={charactersPanelHeaderRef}
            data-panel-key={CHARACTERS_PANEL_KEY}
            className={`characters-panel-header characters-section-heading${
              characterSummaryMetrics.needsReview > 0 ? ' has-needs-review' : ''
            }`}
          >
            <div className="characters-panel-heading">
              <Space>
                <TeamOutlined />
                <span>Cast registry</span>
              </Space>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Review canonical names, aliases, Grammar Gender, and coverage.
              </Typography.Text>
            </div>
            <div className="characters-panel-summary">
              <div className="characters-panel-summary-item">
                <Text type="secondary" className="characters-panel-summary-label">
                  Total
                </Text>
                <span className="characters-panel-summary-value">
                  {characterSummaryMetrics.total}
                </span>
              </div>
              <div
                className={`characters-panel-summary-item${
                  characterSummaryMetrics.needsReview > 0 ? ' is-alert' : ''
                }`}
              >
                <Text type="secondary" className="characters-panel-summary-label">
                  Needs review
                  {characterSummaryMetrics.needsReview > 0 && (
                    <WarningOutlined className="panel-needs-review-icon" />
                  )}
                </Text>
                <span className="characters-panel-summary-value">
                  {characterSummaryMetrics.needsReview}
                </span>
              </div>
            </div>
          </div>
          <div data-tour-id="characters-panel" data-testid="tutorial:characters:list-panel" ref={charactersPanelRef} className="characters-panel-body">
            <div className="characters-layout">
              <div className="characters-master">
                <div data-tour-id="characters-filters" data-testid="tutorial:characters:filters" className="characters-filter-bar">
                  <Input
                    placeholder="Search characters"
                    value={characterSearch}
                    onChange={(event) => setCharacterSearch(event.target.value)}
                    className="characters-filter-search"
                  />
                  <Popover
                    trigger="click"
                    placement="bottomLeft"
                    content={
                      <div className="filters-menu">
                        <div className="filters-menu-row">
                          <Text type="secondary" className="filters-menu-label">
                            Gender
                          </Text>
                          <Select
                            options={GENDER_FILTER_OPTIONS}
                            value={genderFilter}
                            onChange={(value: GenderFilterValue) => setGenderFilter(value)}
                            className="characters-filter-select"
                            getPopupContainer={filterMenuPopupContainer}
                          />
                        </div>
                        <div className="filters-menu-row">
                          <Text type="secondary" className="filters-menu-label">
                            Conflicts
                          </Text>
                  <Tooltip
                    title={
                      !hasConflicts
                        ? 'No Grammar Gender conflicts detected yet.'
                        : undefined
                    }
                  >
                            <span className="filters-menu-toggle">
                              <Checkbox
                                className="characters-filter-toggle"
                                checked={showConflictsOnly}
                                onChange={(event) => setShowConflictsOnly(event.target.checked)}
                                disabled={!hasConflicts}
                              >
                                Conflicts only
                              </Checkbox>
                            </span>
                          </Tooltip>
                        </div>
                        <div className="filters-menu-actions">
                          <Button
                            type="link"
                            className="characters-action-link"
                            onClick={clearCharacterFilters}
                            disabled={characterFilterChips.length === 0}
                          >
                            Clear all
                          </Button>
                        </div>
                      </div>
                    }
                  >
                    <Button icon={<FilterOutlined />} className="filters-menu-button">
                      Filters{characterFilterChips.length > 0 ? ` (${characterFilterChips.length})` : ''}
                    </Button>
                  </Popover>
                  <div className="characters-filter-sort">
                    <Text type="secondary" className="characters-filter-sort-label">
                      Sort
                    </Text>
                    <Select
                      options={CHARACTER_SORT_OPTIONS}
                      value={characterSort}
                      onChange={(value: CharacterSortValue) => setCharacterSort(value)}
                      className="characters-filter-sort-select"
                      getPopupContainer={filterMenuPopupContainer}
                    />
                  </div>
                </div>
                <div className="characters-list-shell">
                  {visibleCharacters.length > 0 ? (
                    <div className="characters-list">
                      {visibleCharacters.map((character) => {
                        const descriptionSummary = formatDescriptionSummary(
                          character.descriptionOverrides,
                          character.descriptionCounts
                        )
                        const status = getCharacterStatus(
                          character,
                          aliasCollisionSummary.characterIds.has(character.id)
                        )
                        const coverage = formatCharacterCoverage(character)
                        const genderLabel = formatGenderLabel(character.gender)
                        const isSelected = characterDetailTarget?.id === character.id
                        return (
                          <button
                            key={character.id}
                            type="button"
                            className={`character-row${isSelected ? ' is-selected' : ''}`}
                            onClick={() => openCharacterDetail(character)}
                          >
                            <div className="character-row-main">
                              <div className="character-row-name">
                                {getSafeCharacterName(character.canonicalName, 'Unnamed')}
                              </div>
                              <Tag color={status.color}>{status.label}</Tag>
                            </div>
                            <div className="character-row-meta">
                              <span className="character-row-gender">{genderLabel}</span>
                              <span className="character-row-coverage">{coverage}</span>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <Empty
                      className="characters-list-empty"
                      description={
                        showConflictsOnly && !hasConflicts
                          ? 'No Grammar Gender conflicts detected'
                          : hasActiveFilters
                          ? 'No matches'
                          : 'No character data yet'
                      }
                    />
                  )}
                </div>
              </div>
              <div className="characters-detail" data-testid="tutorial:characters:detail-panel">
                {characterDetailTarget && characterDetailInfo ? (
                  <div className="characters-detail-shell">
                    {(() => {
                      const refreshState = characterDetailInfo.descriptionRefresh
                      const refreshStatus = String(refreshState.status || '').trim().toLowerCase()
                      const refreshActive = refreshStatus === 'queued' || refreshStatus === 'running'
                      const refreshDisabledReason = !analysisReady
                        ? 'Run analysis first'
                        : refreshActive
                          ? 'Description refresh already running'
                          : Number(characterDetailTarget.chapterCount || 0) <= 0
                            ? 'No linked chapters to analyze'
                            : Number(refreshState.remainingCount || 0) <= 0
                              ? 'Free refresh already used'
                              : null
                      const refreshButton = (
                        <Button
                          onClick={() => queueDescriptionRefresh(characterDetailTarget)}
                          disabled={Boolean(refreshDisabledReason) || Boolean(descriptionRefreshSubmittingId)}
                          loading={
                            descriptionRefreshSubmittingId === characterDetailTarget.id ||
                            refreshActive
                          }
                        >
                          Refresh Description
                        </Button>
                      )
                      return (
                    <div className="characters-detail-header">
                      <div>
                        <Typography.Text strong className="character-detail-name">
                          {getSafeCharacterName(characterDetailTarget.canonicalName, 'Unnamed')}
                        </Typography.Text>
                        <div className="characters-detail-meta">
                          <Tag color={characterDetailInfo.status.color}>
                            {characterDetailInfo.status.label}
                          </Tag>
                          <Tag color={genderColorMap[characterDetailTarget.gender || 'unknown']}>
                            {characterDetailInfo.genderLabel}
                          </Tag>
                          {characterDetailTarget.hasGenderConflict && (
                            <Tag icon={<WarningOutlined />} color="warning">
                              Grammar Gender conflict
                            </Tag>
                          )}
                          {characterDetailInfo.hasAliasCollision && (
                            <Tag icon={<WarningOutlined />} color="warning">
                              Potential duplicate
                            </Tag>
                          )}
                          <span className="character-detail-pill">
                            {characterDetailInfo.coverage}
                          </span>
                        </div>
                      </div>
                      <div className="characters-detail-actions">
                        {refreshDisabledReason ? (
                          <Tooltip title={refreshDisabledReason}>
                            <span>{refreshButton}</span>
                          </Tooltip>
                        ) : (
                          refreshButton
                        )}
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => openEditorModal(characterDetailTarget)}
                        >
                          Edit character
                        </Button>
                      </div>
                    </div>
                      )
                    })()}
                    <div className="characters-detail-section">
                      <Text type="secondary" className="characters-detail-section-title">
                        Aliases
                      </Text>
                        {characterDetailInfo.aliasList.length > 0 ? (
                          <div className="characters-detail-aliases">
                            {characterDetailInfo.aliasList.map((alias) => (
                              <div key={alias} className="characters-detail-alias-row">
                                {renderAliasSplitChip(
                                  characterDetailTarget.canonicalName,
                                  alias,
                                  disableActions
                                )}
                              </div>
                            ))}
                          </div>
                      ) : (
                        <div className="characters-detail-empty">None</div>
                      )}
                      {characterDetailInfo.observedAliasList.length > 0 && (
                        <>
                          <Text
                            type="secondary"
                            style={{ fontSize: 12, display: 'block', marginTop: 8 }}
                          >
                            Observed in chapters
                          </Text>
                          <div className="characters-detail-aliases">
                            {characterDetailInfo.observedAliasList.map((alias) => (
                              <div key={`observed-${alias}`} className="characters-detail-alias-row">
                                {renderAliasSplitChip(
                                  characterDetailTarget.canonicalName,
                                  alias,
                                  disableActions
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="characters-detail-section">
                      <Text type="secondary" className="characters-detail-section-title">
                        Description
                      </Text>
                      {getDescriptionRefreshStatusLabel(characterDetailInfo.descriptionRefresh) ? (
                        <div className="character-description-empty" style={{ marginBottom: 8 }}>
                          {getDescriptionRefreshStatusLabel(characterDetailInfo.descriptionRefresh)}
                        </div>
                      ) : null}
                      {characterDetailInfo.descriptionRefresh?.status === 'failed' &&
                      characterDetailInfo.descriptionRefresh?.lastError ? (
                        <div className="character-description-empty" style={{ marginBottom: 8 }}>
                          {characterDetailInfo.descriptionRefresh.lastError}
                        </div>
                      ) : null}
                      {characterDetailInfo.descriptionSummary ? (
                        <div className="character-description">
                          {characterDetailInfo.descriptionSummary}
                        </div>
                      ) : (
                        <div className="character-description-empty">
                          No description captured yet.
                        </div>
                      )}
                      {characterDetailInfo.hasDescriptionOverrides ? (
                        <div className="character-description-empty" style={{ marginTop: 8 }}>
                          Manual description overrides are active. Refreshed inferred details update underneath them until the overrides are cleared.
                        </div>
                      ) : null}
                    </div>
                    <div className="characters-detail-section">
                      <Text type="secondary" className="characters-detail-section-title">
                        Coverage
                      </Text>
                      <div className="character-meta-row">
                        <div className="character-meta-item">
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            First seen
                          </Text>
                          <div className="character-meta-value">
                            {formatChapterRef(characterDetailTarget.firstSeen)}
                          </div>
                        </div>
                        <div className="character-meta-item">
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Last seen
                          </Text>
                          <div className="character-meta-value">
                            {formatChapterRef(characterDetailTarget.lastSeen)}
                          </div>
                        </div>
                        <div className="character-meta-item">
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Chapters
                          </Text>
                          <div className="character-meta-value">
                            {characterDetailTarget.chapterCount ?? '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                    {characterDetailTarget.hasGenderConflict && (
                      <div className="characters-detail-section">
                        <Text type="secondary" className="characters-detail-section-title">
                          Grammar Gender conflicts
                        </Text>
                        {characterDetailInfo.genderCounts.length > 0 ? (
                          <div className="characters-detail-aliases">
                            {characterDetailInfo.genderCounts.map(([gender, count]) => (
                              <Tag key={gender} color="default">
                                {formatGenderLabel(gender)}: {count}
                              </Tag>
                            ))}
                          </div>
                        ) : (
                          <div className="characters-detail-empty">
                            No Grammar Gender (for translation) counts captured yet.
                          </div>
                        )}
                        <div className="characters-detail-conflict-tip">
                          Conflicts happen when a character is tagged with multiple Grammar Gender
                          (for translation) values in relationship rows. Open Advanced Relationship Rules to
                          correct the mismatch.
                        </div>
                        <Space size={8} wrap style={{ marginTop: 8 }}>
                          <Button
                            size="small"
                            loading={conflictResolvingGender === 'male'}
                            disabled={Boolean(conflictResolvingGender)}
                            onClick={() =>
                              resolveCharacterGenderConflict(characterDetailTarget, 'male')
                            }
                          >
                            Resolve as Masculine
                          </Button>
                          <Button
                            size="small"
                            loading={conflictResolvingGender === 'female'}
                            disabled={Boolean(conflictResolvingGender)}
                            onClick={() =>
                              resolveCharacterGenderConflict(characterDetailTarget, 'female')
                            }
                          >
                            Resolve as Feminine
                          </Button>
                        </Space>
                        {characterDetailInfo.conflictSamples.length > 0 ? (
                          <div>
                            <Text type="secondary" className="characters-detail-section-title">
                              Likely chapters to review
                            </Text>
                            <div className="characters-detail-conflict-list">
                              {characterDetailInfo.conflictSamples.slice(0, 3).map((sample, index) => (
                                <div
                                  key={sample.chapterGuid || sample.chapterName || sample.chapterNumber || index}
                                  className="characters-detail-conflict-row"
                                >
                                  <span>{formatChapterRef(sample)}</span>
                                  <Button
                                    size="small"
                                    type="link"
                                    className="characters-action-link"
                                    onClick={() => openMatrixEditorModal(sample.chapterGuid)}
                                    disabled={!sample.chapterGuid}
                                  >
                                    Open advanced chapter rules
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="characters-detail-empty">
                            No chapter samples available yet. Open the Advanced Relationship Rules editor to
                            review recent chapters.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <Empty description="Select a character to see details" />
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="characters-task-section book-detail-secondary-surface" data-testid="tutorial:characters:relationships">
          <div
            ref={relationshipsPanelHeaderRef}
            data-panel-key={RELATIONSHIPS_PANEL_KEY}
            className={`relationships-panel-header characters-section-heading${
              relationshipSummaryMetrics.needsReview > 0 ? ' has-needs-review' : ''
            }`}
          >
            <div className="relationships-panel-heading">
              <Space>
                <BranchesOutlined />
                <span>Relationships</span>
              </Space>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Review Formal / Informal Speech defaults and chapter-level overrides.
                {relationshipSummaryMetrics.suggested > 0 &&
                  ` ${formatPluralCount(relationshipSummaryMetrics.suggested, 'pair')} use provisional defaults; review is optional, not author approval.`}
              </Typography.Text>
            </div>
            <div className="relationships-panel-summary">
              <div className="relationships-panel-summary-item">
                <Text type="secondary" className="relationships-panel-summary-label">
                  Total pairs
                </Text>
                <span className="relationships-panel-summary-value">
                  {relationshipSummaryMetrics.total}
                </span>
              </div>
              <div
                className={`relationships-panel-summary-item${
                  relationshipSummaryMetrics.needsReview > 0 ? ' is-alert' : ''
                }`}
              >
                <Text type="secondary" className="relationships-panel-summary-label">
                  Needs review
                  {relationshipSummaryMetrics.needsReview > 0 && (
                    <WarningOutlined className="panel-needs-review-icon" />
                  )}
                </Text>
                <span className="relationships-panel-summary-value">
                  {relationshipSummaryMetrics.needsReview}
                </span>
              </div>
            </div>
          </div>
          <div
            data-tour-id="relationships-panel"
            data-testid="tutorial:characters:relationships-panel"
            ref={relationshipsPanelRef}
            className="characters-panel-body"
          >
            {invalidRelationshipCount > 0 && (
              <Alert
                type="warning"
                showIcon
                message="Some relationships need to be refreshed"
                description={`${formatPluralCount(invalidRelationshipCount, 'relationship')} could not be matched safely and ${
                  invalidRelationshipCount === 1 ? 'was' : 'were'
                } hidden while the Character List rebuilds.`}
              />
            )}
            <div className="relationships-layout">
              <div className="relationships-master">
                <div
                  data-tour-id="relationships-filters"
                  data-testid="tutorial:characters:relationships-filters"
                  className="relationships-filter-bar"
                >
                  <Input
                    placeholder="Search relationships"
                    value={relationshipSearch}
                    onChange={(event) => setRelationshipSearch(event.target.value)}
                    className="relationships-filter-search"
                  />
                  <Popover
                    trigger="click"
                    placement="bottomLeft"
                    content={
                      <div className="filters-menu">
                        <div className="filters-menu-row">
                          <Text type="secondary" className="filters-menu-label">
                            Status
                          </Text>
                          <div className="filters-menu-toggle">
                            <Checkbox
                              checked={relationshipStatusFilter === 'needsReview'}
                              onChange={(event) =>
                                setRelationshipStatusFilter(event.target.checked ? 'needsReview' : 'all')
                              }
                            >
                              Needs review only
                            </Checkbox>
                          </div>
                        </div>
                        <div className="filters-menu-row">
                          <Text type="secondary" className="filters-menu-label">
                            Formal / Informal Speech
                          </Text>
                          <Select
                            options={RELATIONSHIP_FORMALITY_FILTER_OPTIONS}
                            value={relationshipFormalityFilter}
                            onChange={(value: RelationshipFormalityFilter) =>
                              setRelationshipFormalityFilter(value)
                            }
                            className="relationships-filter-select"
                            getPopupContainer={filterMenuPopupContainer}
                          />
                        </div>
                        <div className="filters-menu-row">
                          <Text type="secondary" className="filters-menu-label">
                            Chapter count
                          </Text>
                          <Select
                            options={RELATIONSHIP_CHAPTER_FILTER_OPTIONS}
                            value={relationshipChapterFilter}
                            onChange={(value: RelationshipChapterFilter) =>
                              setRelationshipChapterFilter(value)
                            }
                            className="relationships-filter-select"
                            getPopupContainer={filterMenuPopupContainer}
                          />
                        </div>
                        <div className="filters-menu-actions">
                          <Button
                            type="link"
                            className="characters-action-link"
                            onClick={clearRelationshipFilters}
                            disabled={relationshipFilterChips.length === 0}
                          >
                            Clear all
                          </Button>
                        </div>
                      </div>
                    }
                  >
                    <Button icon={<FilterOutlined />} className="filters-menu-button">
                      Filters{relationshipFilterChips.length > 0 ? ` (${relationshipFilterChips.length})` : ''}
                    </Button>
                  </Popover>
                  <div className="relationships-filter-sort">
                    <Text type="secondary" className="relationships-filter-sort-label">
                      Sort
                    </Text>
                    <Select
                      options={RELATIONSHIP_SORT_OPTIONS}
                      value={relationshipSort}
                      onChange={(value: RelationshipSortValue) => setRelationshipSort(value)}
                      className="relationships-filter-sort-select"
                      getPopupContainer={filterMenuPopupContainer}
                    />
                  </div>
                </div>
                <div className="relationships-list-shell">
                  {visibleRelationships.length > 0 ? (
                    <div className="relationships-list">
                      {visibleRelationships.map((relationship, index) => {
                        const status = getRelationshipStatus(relationship)
                        const coverage = formatRelationshipCoverage(relationship)
                        const distribution = formatRelationshipDistribution(relationship)
                        const pairKey = getRelationshipPairKey(relationship)
                        const isSelected = selectedRelationshipPairKey === pairKey
                        return (
                          <button
                            key={pairKey || relationship.id}
                            type="button"
                            className={`relationship-row${isSelected ? ' is-selected' : ''}`}
                            onClick={() => openRelationshipDetail(relationship)}
                            data-tour-id={index === 0 ? 'relationships-edit-button' : undefined}
                          >
                            <div className="relationship-row-main">
                              <div className="relationship-row-name" translate="no">
                                {getSafeRelationshipName(relationship.characterAName, 'Character A')} ↔{' '}
                                {getSafeRelationshipName(relationship.characterBName, 'Character B')}
                              </div>
                              <Tag color={status.color}>{status.label}</Tag>
                            </div>
                            <div className="relationship-row-meta">
                              <span className="relationship-row-coverage">{coverage}</span>
                              <span className="relationship-row-distribution">{distribution}</span>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <Empty
                      className="relationships-list-empty"
                      description={hasRelationshipFilters ? 'No matches' : 'No relationships detected'}
                    />
                  )}
                </div>
              </div>
              <div
                className="relationships-detail"
                data-tour-id="relationships-detail-panel"
                data-testid="tutorial:characters:relationship-detail"
              >
                {relationshipDetailTarget ? (
                  <div
                    key={selectedRelationshipPairKey || undefined}
                    className="relationships-detail-shell"
                  >
                    <div className="relationships-detail-header">
                      <div className="relationships-detail-header-row">
                        <div>
                          <Typography.Text
                            strong
                            className="relationship-detail-name"
                            translate="no"
                          >
                            {relationshipNameA} ↔ {relationshipNameB}
                          </Typography.Text>
                          <div className="relationship-detail-meta">
                            <Tag color={getRelationshipStatus(relationshipDetailTarget).color}>
                              {getRelationshipStatus(relationshipDetailTarget).label}
                            </Tag>
                            <span className="relationship-detail-pill">
                              {formatRelationshipDetailDistribution(relationshipDetailTarget)}
                            </span>
                            <span className="relationship-detail-pill">
                              {formatRelationshipCoverage(relationshipDetailTarget)}
                            </span>
                          </div>
                          {relationshipReviewReasons.length > 0 && (
                            <Text type="secondary" className="relationship-detail-reason">
                              {relationshipReviewReasons.join(' · ')}
                            </Text>
                          )}
                        </div>
                        {relationshipShowReviewToggle && (
                          <div className="relationship-detail-review">
                            <Tooltip
                              title={
                                relationshipIsReviewed
                                  ? 'Clear the approved directional choices for this relationship.'
                                  : 'Approve the displayed choice in each direction. Conflicting chapter observations remain visible.'
                              }
                            >
                              <Button
                                size="small"
                                onClick={() => confirmRelationshipReviewStatus(!relationshipIsReviewed)}
                                loading={relationshipReviewUpdating}
                                disabled={relationshipReviewControlsDisabled}
                              >
                                {relationshipIsReviewed ? 'Clear approval' : 'Approve directional choices'}
                              </Button>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                      <div>
                        {!!relationshipDetail?.approvedDecisionConflicts?.length && <Alert type="warning"
                          message={`${relationshipDetail.approvedDecisionConflicts.length} chapter observations differ from the approved directional decisions.`} />}
                        <Text>Approve the two selected directions for future translations. Chapter values below are observations.</Text>
                        {relationshipDetailTarget?.approvedDecision && <div>
                          Approved: {relationshipNameA} → {relationshipNameB}: {relationshipDetailTarget.approvedDecision.formalityAtoB || 'unresolved'};
                          {' '}{relationshipNameB} → {relationshipNameA}: {relationshipDetailTarget.approvedDecision.formalityBtoA || 'unresolved'}
                        </div>}
                        <Checkbox checked={approveChapterVariation} onChange={e => setApproveChapterVariation(e.target.checked)}>
                          Record these chapter choices as intentional variation
                        </Checkbox>
                        {approveChapterVariation && <Input.TextArea aria-label="Context for intentional address variation"
                          value={relationshipDecisionContext} maxLength={1000}
                          placeholder="Explain the change or scene context. Resolve any mixed chapter rows first."
                          onChange={e => setRelationshipDecisionContext(e.target.value)} />}
                      </div>
                      <div className="relationships-detail-header-row relationships-detail-header-actions">
                        <div className="relationships-detail-controls">
                          <div className="relationships-detail-control-row">
                            <div className="relationships-detail-control">
                              <Text
                                type="secondary"
                                className="relationships-detail-action-label"
                                translate="no"
                              >
                                {bulkDirectionLabelAtoB}
                              </Text>
                              <Select
                                options={RELATIONSHIP_FORMALITY_SELECT_OPTIONS}
                                value={relationshipBulkFormalityAtoB}
                                onChange={(value: RelationshipFormalityValue) =>
                                  handleBulkAtoBChange(value)
                                }
                                className="relationships-detail-direction-select"
                                disabled={relationshipBulkControlsDisabled}
                              />
                            </div>
                            <div className="relationships-detail-control">
                              <Text
                                type="secondary"
                                className="relationships-detail-action-label"
                                translate="no"
                              >
                                {bulkDirectionLabelBtoA}
                              </Text>
                              <Select
                                options={RELATIONSHIP_FORMALITY_SELECT_OPTIONS}
                                value={relationshipBulkFormalityBtoA}
                                onChange={(value: RelationshipFormalityValue) =>
                                  handleBulkBtoAChange(value)
                                }
                                className="relationships-detail-direction-select"
                                disabled={relationshipBulkControlsDisabled}
                              />
                            </div>
                            <div className="relationships-detail-control relationships-detail-link-control">
                              <Checkbox
                                checked={relationshipBulkLinkDirections}
                                onChange={(event) =>
                                  handleBulkLinkDirectionsChange(event.target.checked)
                                }
                                disabled={relationshipBulkControlsDisabled}
                              >
                                Keep both formalities in sync
                              </Checkbox>
                              <Text type="secondary" className="relationships-detail-link-help">
                                Changing one formality updates the other.
                              </Text>
                            </div>
                          </div>
                          <div className="relationships-detail-control-row">
                            <div className="relationships-detail-control relationships-detail-selection-control">
                              <Text type="secondary" className="relationships-detail-action-label">
                                Apply to chapters
                              </Text>
                              <div className="relationships-detail-selection-inline">
                                <Checkbox
                                  indeterminate={someRelationshipChaptersSelected}
                                  checked={allRelationshipChaptersSelected}
                                  onChange={(event) => toggleAllRelationshipChapters(event.target.checked)}
                                  disabled={relationshipBulkControlsDisabled || relationshipSelectableChapters.length === 0}
                                >
                                  All chapters
                                </Checkbox>
                                <Text
                                  type="secondary"
                                  className="relationships-detail-selection-count"
                                >
                                  Selected: {formatPluralCount(selectedRelationshipChapterCount, 'chapter')}
                                </Text>
                              </div>
                            </div>
                            <div className="relationships-detail-control relationships-detail-action-buttons">
                              <Button
                                type="primary"
                                onClick={() => confirmBulkFormalityUpdate()}
                                loading={relationshipBulkUpdating}
                                disabled={relationshipBulkApplyDisabled}
                              >
                                {applyRelationshipLabel}
                              </Button>
                              <Button
                                onClick={handleUndoBulkFormality}
                                disabled={
                                  relationshipUndoStack.length === 0 ||
                                  relationshipBulkControlsDisabled
                                }
                              >
                                Undo
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                      {relationshipBulkFormalityAtoB !== relationshipBulkFormalityBtoA && (
                        <Text
                          type="warning"
                          className="relationships-detail-asymmetry-note"
                        >
                          You're setting asymmetric speech. This is normal in many relationships where status, age, role, or familiarity differ (for example teacher/student or officer/enlisted).
                        </Text>
                      )}
                    </div>
                    {relationshipDetailError && (
                      <Alert
                        type="error"
                        showIcon
                        message="Unable to load relationship chapters"
                        description={relationshipDetailError}
                      />
                    )}
                    <Spin spinning={relationshipDetailLoading}>
                      {relationshipDetail && relationshipDetail.chapters.length > 0 ? (
                        <div className="relationships-detail-chapters">
                          {renderRelationshipGroup(
                            'Needs attention (conflicting or unresolved)',
                            'needsReview',
                            groupedRelationshipChapters.needsReview
                          )}
                          {renderRelationshipGroup(
                            'Provisional defaults — optional review',
                            'suggested',
                            groupedRelationshipChapters.suggested
                          )}
                          {renderRelationshipGroup(
                            'Differs from selected values',
                            'overrides',
                            groupedRelationshipChapters.overrides
                          )}
                          {renderRelationshipGroup(
                            'Matches selected values',
                            'matchesDefault',
                            groupedRelationshipChapters.matchesDefault
                          )}
                        </div>
                      ) : (
                        <Empty description="No chapters found for this relationship" />
                      )}
                    </Spin>
                  </div>
                ) : (
                  <Empty description="Select a relationship to see details" />
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  ) : analysisGateState ? null : (
    <Empty description={emptyDescription} />
  )

  const aliasCanSave =
    !aliasLoading &&
    Boolean(aliasTarget) &&
    aliasSources.length > 0 &&
    !analysisRunning &&
    !splitOperationRunning
  const splitCanSubmit =
    !analysisRunning &&
    !splitOperationRunning &&
    Boolean(splitSourceName.trim()) &&
    Boolean(splitName.trim())
  const editorCanSave =
    canSaveCharacter &&
    !analysisRunning &&
    !splitOperationRunning

  return (
    <>
      <div className="characters-tab-shell book-detail-tab-shell" data-testid="tutorial:characters:workspace">
        <div
          className="characters-tab-content"
          style={{
            pointerEvents: disableSection ? 'none' : 'auto',
            opacity: disableSection ? 0.6 : 1,
          }}
        >
          <header className="characters-workspace-header book-detail-workspace-header book-detail-primary-surface" data-testid="tutorial:characters:header">
            <div className="characters-command-header-row">
              <div className="characters-workspace-header-main">
                <div>
                  <div className="book-detail-eyebrow dashboard-section-kicker">Overview</div>
                  <Text type="secondary" className="characters-command-purpose">
                    Manage cast names, aliases, Grammar Gender, and Formal / Informal Speech before translation.
                  </Text>
                  <Space size={8} wrap align="center" className="characters-updated-row">
                    <Text type="secondary">Last updated: {updatedAtLabel}</Text>
                    {isRegistryRefreshing && <Tag color="blue">Refreshing…</Tag>}
                  </Space>
                </div>
              </div>
              <div className="characters-workspace-actions" data-tour-id="characters-controls" data-testid="tutorial:characters:actions">
                <Space wrap>
                  <span data-tour-id="characters-run-analysis" data-testid="tutorial:characters:run-analysis" className="characters-action-tour-anchor">
                    <Tooltip title="Book-wide analysis that applies across all translation languages.">
                      <Button
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        onClick={openAnalysisModal}
                        disabled={disableRunAnalysis}
                      >
                        Run analysis
                      </Button>
                    </Tooltip>
                  </span>
                  {wrapDisabledAction(
                    <Button
                      icon={<BranchesOutlined />}
                      onClick={openAliasModal}
                      disabled={disableActions || !analysisReady}
                      data-tour-id="characters-alias-button"
                      data-testid="tutorial:characters:alias-mapping"
                    >
                      Alias mapping / merge
                    </Button>
                  )}
                  {wrapDisabledAction(
                    <Button
                      icon={<NodeIndexOutlined />}
                      onClick={() => openMatrixEditorModal()}
                      disabled={disableActions || !analysisReady}
                      data-tour-id="characters-matrix-button"
                      data-testid="tutorial:characters:relationship-rules"
                    >
                      Advanced Relationship Rules
                    </Button>
                  )}
                </Space>
              </div>
            </div>
            {(splitJob || splitJobError) && (
              <Alert
                type={splitStatusTone}
                showIcon
                message={
                  splitStatusLabel
                    ? `Character split ${splitStatusLabel}`
                    : 'Character split'
                }
                description={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span>
                      {(splitJob?.sourceName || splitSourceName || 'Source')} →{' '}
                      {(splitJob?.targetName || splitTargetName || splitName || 'Target')}
                    </span>
                    {splitJobError && <span>{splitJobError}</span>}
                  </div>
                }
              />
            )}
            <div className="characters-command-insights" data-tour-id="characters-analysis-overview" data-testid="tutorial:characters:overview">
              <div className="characters-command-insights-main">
                {analysisGateState && (
                  <div className="characters-analysis-inline">
                    <div className="characters-analysis-inline-header">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div className="characters-analysis-inline-title-row">
                          <Typography.Title level={5} style={{ margin: 0 }}>
                            {analysisGateState.title}
                          </Typography.Title>
                          <Tag color={analysisGateState.statusColor}>{analysisGateState.statusLabel}</Tag>
                        </div>
                        <Text type="secondary">{analysisGateState.description}</Text>
                      </div>
                    </div>
                    <div className="characters-analysis-inline-body">
                      <Text type="secondary">{analysisInfoCopy}</Text>
                      {analysisChapterCountLabel && <Text type="secondary">{analysisChapterCountLabel}</Text>}
                      {analysisCostLabel && <Text type="secondary">{analysisCostLabel}</Text>}
                    </div>
                    <div className="characters-analysis-inline-actions">
                      <Button
                        icon={<InfoCircleOutlined />}
                        className="characters-analysis-inline-learn"
                        onClick={openAnalysisInfo}
                        disabled={analysisLoading}
                      >
                        Learn what this does
                      </Button>
                    </div>
                  </div>
                )}
                {(relationshipSummaryCard || stats) && (
                  <div className="characters-overview-content">
                    {relationshipSummaryCard && (
                      <div data-tour-id="characters-summary" data-testid="tutorial:characters:relationship-summary">
                        {relationshipSummaryCard}
                      </div>
                    )}
                    {stats && (
                      <div data-tour-id="characters-stats" data-testid="tutorial:characters:stats" className="characters-metric-grid">
                        <div className="characters-metric">
                          <Text type="secondary">Names</Text>
                          <div>{characterList.length}</div>
                        </div>
                        <div className="characters-metric">
                          <Text type="secondary">Relationships</Text>
                          <div>{relationshipList.length}</div>
                        </div>
                        <div className="characters-metric">
                          <Text type="secondary">Chapters</Text>
                          <div>{stats.chaptersWithRelationships ?? '—'}</div>
                        </div>
                        <div className="characters-metric">
                          <Text type="secondary">Gender conflicts</Text>
                          <div>{characterConflictCount}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="characters-workflow-compact" data-tour-id="characters-workflow" data-testid="tutorial:characters:readiness">
                <div className="characters-workflow-label">Readiness</div>
                <div className="characters-workflow-chip-grid">
                  {workflowActionItems.map((item, index) => (
                    <button
                      key={`${index}-${String(item.title)}`}
                      type="button"
                      className={`dashboard-readiness-chip dashboard-readiness-chip--${item.statusTone} characters-workflow-chip`}
                      onClick={item.onAction}
                      disabled={item.disabled}
                      aria-label={`${item.chipLabel}: ${item.chipStatusLabel}${item.description ? `. ${item.description}` : ''}`}
                      data-tour-id={item.chipLabel === 'Analysis' ? 'characters-run-analysis-readiness' : undefined}
                    >
                      <span className="characters-workflow-chip-label">
                        {item.chipLabel}:
                      </span>
                      <span className="characters-workflow-chip-state">
                        {item.chipStatusLabel}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </header>

          {castError && !analysisRunning && !splitOperationRunning && (
            <Alert
              type="error"
              showIcon
              message={registry ? 'Showing the last loaded cast. Refresh failed.' : 'Unable to load cast data.'}
              description={castError}
              action={<Button onClick={() => void rebuildRegistry()} disabled={rebuilding}>Retry cast refresh</Button>}
            />
          )}

          <div className="characters-main-slot" data-testid="tutorial:characters:main-slot">{mainContent}</div>
        </div>
        {showRegistryLoading && (
          <div className="characters-loading-overlay">
            <LoadingCard
              label={
                <span className="characters-loading-label">
                  <span>{registryLoadingTitle}</span>
                  <span className="characters-loading-tip">{registryLoadingTip}</span>
                </span>
              }
            />
          </div>
        )}
        {analysisLoading && (
          <div className="characters-analysis-overlay">
            <div className="characters-analysis-box">
              <Spin size="large" />
              <span>Updating character analysis...</span>
            </div>
          </div>
        )}
      </div>

      <Modal
        title={
          <div className="translation-modal-titlebar">
            <span className="translation-modal-titlebar__title">Alias mapping</span>
          </div>
        }
        open={isAliasModalVisible}
        onCancel={closeAliasModal}
        width={960}
        footer={
          <div className="characters-modal-footer">
            <div className="translation-modal-footer-summary" aria-live="polite">
              <span>
                <strong>{aliasSources.length}</strong> alias{aliasSources.length === 1 ? '' : 'es'}
              </span>
              <span className="translation-modal-footer-summary__separator">·</span>
              <span>{aliasTarget ? 'Target selected' : 'Target needed'}</span>
              <span className="translation-modal-footer-summary__separator">·</span>
              {aliasCanSave ? <span>Ready</span> : <span className="translation-modal-footer-summary__warning">Requirements needed</span>}
            </div>
            <div className="characters-modal-footer__actions">
              <Button
                onClick={closeAliasModal}
                disabled={aliasSaving}
                data-testid="tutorial:characters:alias-cancel"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleAddAliases}
                loading={aliasSaving}
                disabled={!aliasCanSave}
                data-testid="tutorial:characters:alias-save"
              >
                Save mapping
              </Button>
            </div>
          </div>
        }
        destroyOnClose
        wrapClassName="characters-modal alias-mapping-modal"
      >
        <div
          className="characters-modal-body"
          data-tour-id="alias-mapping-modal"
          data-testid="tutorial:characters:alias-modal"
          data-tour-ready={aliasLoading ? 'false' : 'true'}
        >
          <Text type="secondary" className="characters-modal-intro">
            Combine duplicate characters or add alternate names. Choose the character to keep,
            then select the duplicate character names or type aliases. Only combine names that
            refer to the same individual. Review the preview before saving.
          </Text>
            <div className="alias-mapping-grid">
            <div
              className="alias-mapping-panel alias-mapping-panel--compact"
              data-tour-id="alias-mapping-add-panel"
              data-testid="tutorial:characters:alias-add-panel"
              data-tour-ready={aliasLoading ? 'false' : 'true'}
            >
              <Text strong className="alias-mapping-panel-title">Add aliases</Text>
              <div className="alias-mapping-form">
                <div className="characters-field">
                  <Text type="secondary" className="characters-field-label">
                    Character to keep (canonical name)
                  </Text>
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select the canonical name"
                    optionFilterProp="label"
                    value={aliasTarget ?? undefined}
                    options={characterOptions}
                    onChange={handleAliasTargetChange}
                    disabled={aliasLoading}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="characters-field">
                  <Text type="secondary" className="characters-field-label">
                    Duplicate characters or aliases
                  </Text>
                  <Select
                    mode="tags"
                    allowClear
                    showSearch
                    placeholder="Type aliases and press Enter"
                    optionFilterProp="label"
                    value={aliasSources}
                    options={aliasSourceOptions}
                    onChange={handleAliasSourcesChange}
                    tokenSeparators={[',']}
                    disabled={aliasLoading}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div className="alias-mapping-preview characters-modal-block">
                <Text strong className="characters-modal-block-heading">
                  Preview
                </Text>
                <div className="characters-field">
                  <Text type="secondary" className="characters-field-label">
                    Target
                  </Text>
                  <div>
                    <Text strong>{aliasTarget || 'Select a target name'}</Text>
                  </div>
                </div>
                <div className="characters-field">
                  <Text type="secondary" className="characters-field-label">
                    Aliases
                  </Text>
                  {aliasPreviewAliases.length > 0 ? (
                    <div className="characters-chip-row">
                      {aliasPreviewAliases.map((alias) => (
                        <Tag key={alias} color="default">
                          {alias}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    <div className="characters-detail-empty">
                      Select aliases to merge.
                    </div>
                  )}
                </div>
                <Text type="secondary" className="characters-field-help">
                  This updates the Character List only; chapter rows are unchanged.
                </Text>
                {aliasConflicts.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Alias matches another character"
                    description={aliasConflicts
                      .map((conflict) => `${conflict.alias} → ${conflict.canonicalName}`)
                      .join(', ')}
                  />
                )}
              </div>
            </div>
            <div className="alias-mapping-panel alias-mapping-panel--secondary">
              <Text strong className="alias-mapping-panel-title">Existing aliases</Text>
              <Input
                placeholder="Search aliases"
                value={aliasMappingSearch}
                onChange={(event) => setAliasMappingSearch(event.target.value)}
              />
              <div className="alias-mapping-list">
                <Spin spinning={aliasLoading}>
                  {cleanedAliasMappings.length > 0 ? (
                    aliasMappingGroups.length > 0 ? (
                      <div className="alias-mapping-groups">
                        {aliasMappingGroups.map((group) => (
                          <div key={group.target} className="alias-mapping-group">
                            <Text strong>{group.target}</Text>
                            {group.aliases.length > 0 ? (
                              <div className="characters-chip-row">
                                {group.aliases.map((alias) => (
                                  <div
                                    key={`${group.target}-${alias}`}
                                    className="alias-mapping-existing-alias"
                                  >
                                    {renderAliasMappingChip(
                                      group.target,
                                      alias,
                                      analysisRunning || splitOperationRunning
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <Text type="secondary">No aliases mapped.</Text>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Empty description="No matches" />
                    )
                  ) : (
                    <Empty description="No alias mappings yet" />
                  )}
                </Spin>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title={
          <div className="translation-modal-titlebar">
            <span className="translation-modal-titlebar__title">Remove alias mapping</span>
          </div>
        }
        open={Boolean(aliasRemovalChoice)}
        onCancel={closeAliasRemovalChoice}
        footer={
          <div className="characters-modal-footer">
            <div className="translation-modal-footer-summary" aria-live="polite">
              <span>{aliasRemovalChoice?.alias || 'Alias'}</span>
              <span className="translation-modal-footer-summary__separator">·</span>
              <span>{aliasRemovalChoice?.targetCanonicalName || 'Target'}</span>
            </div>
            <div className="characters-modal-footer__actions">
              <Button onClick={closeAliasRemovalChoice} disabled={aliasSaving}>Cancel</Button>
              <Button onClick={handleRemoveAliasOnly} loading={aliasSaving}>
                Remove alias only
              </Button>
              <Button
                type="primary"
                onClick={handleSeparateAliasFromRemoval}
                disabled={aliasSaving}
              >
                Separate into character
              </Button>
            </div>
          </div>
        }
        destroyOnClose
        wrapClassName="characters-modal"
      >
        <div className="characters-modal-body">
          <Text type="secondary" className="characters-modal-intro">
            Choose whether <strong>{aliasRemovalChoice?.alias}</strong> is just a bad alias for{' '}
            <strong>{aliasRemovalChoice?.targetCanonicalName}</strong>, or should become its own character.
          </Text>
          <div className="characters-modal-actions characters-modal-actions--fallback">
            <Button onClick={closeAliasRemovalChoice} disabled={aliasSaving}>Cancel</Button>
            <Button onClick={handleRemoveAliasOnly} loading={aliasSaving}>
              Remove alias only
            </Button>
            <Button
              type="primary"
              onClick={handleSeparateAliasFromRemoval}
              disabled={aliasSaving}
            >
              Separate into character
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        title={
          <div className="translation-modal-titlebar">
            <span className="translation-modal-titlebar__title">Separate merged characters</span>
          </div>
        }
        open={isSplitModalVisible}
        onCancel={closeSplitModal}
        footer={
          <div className="characters-modal-footer">
            <div className="translation-modal-footer-summary" aria-live="polite">
              <span>{splitSourceName.trim() ? 'Source selected' : 'Source needed'}</span>
              <span className="translation-modal-footer-summary__separator">·</span>
              <span>{splitName.trim() ? 'Name entered' : 'Name needed'}</span>
              <span className="translation-modal-footer-summary__separator">·</span>
              {splitCanSubmit ? <span>Ready</span> : <span className="translation-modal-footer-summary__warning">Requirements needed</span>}
            </div>
            <div className="characters-modal-footer__actions">
              <Button onClick={closeSplitModal} disabled={splitSubmitting}>Cancel</Button>
              <Button
                type="primary"
                onClick={submitSplitCharacter}
                loading={splitSubmitting}
                disabled={!splitCanSubmit}
              >
                Separate characters
              </Button>
            </div>
          </div>
        }
        destroyOnClose
        wrapClassName="characters-modal"
      >
        <div className="characters-modal-body">
          <Text type="secondary" className="characters-modal-intro">
            Use this when two different people were accidentally combined into one character.
          </Text>
          <section className="characters-modal-block">
            <Text strong className="characters-modal-block-heading">Source and target</Text>
            <div className="character-split-grid">
              <div className="characters-field">
                <Text type="secondary" className="characters-field-label">
                  Combined character
                </Text>
                <Input value={splitSourceName} readOnly />
              </div>
              <div className="characters-field">
                <Text type="secondary" className="characters-field-label">
                  Name to separate
                </Text>
                <Input
                  value={splitName}
                  onChange={(event) => {
                    const nextValue = event.target.value
                    setSplitName(nextValue)
                    setSplitTargetName((previousTarget) => {
                      const previousTrimmed = previousTarget.trim()
                      const currentSplitTrimmed = splitName.trim()
                      if (!previousTrimmed || previousTrimmed.toLowerCase() === currentSplitTrimmed.toLowerCase()) {
                        return nextValue.trim()
                      }
                      return previousTarget
                    })
                  }}
                  placeholder="e.g., Jean-Philippe"
                />
              </div>
            </div>
          </section>
          {splitJob && (
            <Alert
              showIcon
              type={splitStatusTone}
              message={`Status: ${splitStatusLabel || 'running'}`}
            />
          )}
          {splitJobError && (
            <Alert
              showIcon
              type="error"
              message="Split failed"
              description={splitJobError}
            />
          )}
          {splitWarnings.length > 0 && (
            <Alert
              showIcon
              type="warning"
              message="Follow-up recommended"
              description={splitWarnings.join(' ')}
            />
          )}
          {splitResultSummary && (
            <section className="character-split-summary characters-modal-block">
              <Text strong className="characters-modal-block-heading">Result</Text>
              <Text type="secondary">
                Removed {splitResultSummary.aliasMappingsRemoved ?? 0} alias mapping(s), rewrote{' '}
                {splitResultSummary.rewrite?.updatedRows ?? 0} row(s) across{' '}
                {splitResultSummary.rewrite?.updatedChapters ?? 0} chapter(s).
              </Text>
            </section>
          )}
          <div className="characters-modal-actions characters-modal-actions--fallback">
            <Button onClick={closeSplitModal} disabled={splitSubmitting}>Cancel</Button>
            <Button
              type="primary"
              onClick={submitSplitCharacter}
              loading={splitSubmitting}
              disabled={!splitCanSubmit}
            >
              Separate characters
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        title={
          <div className="translation-modal-titlebar">
            <span className="translation-modal-titlebar__title">Edit character</span>
          </div>
        }
        open={isEditorModalVisible}
        onCancel={closeEditorModal}
        footer={
          <div className="characters-modal-footer">
            <div className="translation-modal-footer-summary" aria-live="polite">
              <span>{normalizedEditorName ? 'Name set' : 'Name needed'}</span>
              <span className="translation-modal-footer-summary__separator">·</span>
              <span>{editorGender !== 'unknown' ? 'Grammar Gender set' : 'Grammar Gender needed'}</span>
              <span className="translation-modal-footer-summary__separator">·</span>
              {editorCanSave ? <span>Ready</span> : <span className="translation-modal-footer-summary__warning">No saveable changes</span>}
            </div>
            <div className="characters-modal-footer__actions">
              <Button onClick={closeEditorModal} disabled={editorSaving}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleSaveCharacter}
                loading={editorSaving}
                disabled={!editorCanSave}
              >
                Apply changes
              </Button>
            </div>
          </div>
        }
        destroyOnClose
        width={980}
        wrapClassName="characters-modal character-editor-modal"
      >
        <div className="characters-modal-body character-editor-modal-body">
          <div className="character-editor-top-grid">
            <section className="characters-modal-block">
              <Text strong className="characters-modal-block-heading">Identity and aliases</Text>
              <div className="characters-field">
                <Text style={EDITOR_LABEL_STYLE}>
                  Name
                </Text>
                <Input value={editorName} onChange={(event) => setEditorName(event.target.value)} />
              </div>
              <div className="characters-field">
                <Text style={EDITOR_LABEL_STYLE}>
                  Aliases
                </Text>
                <Select
                  mode="tags"
                  allowClear
                  showSearch
                  placeholder="Type aliases and press Enter"
                  optionFilterProp="label"
                  value={editorAliases}
                  options={editorAliasOptions}
                  onChange={handleEditorAliasesChange}
                  tokenSeparators={[',']}
                  disabled={aliasLoading || analysisRunning || splitOperationRunning}
                  style={{ width: '100%', marginTop: 6 }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Aliases map to this character in future analyses and translations.
                </Text>
                {editorObservedAliases.length > 0 && (
                  <div className="characters-field">
                    <Text type="secondary" className="characters-field-label">
                      Observed in chapters
                    </Text>
                    <div className="characters-chip-row">
                      {editorObservedAliases.map((alias) => (
                        <div key={alias} className="characters-detail-alias-row">
                          {renderAliasSplitChip(
                            editorTarget?.canonicalName || '',
                            alias,
                            !editorTarget || analysisRunning || splitOperationRunning
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
            <section className="characters-modal-block">
              <Text strong className="characters-modal-block-heading">Grammar Gender</Text>
              <div className="characters-field">
                <Text style={EDITOR_LABEL_STYLE}>
                  Grammar Gender (for translation)
                </Text>
                <Select
                  value={editorGender === 'unknown' ? undefined : editorGender}
                  options={GENDER_OPTIONS}
                  onChange={(value: GenderValue) => setEditorGender(value)}
                  placeholder="Select a form"
                  style={{ width: '100%' }}
                />
                <Text style={EDITOR_LABEL_STYLE}>
                  Used for grammatical agreement in some languages. It doesn’t describe identity—pick
                  the form used in the chapter text.
                </Text>
              </div>
            </section>
          </div>
          <section className="characters-modal-block">
            <div className="characters-modal-title-row">
              <Text style={DESCRIPTION_LABEL_STYLE}>Character Description</Text>
              <Tooltip title={DESCRIPTION_TOOLTIP}>
                <InfoCircleOutlined style={{ color: 'var(--text-secondary)' }} />
              </Tooltip>
            </div>
            <Text type="secondary" className="characters-field-help">
              Overrides are saved only for fields you edit. Clearing a touched field removes its override.
            </Text>
            <div className="character-description-grid">
              {DESCRIPTION_INPUT_FIELDS.map((field) => {
                const hasOverride = Boolean(editorTarget?.descriptionOverrides?.[field.key]?.length)
                const hasTouched = descriptionTouchedRef.current.has(field.key)
                const currentValues = parseDescriptionInputValues(
                  field.key,
                  editorDescriptions[field.key] || ''
                )
                const willOverride = hasTouched && currentValues.length > 0
                const willClear = hasTouched && currentValues.length === 0 && hasOverride
                return (
                  <div key={field.key} className={field.key === 'distinguishingFeatures' ? 'characters-field character-description-wide' : 'characters-field'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Text style={EDITOR_LABEL_STYLE}>
                        {field.label}
                      </Text>
                      {willClear ? (
                        <Tag color="default">Clears override</Tag>
                      ) : hasOverride || willOverride ? (
                        <Tag color="gold">Override</Tag>
                      ) : null}
                    </div>
                  {field.key === 'distinguishingFeatures' ? (
                    <Input.TextArea
                      value={editorDescriptions[field.key] || ''}
                      placeholder={field.placeholder}
                      onChange={(event) => handleDescriptionInputChange(field.key, event.target.value)}
                      autoSize={{ minRows: 3, maxRows: 5 }}
                    />
                  ) : (
                    <Input
                      value={editorDescriptions[field.key] || ''}
                      placeholder={field.placeholder}
                      onChange={(event) => handleDescriptionInputChange(field.key, event.target.value)}
                    />
                  )}
                  {field.key === 'distinguishingFeatures' && (
                    <Text style={EDITOR_LABEL_STYLE}>
                      Comma-separated for multiple features.
                    </Text>
                  )}
                  </div>
                )
              })}
            </div>
          </section>
        </div>
      </Modal>
      <AppTour
        open={controlsTourOpen}
        steps={controlsTourSteps}
        current={controlsTourStep}
        onChange={handleControlsTourChange}
        onClose={handleControlsTourClose}
        onFinish={handleControlsTourFinish}
        zIndex={2200}
      />
      <AppTour
        open={analysisTourOpen}
        steps={analysisTourSteps}
        current={analysisTourStep}
        onChange={handleAnalysisTourChange}
        onClose={handleAnalysisTourClose}
        onFinish={handleAnalysisTourFinish}
        zIndex={2200}
      />
    </>
  )
}

export default CharactersTab
